/**
 * Modal Visualizar Check-Out
 * Exibe check-outs de OS por área e técnico (estrutura tipo caixa de email).
 * Dados iniciais exemplos; integração com API em etapa posterior.
 */

import React, { useState, useCallback, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { User, TextAlignStart, ListX, ChevronDown, ChevronUp, ListFilter, ListChecks, Search, X, Ticket, Wrench, History, Timer, Pencil, Check, MapPin, CheckCircle, AlertTriangle, Image as ImageIcon, PenLine, Expand, RefreshCw, Box, Trash2, Boxes, Plus, PackagePlus, Columns2, Tag, Map as MapIcon, Loader2, Play, Route } from 'lucide-react';
import './CheckoutViewModal.css';
import '../../oficina/components/OficinaView.css';
import { getApiBaseUrl } from '../../../utils/apiConfig';
import { resolveCheckoutFotoSrc, isCheckoutMediaVideo } from '../utils/resolveCheckoutFotoSrc';
import {
  fetchCheckoutResumo,
  fetchAdhocOsEnrichBySerial,
  fetchPecasPendentes,
  fetchPecasTrocadas,
  fetchFotos,
  fetchAssinatura,
  fetchOsRastreio,
  fetchDisponibilidadeEstoque,
  fetchCompatibilidade,
  fetchPecasCompativelModelo,
  deletePecaPendente,
  incluirPecasPendentes,
  trocarPecaPendente,
  fetchCondicoesSearch,
  updateCondicao,
  fetchGerarPedido,
  fetchReservarPedido,
  fetchPedidoFechamentoDados,
  fetchTransportadorasPedido,
  fetchClassificacoesPedido,
  updatePedidoFechamentoApi
} from '../services/checkoutViewService';
import { sendOSConcluida } from '../../process-config/services/emailNotificationsService';
import { BoardEncaminharAgendaOverlay } from '../../board/components/BoardEncaminharAgendaOverlay';
import { CheckoutCycleServiceField } from './CheckoutCycleServiceField';
import { GoHoverTooltip } from '../../go-config/components/GoHoverTooltip';
import { EquipmentConsultSearchModal } from '../../equipment-consult';
import {
  TIPO_OS_NAMES,
  preventivaToTipoLetter,
} from '../../board/board-search-filters/tipoOsFilterUtils';
import { CheckoutAdhocOsCreateModal } from './CheckoutAdhocOsCreateModal';
import { CheckoutViewOsRastreioModal } from './CheckoutViewOsRastreioModal';
import OpenOsV2EquipmentHistoryOverlay from '../../new-os/components/OpenOsV2EquipmentHistoryOverlay';
import CheckoutViewSkeleton from './CheckoutViewSkeleton';

const titleCase = (str) => {
  if (!str || typeof str !== 'string') return '';
  return str.replace(/\w\S*/g, (t) => t.charAt(0).toUpperCase() + t.slice(1).toLowerCase());
};

const ADHOC_OS_TOOLTIP = 'OS avulsa';

function AdhocCountWithTip({ value }) {
  return (
    <GoHoverTooltip text={ADHOC_OS_TOOLTIP} className="checkout-view-adhoc-count-tip">
      <span className="checkout-view-split-count checkout-view-split-count--adhoc">{value}</span>
    </GoHoverTooltip>
  );
}

function CheckoutSplitCount({ adhocCount = 0, count = 0, className = '' }) {
  const adhoc = Number(adhocCount) || 0;
  const normal = Number(count) || 0;
  if (adhoc <= 0 && normal <= 0) return null;
  const wrapClass = ['checkout-view-split-count-wrap', className].filter(Boolean).join(' ');
  if (adhoc > 0 && normal <= 0) {
    return (
      <span className={wrapClass}>
        <AdhocCountWithTip value={adhoc} />
      </span>
    );
  }
  if (adhoc <= 0 && normal > 0) {
    return (
      <span className={wrapClass}>
        <span className="checkout-view-split-count checkout-view-split-count--normal">{normal}</span>
      </span>
    );
  }
  return (
    <span className={wrapClass}>
      <AdhocCountWithTip value={adhoc} />
      <span className="checkout-view-split-count-sep" aria-hidden="true" />
      <span className="checkout-view-split-count checkout-view-split-count--normal">{normal}</span>
    </span>
  );
}

const getAreaCheckoutCount = (areaId, areas, techniciansByArea) => {
  const arr = Array.isArray(areas) ? areas : [];
  const a = arr.find((x) => String(x.id) === String(areaId));
  if (a && typeof a.count === 'number') return a.count;
  const techs = (techniciansByArea && techniciansByArea[areaId]) || [];
  return techs.reduce((sum, t) => sum + (t.count || 0), 0);
};

const getTotalCheckoutCount = (areas, techniciansByArea) => {
  const arr = Array.isArray(areas) ? areas : [];
  const hasAreaCount = arr.some((a) => typeof a.count === 'number');
  if (hasAreaCount) return arr.reduce((s, a) => s + (a.count || 0), 0);
  return arr.reduce((s, a) => s + getAreaCheckoutCount(a.id, arr, techniciansByArea || {}), 0);
};

const getConditions = (areaId, techName, conditionsByAreaTech) => {
  if (conditionsByAreaTech?.[areaId]?.[techName]?.length) return conditionsByAreaTech[areaId][techName];
  return [];
};

const resolveStockValidationQty = (naEmpresa, outrasEmpresas, scope) => {
  if (String(scope || '').trim().toLowerCase() === 'os_company') return naEmpresa;
  return naEmpresa + outrasEmpresas;
};

const getConditionStockValidationScope = (selectedOs, conditionsByAreaTech) => {
  if (!selectedOs) return 'all_companies';
  const cond = getConditions(selectedOs.areaId, selectedOs.techName, conditionsByAreaTech)
    .find((c) => c.label === selectedOs.conditionLabel);
  return String(cond?.stock_validation_scope || '').trim().toLowerCase() === 'os_company'
    ? 'os_company'
    : 'all_companies';
};

const techKey = (areaId, techName) => `${areaId}|${techName}`;
const conditionKey = (areaId, techName, condLabel) => `${areaId}|${techName}|${condLabel}`;

const getOsList = (areaId, techName, condLabel, osByAreaTechCondition) => {
  if (osByAreaTechCondition?.[areaId]?.[techName]?.[condLabel]?.length) {
    return osByAreaTechCondition[areaId][techName][condLabel];
  }
  return [];
};

const getAdhocOsSerialKey = (os) => String(os?.numeroSerie || os?.patrimonio || '').trim();

const findOsInCheckoutResumo = (osByAreaTechCondition, numeroOS) => {
  const target = String(numeroOS || '').trim();
  if (!target || !osByAreaTechCondition) return null;
  for (const [areaId, techMap] of Object.entries(osByAreaTechCondition)) {
    if (!techMap || typeof techMap !== 'object') continue;
    for (const [techName, condMap] of Object.entries(techMap)) {
      if (!condMap || typeof condMap !== 'object') continue;
      for (const [conditionLabel, list] of Object.entries(condMap)) {
        const os = (Array.isArray(list) ? list : []).find(
          (o) => String(o?.numeroOS || '').trim() === target
        );
        if (os) return { areaId, techName, conditionLabel, os };
      }
    }
  }
  return null;
};

const getAdhocOsClienteDisplay = (os, enrichBySerial) => {
  if (!os?.isAdhoc) return os?.cliente || '—';
  const serial = getAdhocOsSerialKey(os);
  const enrich = serial ? enrichBySerial?.[serial] : null;
  if (enrich?.cliente) return enrich.cliente;
  return os.cliente || 'Avulso';
};

const getAdhocOsModeloDisplay = (os, enrichBySerial) => {
  if (!os?.isAdhoc) return os?.modeloEquipamento || '—';
  const serial = getAdhocOsSerialKey(os);
  const enrich = serial ? enrichBySerial?.[serial] : null;
  if (enrich?.modelo) return enrich.modelo;
  return os.modeloEquipamento || 'Avulso';
};

const formatOsDateTime = (iso) => {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    const dd = String(d.getDate()).padStart(2, '0');
    const MM = String(d.getMonth() + 1).padStart(2, '0');
    const yyyy = d.getFullYear();
    const hh = String(d.getHours()).padStart(2, '0');
    const mm = String(d.getMinutes()).padStart(2, '0');
    return `${dd}/${MM}/${yyyy} ${hh}:${mm}`;
  } catch {
    return iso;
  }
};

function phoneDigitsOnly(raw) {
  return String(raw || '').replace(/\D/g, '').slice(0, 11);
}
function formatPhoneBrDisplay(raw) {
  const d = phoneDigitsOnly(raw);
  if (d.length === 0) return '';
  if (d.length <= 2) return `(${d}`;
  if (d.length <= 6) return `(${d.slice(0, 2)}) ${d.slice(2)}`;
  if (d.length <= 10) return `(${d.slice(0, 2)}) ${d.slice(2, 6)}-${d.slice(6)}`;
  return `(${d.slice(0, 2)}) ${d.slice(2, 7)}-${d.slice(7, 11)}`;
}
function isValidEmailPedido(s) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(s || '').trim());
}

function emptyPedidoEditModal() {
  return {
    open: false,
    codPedido: '',
    numeroOS: '',
    loading: false,
    saving: false,
    loadError: null,
    saveError: null,
    saveSuccess: false,
    solicitante: '',
    foneDisplay: '',
    email: '',
    transpInput: '',
    classifInput: '',
    obsNfe: '',
    observacao: '',
    obsInt: '',
    initialTranspNome: '',
    initialTranspCodigo: '',
    initialClassifNome: '',
    initialClassifCodigo: '',
    transpPickNome: null,
    transpPickCodigo: null,
    classifPickNome: null,
    classifPickCodigo: null,
    transpResults: [],
    transpSearchLoading: false,
    classifResults: [],
    classifSearchLoading: false
  };
}

const patrimônioOuSerie = (os) => (os.patrimonio?.trim?.() || os.numeroSerie || '—');
const patrimônioESérie = (os) => {
  const pat = os.patrimonio?.trim?.();
  const ser = os.numeroSerie?.trim?.();
  if (pat && ser) return `${pat} · ${ser}`;
  return pat || ser || '—';
};
const patrimônioOuSerieParaLista = (os) => {
  const pat = os.patrimonio?.trim?.();
  const ser = os.numeroSerie?.trim?.();
  const valor = pat || ser || '—';
  const titulo = pat && ser ? ser : undefined;
  return { valor, titulo };
};
const formatDateDdMmYyyy = (val) => {
  if (val == null || val === '') return '';
  try {
    const d = new Date(val);
    if (isNaN(d.getTime())) return '';
    const dd = String(d.getUTCDate()).padStart(2, '0');
    const MM = String(d.getUTCMonth() + 1).padStart(2, '0');
    const yyyy = d.getUTCFullYear();
    return `${dd}/${MM}/${yyyy}`;
  } catch {
    return '';
  }
};

function AlterarStatusSelect({ value, onChange, options, disabled }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const onOutside = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    if (open) document.addEventListener('click', onOutside);
    return () => document.removeEventListener('click', onOutside);
  }, [open]);
  const valueStr = value != null ? String(value) : '';
  const selected = options.find((o) => String(o.codigo) === valueStr);
  const label = selected ? (selected.nome || selected.codigo) : 'Selecione...';
  const handleOptionSelect = (opt, e) => {
    e.preventDefault();
    e.stopPropagation();
    onChange(opt.codigo != null ? String(opt.codigo) : '');
    setOpen(false);
  };
  return (
    <div className="checkout-view-alterar-status-select-wrap" ref={ref}>
      <button
        type="button"
        className="checkout-view-alterar-status-select-trigger"
        onClick={() => !disabled && setOpen((o) => !o)}
        disabled={disabled}
      >
        <span>{label}</span>
        <span className="checkout-view-alterar-status-select-chevron" aria-hidden style={{ transform: open ? 'rotate(180deg)' : 'none' }}>▼</span>
      </button>
      {open && (
        <div className="checkout-view-alterar-status-select-dropdown">
          {options.map((opt) => (
            <div
              key={String(opt.codigo)}
              className="checkout-view-alterar-status-select-option"
              data-selected={String(opt.codigo) === valueStr}
              onMouseDown={(e) => handleOptionSelect(opt, e)}
              role="option"
              aria-selected={String(opt.codigo) === valueStr}
            >
              {opt.nome || opt.codigo}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const formatContador = (val) => {
  if (val == null || val === '') return '—';
  const n = typeof val === 'number' ? val : parseInt(val, 10);
  return isNaN(n) ? val : n.toLocaleString('pt-BR');
};
const formatHoraHhMm = (val) => {
  if (val == null || val === '') return '';
  const t = String(val).trim();
  if (t.length >= 5) return t.substring(0, 5);
  return t.includes(':') ? t : '';
};

/** yyyy-mm-dd para <input type="date"> (coerente com formatDateDdMmYyyy em UTC). */
function osDateToYyyyMmDdInput(val) {
  if (val == null || val === '') return '';
  try {
    const d = new Date(val);
    if (isNaN(d.getTime())) return '';
    const y = d.getUTCFullYear();
    const mo = String(d.getUTCMonth() + 1).padStart(2, '0');
    const dd = String(d.getUTCDate()).padStart(2, '0');
    return `${y}-${mo}-${dd}`;
  } catch {
    return '';
  }
}

/**
 * Regras: datas reais (AAAA-MM-DD), horários 24h; início ≤ fim; mesmo dia → início < fim (horário).
 * @returns {{ ok: true, horaIni: string, horaFim: string } | { ok: false, message: string }}
 */
function validateHorariosAtendimentoClient(dataIniStr, horaIniStr, dataFimStr, horaFimStr) {
  const di = String(dataIniStr || '').trim();
  const hi = String(horaIniStr || '').trim();
  const df = String(dataFimStr || '').trim();
  const hf = String(horaFimStr || '').trim();

  const mDate = (s) => {
    const m = s.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (!m) return null;
    const y = parseInt(m[1], 10);
    const mo = parseInt(m[2], 10);
    const d = parseInt(m[3], 10);
    if (mo < 1 || mo > 12 || d < 1 || d > 31) return null;
    const dt = new Date(Date.UTC(y, mo - 1, d));
    if (Number.isNaN(dt.getTime())) return null;
    if (dt.getUTCFullYear() !== y || dt.getUTCMonth() !== mo - 1 || dt.getUTCDate() !== d) return null;
    return { y, mo, d };
  };

  const mTime = (s) => {
    const m = String(s).trim().match(/^(\d{1,2}):(\d{2})(?::\d{2})?$/);
    if (!m) return null;
    const h = parseInt(m[1], 10);
    const mi = parseInt(m[2], 10);
    if (h < 0 || h > 23 || mi < 0 || mi > 59) return null;
    const norm = `${String(h).padStart(2, '0')}:${String(mi).padStart(2, '0')}`;
    return { h, mi, minutes: h * 60 + mi, norm };
  };

  if (!di || !df) {
    return { ok: false, message: 'Informe as datas de início e fim.' };
  }
  const pIni = mDate(di);
  const pFim = mDate(df);
  if (!pIni || !pFim) {
    return { ok: false, message: 'Datas inválidas. Use o calendário ou formato AAAA-MM-DD.' };
  }
  if (!hi || !hf) {
    return { ok: false, message: 'Informe os horários de início e fim.' };
  }
  const tIni = mTime(hi);
  const tFim = mTime(hf);
  if (!tIni || !tFim) {
    return { ok: false, message: 'Horários inválidos. Use formato 24h (HH:mm).' };
  }

  const startMs = Date.UTC(pIni.y, pIni.mo - 1, pIni.d) + tIni.minutes * 60 * 1000;
  const endMs = Date.UTC(pFim.y, pFim.mo - 1, pFim.d) + tFim.minutes * 60 * 1000;

  if (startMs > endMs) {
    return { ok: false, message: 'Data e hora inicial não podem ser posteriores à final.' };
  }
  const sameDay = pIni.y === pFim.y && pIni.mo === pFim.mo && pIni.d === pFim.d;
  if (sameDay) {
    if (tIni.minutes > tFim.minutes) {
      return { ok: false, message: 'No mesmo dia, o horário inicial não pode ser posterior ao final.' };
    }
    if (tIni.minutes === tFim.minutes) {
      return { ok: false, message: 'No mesmo dia, o horário inicial não pode ser igual ao final.' };
    }
  }

  return { ok: true, horaIni: tIni.norm, horaFim: tFim.norm };
}

const horáriosAtendimento = (os) => {
  const dataIni = formatDateDdMmYyyy(os.TB02122_DATAINI);
  const horaIni = formatHoraHhMm(os.TB02122_HORAINI);
  const dataFim = formatDateDdMmYyyy(os.TB02122_DATAFIM);
  const horaFim = formatHoraHhMm(os.TB02122_HORAFIM);
  const ini = [dataIni, horaIni].filter(Boolean).join(' ').trim() || '—';
  const fim = [dataFim, horaFim].filter(Boolean).join(' ').trim() || '—';
  if (ini === '—' && fim === '—') return '—';
  if (ini === '—') return fim;
  if (fim === '—') return ini;
  return `${ini} · ${fim}`;
};
const formatDataHoraFimOsDt = (os) => {
  const data = formatDateDdMmYyyy(os.TB02122_DATAFIM);
  const hora = formatHoraHhMm(os.TB02122_HORAFIM);
  const s = [data, hora].filter(Boolean).join(' ').trim();
  return s || (os.dataHoraFimAtendimento || formatOsDateTime(os.dataHoraFimAtendimento || os.dataHoraCheckout || os.dataHoraInicioAtendimento)) || '—';
};
const parseBrDateTimeSortKey = (value) => {
  if (!value || typeof value !== 'string') return NaN;
  const m = value.trim().match(/^(\d{2})\/(\d{2})\/(\d{4})(?:\s+(\d{2}):(\d{2}))?/);
  if (!m) return NaN;
  return new Date(+m[3], +m[2] - 1, +m[1], +(m[4] || 0), +(m[5] || 0), 0, 0).getTime();
};
const getOsSortKey = (os) => {
  const d = os.TB02122_DATAFIM;
  const h = formatHoraHhMm(os.TB02122_HORAFIM);
  if (d && h) {
    const [hh, mm] = h.split(':').map(Number);
    const dt = new Date(d);
    return new Date(dt.getUTCFullYear(), dt.getUTCMonth(), dt.getUTCDate(), hh || 0, mm || 0, 0, 0).getTime();
  }
  const brKey = parseBrDateTimeSortKey(os.dataHoraFimAtendimento);
  if (Number.isFinite(brKey)) return brKey;
  const iso = os.dataHoraFimAtendimento || os.dataHoraCheckout || os.dataHoraInicioAtendimento;
  if (iso && typeof iso === 'string') return new Date(iso).getTime();
  return Infinity;
};

/** Menor timestamp entre OS da lista (mais antigo / “mais atrasado” primeiro na ordenação). */
const getEarliestOsSortKeyInList = (list) => {
  if (!Array.isArray(list) || list.length === 0) return Infinity;
  let min = Infinity;
  for (const os of list) {
    const k = getOsSortKey(os);
    if (k < min) min = k;
  }
  return min;
};

/**
 * Menor DATAFIM+HORAFIM entre as condições do técnico.
 * @param {Set<string>|null} filterSet — se tiver itens, só considera essas condições (painel detalhe).
 * @param {Set<string>|null} tipoSet — letras de tipo de OS (C/B/S…); vazio = todos.
 */
const getCheckoutOsTipoLetter = (os) =>
  preventivaToTipoLetter(os?.TB02115_PREVENTIVA ?? os?.tipoOS ?? 'N');

const filterOsListByTipo = (list, tipoSet) => {
  if (!Array.isArray(list)) return [];
  if (!(tipoSet instanceof Set) || tipoSet.size === 0) return list;
  return list.filter((os) => tipoSet.has(getCheckoutOsTipoLetter(os)));
};

const getEarliestOsSortKeyForTech = (
  areaId,
  techName,
  osByAreaTechCondition,
  filterSet = null,
  tipoSet = null
) => {
  const root = osByAreaTechCondition?.[areaId]?.[techName];
  if (!root || typeof root !== 'object') return Infinity;
  let min = Infinity;
  const useFilter = filterSet instanceof Set && filterSet.size > 0;
  for (const [condLabel, list] of Object.entries(root)) {
    if (useFilter && !filterSet.has(condLabel)) continue;
    const k = getEarliestOsSortKeyInList(filterOsListByTipo(Array.isArray(list) ? list : [], tipoSet));
    if (k < min) min = k;
  }
  return min;
};

const tempoProduzidoHhMm = (os) => {
  const dataIni = os.TB02122_DATAINI;
  const horaIni = formatHoraHhMm(os.TB02122_HORAINI);
  const dataFim = os.TB02122_DATAFIM;
  const horaFim = formatHoraHhMm(os.TB02122_HORAFIM);
  if (!dataIni || !horaIni || !dataFim || !horaFim) return null;
  try {
    const [hi, mi] = horaIni.split(':').map(Number);
    const [hf, mf] = horaFim.split(':').map(Number);
    const dIni = new Date(dataIni);
    const dFim = new Date(dataFim);
    if (isNaN(dIni.getTime()) || isNaN(dFim.getTime())) return null;
    const dtIni = new Date(dIni.getUTCFullYear(), dIni.getUTCMonth(), dIni.getUTCDate(), hi, mi || 0, 0, 0);
    const dtFim = new Date(dFim.getUTCFullYear(), dFim.getUTCMonth(), dFim.getUTCDate(), hf, mf || 0, 0, 0);
    const diffMs = dtFim.getTime() - dtIni.getTime();
    if (diffMs < 0) return null;
    const totalMin = Math.floor(diffMs / 60000);
    const hh = Math.floor(totalMin / 60);
    const mm = totalMin % 60;
    return `${String(hh).padStart(2, '0')}:${String(mm).padStart(2, '0')}`;
  } catch {
    return null;
  }
};

function ConditionFilterMulti({ options, selected, onChange }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const onOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    if (open) {
      document.addEventListener('mousedown', onOutside, true);
      return () => document.removeEventListener('mousedown', onOutside, true);
    }
  }, [open]);

  const hasOptions = Array.isArray(options) && options.length > 0;
  const selectedSet = selected instanceof Set ? selected : new Set();
  const totalSelected = selectedSet.size;

  const summaryLabel = (() => {
    if (!hasOptions) return 'Condição';
    if (totalSelected === 0) return 'Todas as condições';
    if (totalSelected === 1) {
      const [only] = Array.from(selectedSet);
      return only;
    }
    const [first] = Array.from(selectedSet);
    return `${first} +${totalSelected - 1}`;
  })();

  const handleToggle = (label, e) => {
    e.preventDefault();
    e.stopPropagation();
    const next = new Set(selectedSet);
    if (next.has(label)) next.delete(label);
    else next.add(label);
    onChange(next);
  };

  const clearFilters = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (totalSelected === 0) return;
    onChange(new Set());
  };

  return (
    <div className="checkout-view-condition-filter-wrap" ref={ref}>
      <GoHoverTooltip text="Filtrar por condição">
        <button
          type="button"
          className="checkout-view-condition-filter-trigger"
          onClick={() => hasOptions && setOpen((o) => !o)}
          disabled={!hasOptions}
          aria-haspopup="listbox"
          aria-expanded={open}
        >
          <Tag size={12} strokeWidth={2} className="checkout-view-condition-filter-icon" aria-hidden />
          <span className="checkout-view-condition-filter-label">{summaryLabel}</span>
          {totalSelected > 0 && (
            <button
              type="button"
              className="checkout-view-condition-filter-clear"
              onClick={clearFilters}
              aria-label="Limpar filtro de condição"
            >
              <X size={11} strokeWidth={2} aria-hidden />
            </button>
          )}
          <span className="checkout-view-condition-filter-chevron" aria-hidden style={{ transform: open ? 'rotate(180deg)' : 'none' }}>▼</span>
        </button>
      </GoHoverTooltip>
      {open && hasOptions && (
        <div className="checkout-view-condition-filter-dropdown" role="listbox" aria-label="Filtrar por condição">
          {options.map((opt) => {
            const isSelected = selectedSet.has(opt.label);
            return (
              <button
                key={opt.label}
                type="button"
                className={`checkout-view-condition-filter-option ${isSelected ? 'is-selected' : ''}`}
                onMouseDown={(e) => handleToggle(opt.label, e)}
              >
                <span className="checkout-view-condition-filter-option-check" aria-hidden>
                  {isSelected ? '✔' : ''}
                </span>
                <span className="checkout-view-condition-filter-option-label" title={opt.label}>{opt.label}</span>
                <span className="checkout-view-condition-filter-option-count">{opt.count}</span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

function TipoOsFilterMulti({ options, selected, onChange }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const onOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    if (open) {
      document.addEventListener('mousedown', onOutside, true);
      return () => document.removeEventListener('mousedown', onOutside, true);
    }
  }, [open]);

  const hasOptions = Array.isArray(options) && options.length > 0;
  const selectedSet = selected instanceof Set ? selected : new Set();
  const totalSelected = selectedSet.size;

  const summaryLabel = (() => {
    if (!hasOptions) return 'Tipo de OS';
    if (totalSelected === 0) return 'Todos os tipos';
    if (totalSelected === 1) {
      const [only] = Array.from(selectedSet);
      return TIPO_OS_NAMES[only] || only;
    }
    const [first] = Array.from(selectedSet);
    return `${TIPO_OS_NAMES[first] || first} +${totalSelected - 1}`;
  })();

  const handleToggle = (tipo, e) => {
    e.preventDefault();
    e.stopPropagation();
    const next = new Set(selectedSet);
    if (next.has(tipo)) next.delete(tipo);
    else next.add(tipo);
    onChange(next);
  };

  const clearFilters = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (totalSelected === 0) return;
    onChange(new Set());
  };

  return (
    <div className="checkout-view-condition-filter-wrap" ref={ref}>
      <GoHoverTooltip text="Filtrar por tipo de OS">
        <button
          type="button"
          className="checkout-view-condition-filter-trigger"
          onClick={() => hasOptions && setOpen((o) => !o)}
          disabled={!hasOptions}
          aria-haspopup="listbox"
          aria-expanded={open}
        >
          <ListFilter size={12} strokeWidth={2} className="checkout-view-condition-filter-icon" aria-hidden />
          <span className="checkout-view-condition-filter-label">{summaryLabel}</span>
          {totalSelected > 0 && (
            <button
              type="button"
              className="checkout-view-condition-filter-clear"
              onClick={clearFilters}
              aria-label="Limpar filtro de tipo de OS"
            >
              <X size={11} strokeWidth={2} aria-hidden />
            </button>
          )}
          <span
            className="checkout-view-condition-filter-chevron"
            aria-hidden
            style={{ transform: open ? 'rotate(180deg)' : 'none' }}
          >
            ▼
          </span>
        </button>
      </GoHoverTooltip>
      {open && hasOptions && (
        <div className="checkout-view-condition-filter-dropdown" role="listbox" aria-label="Filtrar por tipo de OS">
          {options.map((opt) => {
            const isSelected = selectedSet.has(opt.tipo);
            return (
              <button
                key={opt.tipo}
                type="button"
                className={`checkout-view-condition-filter-option ${isSelected ? 'is-selected' : ''}`}
                onMouseDown={(e) => handleToggle(opt.tipo, e)}
              >
                <span className="checkout-view-condition-filter-option-check" aria-hidden>
                  {isSelected ? '✔' : ''}
                </span>
                <span className="checkout-view-condition-filter-option-label" title={opt.label}>
                  {opt.label}
                </span>
                <span className="checkout-view-condition-filter-option-count">{opt.count}</span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

const CheckoutViewModal = ({ isOpen, onClose, asPage = false }) => {
  const [apiAreas, setApiAreas] = useState([]);
  const [apiTechniciansByArea, setApiTechniciansByArea] = useState({});
  const [apiConditionsByAreaTech, setApiConditionsByAreaTech] = useState({});
  const [apiOsByAreaTechCondition, setApiOsByAreaTechCondition] = useState({});
  const [isLoadingCheckout, setIsLoadingCheckout] = useState(false);
  const [selectedAreaIds, setSelectedAreaIds] = useState(() => []);
  const [expandedAreaIds, setExpandedAreaIds] = useState(() => new Set());
  const [selectedTechKeys, setSelectedTechKeys] = useState(() => new Set());
  const [expandedConditionKey, setExpandedConditionKey] = useState(null);
  const [adhocOsEnrichBySerial, setAdhocOsEnrichBySerial] = useState({});
  const adhocEnrichFetchedRef = useRef(new Set());
  const [adhocEquipSearch, setAdhocEquipSearch] = useState({
    open: false,
    serial: '',
    os: null,
    areaId: '',
    techName: '',
    condLabel: '',
  });
  const [adhocOsCreateFlow, setAdhocOsCreateFlow] = useState(null);
  const [techFilterOpen, setTechFilterOpen] = useState(false);
  const [techSearchOpen, setTechSearchOpen] = useState(false);
  const [techSearchQuery, setTechSearchQuery] = useState('');
  const [detailSearchOpen, setDetailSearchOpen] = useState(() => !!asPage);
  const [detailSearchQuery, setDetailSearchQuery] = useState('');
  const [selectedOs, setSelectedOs] = useState(null);
  const [patrimonioMenuOpen, setPatrimonioMenuOpen] = useState(false);
  const [showAtendimentosModal, setShowAtendimentosModal] = useState(false);
  const [pecasPendentes, setPecasPendentes] = useState([]);
  const [pecasTrocadas, setPecasTrocadas] = useState([]);
  const [expandedDisponibilidadeIndex, setExpandedDisponibilidadeIndex] = useState(null);
  const [disponibilidadeData, setDisponibilidadeData] = useState(null);
  const [isLoadingDisponibilidade, setIsLoadingDisponibilidade] = useState(false);
  const [expandedCompatibilidadeIndex, setExpandedCompatibilidadeIndex] = useState(null);
  const [compatibilidadeData, setCompatibilidadeData] = useState(null);
  const [isLoadingCompatibilidade, setIsLoadingCompatibilidade] = useState(false);
  const [showIncluirPecasModal, setShowIncluirPecasModal] = useState(false);
  const [pecasCompativelModeloData, setPecasCompativelModeloData] = useState([]);
  const [isLoadingPecasCompativelModelo, setIsLoadingPecasCompativelModelo] = useState(false);
  const [selectedPecasIncluir, setSelectedPecasIncluir] = useState(new Set());
  const [incluirPecaQuantities, setIncluirPecaQuantities] = useState({});
  const [incluirPecaQuantityEditIndex, setIncluirPecaQuantityEditIndex] = useState(null);
  const [incluirPecaQuantityDraft, setIncluirPecaQuantityDraft] = useState('');
  const [isIncluirPecasLoading, setIsIncluirPecasLoading] = useState(false);
  const [incluirPecasFeedback, setIncluirPecasFeedback] = useState(null);
  const [isRefreshingPecasPendentes, setIsRefreshingPecasPendentes] = useState(false);
  const [isTrocarPecaLoading, setIsTrocarPecaLoading] = useState(false);
  const [trocarPecaFeedback, setTrocarPecaFeedback] = useState(null);
  const [incluirPecasSearchValue, setIncluirPecasSearchValue] = useState('');
  const [incluirPecasSortBy, setIncluirPecasSortBy] = useState(null);
  const [incluirPecasSortDir, setIncluirPecasSortDir] = useState('asc');
  const [isLaudoEditing, setIsLaudoEditing] = useState(false);
  const [laudoEditValue, setLaudoEditValue] = useState('');
  const [isLaudoSaving, setIsLaudoSaving] = useState(false);
  const [laudoSaveFeedback, setLaudoSaveFeedback] = useState(null);
  const [isHorariosEditing, setIsHorariosEditing] = useState(false);
  const [horariosEdit, setHorariosEdit] = useState({
    dataIni: '',
    horaIni: '',
    dataFim: '',
    horaFim: ''
  });
  const [isHorariosSaving, setIsHorariosSaving] = useState(false);
  const [horariosSaveFeedback, setHorariosSaveFeedback] = useState(null);
  const [horariosSaveMessage, setHorariosSaveMessage] = useState('');
  const [isContadoresEditing, setIsContadoresEditing] = useState(false);
  const [contadoresEditValues, setContadoresEditValues] = useState(null);
  const [isContadoresSaving, setIsContadoresSaving] = useState(false);
  const [contadoresFeedback, setContadoresFeedback] = useState(null);
  const [contadoresErrorMsg, setContadoresErrorMsg] = useState('');
  const [excluirPecaLoadingIndex, setExcluirPecaLoadingIndex] = useState(null);
  const [excluirPecaFeedback, setExcluirPecaFeedback] = useState(null);
  const [isConcluirLoading, setIsConcluirLoading] = useState(false);
  const [concluirFeedback, setConcluirFeedback] = useState(null);
  const [concluirMessage, setConcluirMessage] = useState(null);
  const [concluirStepMessage, setConcluirStepMessage] = useState(null);
  const [reincidenciaModal, setReincidenciaModal] = useState({ show: false, novaOs: '', pedidos: [], endereco: '' });
  const [cycleServiceCode, setCycleServiceCode] = useState('');
  const [roteirizarLoading, setRoteirizarLoading] = useState(false);
  const [roteirizarMessage, setRoteirizarMessage] = useState(null);
  const [roteirizarAgendaOpen, setRoteirizarAgendaOpen] = useState(false);
  const reincidenciaOrderIds = React.useMemo(
    () => (reincidenciaModal.novaOs ? [String(reincidenciaModal.novaOs).trim()] : []),
    [reincidenciaModal.novaOs]
  );
  const [isRefreshingData, setIsRefreshingData] = useState(false);
  const [isDraggingCondition, setIsDraggingCondition] = useState(false);
  const [isDragOverDropZone, setIsDragOverDropZone] = useState(false);
  const [dragConditionPayload, setDragConditionPayload] = useState(null);
  const [isConcluirLoteLoading, setIsConcluirLoteLoading] = useState(false);
  const [concluirLoteProgress, setConcluirLoteProgress] = useState(null);
  const [concluirLoteFeedback, setConcluirLoteFeedback] = useState(null);
  const conditionDragJustEndedRef = useRef(false);
  const [isCondicaoEditing, setIsCondicaoEditing] = useState(false);
  const [condicaoEditValue, setCondicaoEditValue] = useState('');
  const [condicaoSearchResults, setCondicaoSearchResults] = useState([]);
  const [condicaoSearchLoading, setCondicaoSearchLoading] = useState(false);
  const [isCondicaoSaving, setIsCondicaoSaving] = useState(false);
  const [condicaoFeedback, setCondicaoFeedback] = useState(null);
  const [selectedOsCondFuncClose, setSelectedOsCondFuncClose] = useState(null);
  const condicaoSearchTimeoutRef = useRef(null);
  const condicaoInputRef = useRef(null);
  const condicaoDropdownRef = useRef(null);
  const [fotos, setFotos] = useState([]);
  const [isFotosLoading, setIsFotosLoading] = useState(false);
  const [showFotosModal, setShowFotosModal] = useState(false);
  const [assinaturaSvg, setAssinaturaSvg] = useState(null);
  const [isAssinaturaLoading, setIsAssinaturaLoading] = useState(false);
  const [showAssinaturaModal, setShowAssinaturaModal] = useState(false);
  const [showEnderecoMapModal, setShowEnderecoMapModal] = useState(false);
  const [osRastreio, setOsRastreio] = useState(null);
  const [isOsRastreioLoading, setIsOsRastreioLoading] = useState(false);
  const [showOsRastreioModal, setShowOsRastreioModal] = useState(false);
  const [zoomFoto, setZoomFoto] = useState(null);
  const [showAlterarStatusModal, setShowAlterarStatusModal] = useState(false);
  const [opcoesStatusConclusao, setOpcoesStatusConclusao] = useState([]);
  const [selectedCodStatusConclusao, setSelectedCodStatusConclusao] = useState('');
  const [isOpcoesStatusLoading, setIsOpcoesStatusLoading] = useState(false);
  const [alterarStatusLoading, setAlterarStatusLoading] = useState(false);
  const [alterarStatusFeedback, setAlterarStatusFeedback] = useState(null);
  const [alterarStatusLoteOs, setAlterarStatusLoteOs] = useState(null);
  const [selectedFotoIdx, setSelectedFotoIdx] = useState(0);
  const [showGerarPedidoModal, setShowGerarPedidoModal] = useState(false);
  const [gerarPedidoDisponiveis, setGerarPedidoDisponiveis] = useState([]);
  const [gerarPedidoIndisponiveis, setGerarPedidoIndisponiveis] = useState([]);
  const [isLoadingGerarPedidoModal, setIsLoadingGerarPedidoModal] = useState(false);
  const gerarPedidoLoadCompletedRef = useRef(false);
  const [gerarPedidoDragSource, setGerarPedidoDragSource] = useState(null);
  const [gerarPedidoSelectedDisponiveis, setGerarPedidoSelectedDisponiveis] = useState(() => new Set());
  const [gerarPedidoSelectedIndisponiveis, setGerarPedidoSelectedIndisponiveis] = useState(() => new Set());
  const [pedidosGeradosPorPeca, setPedidosGeradosPorPeca] = useState(() => ({})); // { "codFechamento|codigo": numeroPedido }
  const [conditionFilterSelected, setConditionFilterSelected] = useState(() => new Set());
  const [tipoOsFilterSelected, setTipoOsFilterSelected] = useState(() => new Set());
  const [isGerarPedidoLoading, setIsGerarPedidoLoading] = useState(false);
  const [gerarPedidoError, setGerarPedidoError] = useState(null);
  const [gerarPedidoSuccessMsg, setGerarPedidoSuccessMsg] = useState(null);
  /** Pedido gerado com falha na reserva: { pedidoCodigo, erros[], tentativas } */
  const [reservaPendente, setReservaPendente] = useState(null);
  const [isReservarPedidoLoading, setIsReservarPedidoLoading] = useState(false);
  const [isGerarPedidoDiretoLoading, setIsGerarPedidoDiretoLoading] = useState(false);
  const [gerarPedidoDiretoMessage, setGerarPedidoDiretoMessage] = useState('');
  const gerarPedidoSuccessTimerRef = useRef(null);
  const [refTooltip, setRefTooltip] = useState(null);
  const [pedidoMenuOpen, setPedidoMenuOpen] = useState(false);
  const pedidoMenuRef = useRef(null);
  const patrimonioMenuRef = useRef(null);
  const techFilterRef = useRef(null);
  const techSearchInputRef = useRef(null);
  const detailSearchInputRef = useRef(null);
  const [pedidoEditModal, setPedidoEditModal] = useState(() => emptyPedidoEditModal());
  const [pedidoTranspTouched, setPedidoTranspTouched] = useState(false);
  const [pedidoClassifTouched, setPedidoClassifTouched] = useState(false);
  const pedidoTranspSearchTimeoutRef = useRef(null);
  const pedidoClassifSearchTimeoutRef = useRef(null);
  const pedidoTranspSearchGenRef = useRef(0);
  const pedidoClassifSearchGenRef = useRef(0);
  const pedidoEditTranspWrapRef = useRef(null);
  const pedidoEditClassifWrapRef = useRef(null);
  const pedidoEditFoneInputRef = useRef(null);
  const areaSidebarRef = useRef(null);
  const techListMainRef = useRef(null);
  const [pageTechCardHeight, setPageTechCardHeight] = useState(null);
  const [pageTechCardFlushSelector, setPageTechCardFlushSelector] = useState(false);

  useEffect(() => {
    if (techSearchOpen && techSearchInputRef.current) techSearchInputRef.current.focus();
  }, [techSearchOpen]);

  // Modo página: cartão de técnicos com altura mínima até a linha inferior do último
  // seletor de área ativo; cresce com o conteúdo até o fim da tela (scroll só interno)
  useEffect(() => {
    if (!asPage || isLoadingCheckout) {
      setPageTechCardHeight(null);
      return;
    }
    const sidebar = areaSidebarRef.current;
    const main = techListMainRef.current;
    if (!sidebar || !main) {
      setPageTechCardHeight(null);
      return;
    }
    const actives = sidebar.querySelectorAll('.checkout-view-area-avatar.active');
    if (actives.length === 0) {
      setPageTechCardHeight(null);
      return;
    }
    const last = actives[actives.length - 1];
    const lastRect = last.getBoundingClientRect();
    const mainRect = main.getBoundingClientRect();
    const containerRect = sidebar.parentElement.getBoundingClientRect();
    // Cartão termina na mesma linha inferior do seletor ativo; 16px = padding inferior do main
    const targetBottom = lastRect.bottom;
    const minHeight = Math.round(targetBottom - mainRect.top) + 16;
    const maxHeight = Math.round(containerRect.bottom - mainRect.top);
    setPageTechCardHeight({
      min: Math.max(120, Math.min(minHeight, maxHeight)),
      max: maxHeight,
    });
  }, [asPage, isLoadingCheckout, apiAreas, selectedAreaIds]);

  // Modo página: canto inferior-esquerdo do cartão reto somente quando o fundo do
  // cartão coincide com a linha inferior do seletor de área ativo (encaixe com a aba)
  useEffect(() => {
    if (!asPage || isLoadingCheckout) {
      setPageTechCardFlushSelector(false);
      return undefined;
    }
    const main = techListMainRef.current;
    const sidebar = areaSidebarRef.current;
    if (!main || !sidebar) {
      setPageTechCardFlushSelector(false);
      return undefined;
    }
    const check = () => {
      const actives = sidebar.querySelectorAll('.checkout-view-area-avatar.active');
      if (actives.length === 0) {
        setPageTechCardFlushSelector(false);
        return;
      }
      const lastRect = actives[actives.length - 1].getBoundingClientRect();
      const mainRect = main.getBoundingClientRect();
      const cardBottom = mainRect.bottom - 16; // padding inferior do main
      setPageTechCardFlushSelector(Math.abs(cardBottom - lastRect.bottom) <= 2);
    };
    check();
    const ro = new ResizeObserver(check);
    ro.observe(main);
    return () => ro.disconnect();
  }, [asPage, isLoadingCheckout, apiAreas, selectedAreaIds, pageTechCardHeight]);

  useEffect(() => {
    if (detailSearchOpen && detailSearchInputRef.current) detailSearchInputRef.current.focus();
  }, [detailSearchOpen]);

  useEffect(() => {
    if (!selectedOs) {
      setIsLaudoEditing(false);
      setLaudoSaveFeedback(null);
      return;
    }
    setLaudoEditValue(selectedOs.os?.TB02122_OBS ?? '');
    setIsLaudoEditing(false);
    setLaudoSaveFeedback(null);
  }, [selectedOs?.os?.numeroOS]);

  useEffect(() => {
    if (!selectedOs) {
      setIsHorariosEditing(false);
      setHorariosSaveFeedback(null);
      setHorariosSaveMessage('');
      return;
    }
    setIsHorariosEditing(false);
    setHorariosSaveFeedback(null);
    setHorariosSaveMessage('');
  }, [selectedOs?.os?.numeroOS]);

  useEffect(() => {
    if (!selectedOs) {
      setIsContadoresEditing(false);
      setContadoresEditValues(null);
      setContadoresFeedback(null);
      setIsContadoresSaving(false);
      return;
    }
    setIsContadoresEditing(false);
    setContadoresEditValues(null);
    setContadoresFeedback(null);
    setIsContadoresSaving(false);
  }, [selectedOs?.os?.numeroOS]);

  useEffect(() => {
    if (!selectedOs) {
      setIsCondicaoEditing(false);
      setCondicaoFeedback(null);
      setSelectedOsCondFuncClose(null);
      return;
    }
    setCondicaoEditValue(selectedOs.conditionLabel ?? '');
    setIsCondicaoEditing(false);
    setCondicaoFeedback(null);
    setSelectedOsCondFuncClose(null);
  }, [selectedOs?.os?.numeroOS]);

  useEffect(() => {
    if (!selectedOs) {
      setExpandedDisponibilidadeIndex(null);
      setDisponibilidadeData(null);
    }
  }, [selectedOs]);

  const handleDisponibilidadeClick = useCallback(async (index) => {
    if (expandedDisponibilidadeIndex === index) {
      setExpandedDisponibilidadeIndex(null);
      setDisponibilidadeData(null);
      return;
    }
    const peca = pecasPendentes[index];
    if (!peca?.codigo || !selectedOs?.os) return;
    const codEmp = selectedOs.os.TB02122_CODEMP != null ? String(selectedOs.os.TB02122_CODEMP).trim() : '';
    setExpandedDisponibilidadeIndex(index);
    setDisponibilidadeData(null);
    setIsLoadingDisponibilidade(true);
    try {
      const token = localStorage.getItem('authToken');
      if (!token) return;
      const rows = await fetchDisponibilidadeEstoque(token, peca.codigo);
      let naEmpresa = 0;
      let outrasEmpresas = 0;
      for (const r of rows || []) {
        const qtde = typeof r.QTDE === 'number' ? r.QTDE : parseInt(r.QTDE, 10) || 0;
        const emp = r.EMP != null ? String(r.EMP).trim() : '';
        if (emp === codEmp) naEmpresa += qtde;
        else outrasEmpresas += qtde;
      }
      setDisponibilidadeData({ naEmpresa, outrasEmpresas });
    } catch {
      setDisponibilidadeData({ naEmpresa: 0, outrasEmpresas: 0 });
    } finally {
      setIsLoadingDisponibilidade(false);
    }
  }, [expandedDisponibilidadeIndex, pecasPendentes, selectedOs?.os]);

  const handleCompatibilidadeClick = useCallback(async (index) => {
    if (expandedCompatibilidadeIndex === index) {
      setExpandedCompatibilidadeIndex(null);
      setCompatibilidadeData(null);
      return;
    }
    const peca = pecasPendentes[index];
    if (!peca?.codigo || !selectedOs?.os) return;
    const codEmp = selectedOs.os.TB02122_CODEMP != null ? String(selectedOs.os.TB02122_CODEMP).trim() : '';
    setExpandedCompatibilidadeIndex(index);
    setCompatibilidadeData(null);
    setIsLoadingCompatibilidade(true);
    try {
      const token = localStorage.getItem('authToken');
      if (!token) return;
      const rows = await fetchCompatibilidade(token, peca.codigo, codEmp);
      setCompatibilidadeData(rows || []);
    } catch {
      setCompatibilidadeData([]);
    } finally {
      setIsLoadingCompatibilidade(false);
    }
  }, [expandedCompatibilidadeIndex, pecasPendentes, selectedOs?.os]);

  const handleOpenIncluirPecasModal = useCallback(async () => {
    if (!selectedOs?.os) return;
    const codProdMaquina = selectedOs.os.TB02122_PRODUTO;
    const codFechamento = selectedOs.os.TB02122_CODIGO;
    setShowIncluirPecasModal(true);
    setPecasCompativelModeloData([]);
    setSelectedPecasIncluir(new Set());
    setIncluirPecasSearchValue('');
    setIncluirPecasSortBy(null);
    setIncluirPecasSortDir('asc');
    setIsLoadingPecasCompativelModelo(true);
    try {
      const token = localStorage.getItem('authToken');
      if (!token) return;
      const rows = await fetchPecasCompativelModelo(token, codProdMaquina, codFechamento);
      setPecasCompativelModeloData(rows || []);
    } catch {
      setPecasCompativelModeloData([]);
    } finally {
      setIsLoadingPecasCompativelModelo(false);
    }
  }, [selectedOs?.os]);

  const togglePecaIncluirSelection = useCallback((index) => {
    setSelectedPecasIncluir((prev) => {
      const next = new Set(prev);
      if (next.has(index)) {
        next.delete(index);
        setIncluirPecaQuantities((q) => {
          const o = { ...q };
          delete o[index];
          return o;
        });
      } else next.add(index);
      return next;
    });
  }, []);

  const startIncluirPecaQuantityEdit = useCallback((originalIdx) => {
    setIncluirPecaQuantityEditIndex(originalIdx);
    setIncluirPecaQuantityDraft(String(incluirPecaQuantities[originalIdx] ?? 1));
  }, [incluirPecaQuantities]);

  const confirmIncluirPecaQuantity = useCallback((originalIdx) => {
    const n = parseInt(incluirPecaQuantityDraft.replace(/\D/g, '') || '0', 10);
    if (n >= 1) {
      setSelectedPecasIncluir((prev) => new Set(prev).add(originalIdx));
      setIncluirPecaQuantities((prev) => ({ ...prev, [originalIdx]: n }));
    }
    setIncluirPecaQuantityEditIndex(null);
    setIncluirPecaQuantityDraft('');
  }, [incluirPecaQuantityDraft]);

  const cancelIncluirPecaQuantity = useCallback(() => {
    setIncluirPecaQuantityEditIndex(null);
    setIncluirPecaQuantityDraft('');
  }, []);

  const handleIncluirPecasConfirm = useCallback(async () => {
    if (selectedPecasIncluir.size === 0 || !selectedOs?.os?.TB02122_CODIGO || isIncluirPecasLoading) return;
    const token = localStorage.getItem('authToken');
    if (!token) return;
    const sortedSelected = [...selectedPecasIncluir].sort((a, b) => a - b);
    const itens = sortedSelected.map((idx) => {
      const r = pecasCompativelModeloData[idx];
      return {
        codProd: r.TB01010_CODIGO ?? '',
        qtde: incluirPecaQuantities[idx] ?? 1
      };
    }).filter((x) => x.codProd);
    if (itens.length === 0) return;
    setIsIncluirPecasLoading(true);
    setIncluirPecasFeedback(null);
    try {
      await incluirPecasPendentes(token, selectedOs.os.TB02122_CODIGO, itens);
      setIncluirPecasFeedback('success');
      setSelectedPecasIncluir(new Set());
      setIncluirPecaQuantities({});
      setIncluirPecaQuantityEditIndex(null);
      setIncluirPecaQuantityDraft('');
      setShowIncluirPecasModal(false);
      setIsRefreshingPecasPendentes(true);
      const data = await fetchPecasPendentes(token, selectedOs.os.TB02122_CODIGO);
      setPecasPendentes(data || []);
      if (selectedOs?.os?.TB02122_CODIGO != null) {
        const codFech = selectedOs.os.TB02122_CODIGO;
        setPedidosGeradosPorPeca((prev) => {
          const next = { ...prev };
          Object.keys(next).forEach((k) => { if (String(k).startsWith(`${codFech}-`)) delete next[k]; });
          (data || []).forEach((p) => {
            if (p.pedidoGerado != null && p.pedidoGerado !== '') next[`${codFech}-${p.codigo}`] = String(p.pedidoGerado);
          });
          return next;
        });
      }
      setExpandedDisponibilidadeIndex(null);
      setExpandedCompatibilidadeIndex(null);
      setTimeout(() => {
        setIncluirPecasFeedback(null);
      }, 2800);
    } catch {
      setIncluirPecasFeedback('error');
      setTimeout(() => setIncluirPecasFeedback(null), 2800);
    } finally {
      setIsIncluirPecasLoading(false);
      setIsRefreshingPecasPendentes(false);
    }
  }, [selectedPecasIncluir, selectedOs?.os?.TB02122_CODIGO, pecasCompativelModeloData, incluirPecaQuantities, isIncluirPecasLoading]);

  const handleTrocarPeca = useCallback(async (pendenteIndex, codProdIncluir) => {
    if (!selectedOs?.os?.TB02122_CODIGO || isTrocarPecaLoading) return;
    const peca = pecasPendentes[pendenteIndex];
    if (!peca?.codigo || !codProdIncluir) return;
    const token = localStorage.getItem('authToken');
    if (!token) return;
    const qtde = parseInt(peca.qtde, 10) || 1;
    setIsTrocarPecaLoading(true);
    setTrocarPecaFeedback(null);
    try {
      await trocarPecaPendente(token, selectedOs.os.TB02122_CODIGO, peca.codigo, String(codProdIncluir).trim(), qtde);
      const data = await fetchPecasPendentes(token, selectedOs.os.TB02122_CODIGO);
      setPecasPendentes(data || []);
      if (selectedOs?.os?.TB02122_CODIGO != null) {
        const codFech = selectedOs.os.TB02122_CODIGO;
        setPedidosGeradosPorPeca((prev) => {
          const next = { ...prev };
          Object.keys(next).forEach((k) => { if (String(k).startsWith(`${codFech}-`)) delete next[k]; });
          (data || []).forEach((p) => {
            if (p.pedidoGerado != null && p.pedidoGerado !== '') next[`${codFech}-${p.codigo}`] = String(p.pedidoGerado);
          });
          return next;
        });
      }
      setExpandedCompatibilidadeIndex(null);
      setExpandedDisponibilidadeIndex(null);
      setTrocarPecaFeedback('success');
      setTimeout(() => setTrocarPecaFeedback(null), 2800);
    } catch {
      setTrocarPecaFeedback('error');
      setTimeout(() => setTrocarPecaFeedback(null), 2800);
    } finally {
      setIsTrocarPecaLoading(false);
    }
  }, [selectedOs?.os?.TB02122_CODIGO, pecasPendentes, isTrocarPecaLoading]);

  // Carrega fotos (se existirem) sempre que uma nova OS é selecionada no painel de detalhes
  useEffect(() => {
    const numeroOS = selectedOs?.os?.numeroOS;
    if (!numeroOS) {
      setFotos([]);
      setSelectedFotoIdx(0);
      setIsFotosLoading(false);
      setShowFotosModal(false);
      return;
    }
    const token = localStorage.getItem('authToken');
    if (!token) return;
    let cancelled = false;
    setIsFotosLoading(true);
    setFotos([]);
    setShowFotosModal(false);
    fetchFotos(token, numeroOS)
      .then((data) => {
        if (cancelled) return;
        setFotos(data || []);
        setSelectedFotoIdx(0);
      })
      .catch(() => {
        if (cancelled) return;
        setFotos([]);
        setSelectedFotoIdx(0);
      })
      .finally(() => {
        if (!cancelled) setIsFotosLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [selectedOs?.os?.numeroOS]);

  // Carrega assinatura SVG (se existir) ao selecionar OS no painel de detalhes
  useEffect(() => {
    const numeroOS = selectedOs?.os?.numeroOS;
    if (!numeroOS) {
      setAssinaturaSvg(null);
      setIsAssinaturaLoading(false);
      setShowAssinaturaModal(false);
      return;
    }
    const token = localStorage.getItem('authToken');
    if (!token) return;
    let cancelled = false;
    setIsAssinaturaLoading(true);
    setAssinaturaSvg(null);
    setShowAssinaturaModal(false);
    fetchAssinatura(token, numeroOS)
      .then((data) => {
        if (cancelled) return;
        setAssinaturaSvg(data?.signatureSvg || null);
      })
      .catch(() => {
        if (cancelled) return;
        setAssinaturaSvg(null);
      })
      .finally(() => {
        if (!cancelled) setIsAssinaturaLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [selectedOs?.os?.numeroOS]);

  // Carrega rastreio GPS (se existir) ao selecionar OS — botão só aparece com dados
  useEffect(() => {
    const numeroOS = selectedOs?.os?.numeroOS;
    if (!numeroOS) {
      setOsRastreio(null);
      setIsOsRastreioLoading(false);
      setShowOsRastreioModal(false);
      return;
    }
    const token = localStorage.getItem('authToken');
    if (!token) return;
    let cancelled = false;
    setIsOsRastreioLoading(true);
    setOsRastreio(null);
    setShowOsRastreioModal(false);
    fetchOsRastreio(token, numeroOS)
      .then((data) => {
        if (cancelled) return;
        setOsRastreio(data || null);
      })
      .catch(() => {
        if (cancelled) return;
        setOsRastreio(null);
      })
      .finally(() => {
        if (!cancelled) setIsOsRastreioLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [selectedOs?.os?.numeroOS]);

  const refreshCheckoutResumo = useCallback(async () => {
    const token = localStorage.getItem('authToken');
    if (!token) return null;
    try {
      const data = await fetchCheckoutResumo(token);
      if (data) {
        setApiAreas(data.areas || []);
        setApiTechniciansByArea(data.techniciansByArea || {});
        setApiConditionsByAreaTech(data.conditionsByAreaTech || {});
        setApiOsByAreaTechCondition(data.osByAreaTechCondition || {});
        return data;
      }
    } catch {
      // falha silenciosa no refresh
    }
    return null;
  }, []);

  const handleAtualizarDados = useCallback(async () => {
    setTechFilterOpen(false);
    const token = localStorage.getItem('authToken');
    if (!token) return;
    setIsRefreshingData(true);
    let codigoOsParaPecas = null;
    try {
      const data = await fetchCheckoutResumo(token);
      if (data) {
        setApiAreas(data.areas || []);
        setApiTechniciansByArea(data.techniciansByArea || {});
        setApiConditionsByAreaTech(data.conditionsByAreaTech || {});
        setApiOsByAreaTechCondition(data.osByAreaTechCondition || {});
        setAdhocOsEnrichBySerial({});
        adhocEnrichFetchedRef.current = new Set();
        setExpandedConditionKey(null);
        if (selectedOs && data.osByAreaTechCondition) {
          const key = selectedOs.areaId;
          const condMap = data.osByAreaTechCondition[key];
          const list = condMap?.[selectedOs.techName]?.[selectedOs.conditionLabel] || [];
          const stillExists = list.some(
            (o) => o.numeroOS === selectedOs.os?.numeroOS && String(o.numeroSerie || '') === String(selectedOs.os?.numeroSerie || '')
          );
          if (!stillExists) {
            setSelectedOs(null);
            setPatrimonioMenuOpen(false);
            setShowAtendimentosModal(false);
          } else if (selectedOs?.os?.TB02122_CODIGO != null) {
            codigoOsParaPecas = selectedOs.os.TB02122_CODIGO;
          }
        }
      }
      if (codigoOsParaPecas != null) {
        const pecasData = await fetchPecasPendentes(token, codigoOsParaPecas);
        setPecasPendentes(pecasData || []);
        setPedidosGeradosPorPeca((prev) => {
          const next = { ...prev };
          Object.keys(next).forEach((k) => { if (String(k).startsWith(`${codigoOsParaPecas}-`)) delete next[k]; });
          (pecasData || []).forEach((p) => {
            if (p.pedidoGerado != null && p.pedidoGerado !== '') next[`${codigoOsParaPecas}-${p.codigo}`] = String(p.pedidoGerado);
          });
          return next;
        });
      }
    } catch {
      // falha silenciosa
    } finally {
      setIsRefreshingData(false);
    }
  }, [selectedOs]);

  const pedidosParaConcluir = React.useMemo(() => {
    const codFech = selectedOs?.os?.TB02122_CODIGO;
    if (codFech == null) return [];
    const set = new Set();
    (pecasPendentes || []).forEach((peca) => {
      const key = `${codFech}-${peca.codigo}`;
      const numeroPedido = pedidosGeradosPorPeca[key] ?? peca.pedidoGerado ?? '';
      if (numeroPedido && String(numeroPedido).trim()) set.add(String(numeroPedido).trim());
    });
    return Array.from(set);
  }, [selectedOs?.os?.TB02122_CODIGO, pecasPendentes, pedidosGeradosPorPeca]);

  const handleConcluir = useCallback(async () => {
    if (!selectedOs?.os?.numeroOS || isConcluirLoading) return;
    const token = localStorage.getItem('authToken');
    if (!token) return;
    setIsConcluirLoading(true);
    setConcluirFeedback(null);
    setConcluirMessage(null);
    setConcluirStepMessage(pedidosParaConcluir.length > 0 && !selectedOs.os.isNative ? 'Gerando nova OS de reincidência...' : 'Concluindo...');
    try {
      const base = getApiBaseUrl();
      const body = {
        codFechamento: selectedOs.os.TB02122_CODIGO,
        numeroOS: selectedOs.os.numeroOS,
        numeroSerie: selectedOs.os.numeroSerie || '',
        codCli: selectedOs.os.TB02122_CODCLI,
        codEmp: selectedOs.os.TB02122_CODEMP
      };
      if (pedidosParaConcluir.length > 0) body.pedidos = pedidosParaConcluir;
      const url = `${base}/api/checkout-view/concluir`;
      const res = await fetch(url, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok && data.success) {
        if (data.needConfirmReincidencia && data.novaOsReincidencia != null) {
          setReincidenciaModal({
            show: true,
            novaOs: String(data.novaOsReincidencia),
            pedidos: Array.isArray(data.pedidos) ? data.pedidos : [],
            endereco: (selectedOs?.os?.endereco && String(selectedOs.os.endereco).trim()) || '',
            nativeReturn: data.nativeReturn || null,
            askService: data.askServiceOnNewCycle === true,
          });
          setCycleServiceCode('');
          setConcluirStepMessage(null);
          setIsConcluirLoading(false);
          return;
        }
        setConcluirFeedback('success');
        (async () => {
          try {
            const t = localStorage.getItem('authToken');
            if (t && selectedOs?.os?.TB02122_CODIGO != null) {
              const r = await sendOSConcluida(t, [selectedOs.os.TB02122_CODIGO]);
              console.log('[Email OS concluída]', r.success ? r.data : r.message);
            }
          } catch (e) {
            console.warn('[Email OS concluída]', e.message);
          }
        })();
        const configRes = await fetch(`${base}/api/config/troca-status-conclusao`, {
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }
        });
        const configData = await configRes.json().catch(() => ({}));
        const codstatus = configData.success && configData.data ? (configData.data.codstatus || '00') : '00';
        if (codstatus === '01') {
          setTimeout(() => {
            setConcluirFeedback(null);
            setShowAlterarStatusModal(true);
            setSelectedCodStatusConclusao('');
            setOpcoesStatusConclusao([]);
            setAlterarStatusFeedback(null);
          }, 1500);
        } else {
          const dataResumo = await refreshCheckoutResumo();
          if (dataResumo && dataResumo.osByAreaTechCondition) {
            setSelectedTechKeys((prev) => {
              const next = new Set(prev);
              for (const key of prev) {
                const [areaId, techName] = key.split('|');
                const condMap = dataResumo.osByAreaTechCondition[areaId]?.[techName];
                if (!condMap) {
                  next.delete(key);
                  continue;
                }
                let total = 0;
                for (const condLabel of Object.keys(condMap)) {
                  total += (condMap[condLabel] || []).length;
                }
                if (total === 0) next.delete(key);
              }
              return next;
            });
          }
          setSelectedOs(null);
          setPatrimonioMenuOpen(false);
          setShowAtendimentosModal(false);
          setTimeout(() => setConcluirFeedback(null), 2200);
        }
      } else {
        if (selectedOs.os.isNative && res.status >= 400 && res.status < 500 && data.message) setConcluirMessage(data.message);
        setConcluirFeedback('error');
        setTimeout(() => setConcluirFeedback(null), selectedOs.os.isNative ? 5000 : 2800);
      }
    } catch {
      setConcluirFeedback('error');
      setTimeout(() => setConcluirFeedback(null), 2800);
    } finally {
      setConcluirStepMessage(null);
      setIsConcluirLoading(false);
    }
  }, [selectedOs, isConcluirLoading, pedidosParaConcluir, refreshCheckoutResumo, setSelectedOs, setPatrimonioMenuOpen, setShowAtendimentosModal, setSelectedTechKeys]);

  const closeReincidenciaModal = useCallback(() => {
    setReincidenciaModal({ show: false, novaOs: '', pedidos: [], endereco: '' });
    setCycleServiceCode('');
    setRoteirizarMessage(null);
    setRoteirizarAgendaOpen(false);
  }, []);

  const handleReincidenciaConfirm = useCallback(async () => {
    const { novaOs, pedidos: peds, nativeReturn, askService } = reincidenciaModal;
    if (askService && !cycleServiceCode) return;
    if (!novaOs) {
      closeReincidenciaModal();
      return;
    }
    const token = localStorage.getItem('authToken');
    if (!token || !selectedOs?.os) return;
    setReincidenciaModal({ show: false, novaOs: '', pedidos: [], endereco: '' });
    setCycleServiceCode('');
    setRoteirizarMessage(null);
    setRoteirizarAgendaOpen(false);
    setIsConcluirLoading(true);
    const base = getApiBaseUrl();
    try {
      if (peds.length > 0 && !nativeReturn) {
        setConcluirStepMessage('Atualizando dados dos pedidos de peças...');
        const vincRes = await fetch(`${base}/api/checkout-view/concluir-vincular-pedidos-reincidencia`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({ novaOsReincidencia: novaOs, pedidos: peds })
        });
        if (!vincRes.ok) throw new Error('Vincular pedidos falhou');
      }
      const data = { success: true };
      if (data.success) {
        setConcluirMessage(nativeReturn ? `OS ${novaOs} voltou como retorno (ciclo ${nativeReturn.cycleNo})` : null);
        setConcluirFeedback('success');
        (async () => {
          try {
            const t = localStorage.getItem('authToken');
            if (t && selectedOs?.os?.TB02122_CODIGO != null && !nativeReturn) {
              const r = await sendOSConcluida(t, [selectedOs.os.TB02122_CODIGO]);
              console.log('[Email OS concluída]', r.success ? r.data : r.message);
            }
          } catch (e) {
            console.warn('[Email OS concluída]', e.message);
          }
        })();
        let codstatus = '00';
        if (!nativeReturn) {
          const configRes = await fetch(`${base}/api/config/troca-status-conclusao`, { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } });
          const configData = await configRes.json().catch(() => ({}));
          codstatus = configData.success && configData.data ? (configData.data.codstatus || '00') : '00';
        }
        if (codstatus === '01') {
          setTimeout(() => {
            setConcluirFeedback(null);
            setShowAlterarStatusModal(true);
            setSelectedCodStatusConclusao('');
            setOpcoesStatusConclusao([]);
            setAlterarStatusFeedback(null);
          }, 1500);
        } else {
          const areaId = selectedOs.areaId;
          const techName = selectedOs.techName;
          const conditionLabel = selectedOs.conditionLabel;
          const osNum = selectedOs.os?.numeroOS;
          const osSerie = selectedOs.os?.numeroSerie ?? '';
          setApiOsByAreaTechCondition((prev) => {
            const next = JSON.parse(JSON.stringify(prev || {}));
            if (!next[areaId]?.[techName]?.[conditionLabel]) return next;
            next[areaId][techName][conditionLabel] = next[areaId][techName][conditionLabel].filter(
              (o) => !(String(o?.numeroOS) === String(osNum) && String(o?.numeroSerie ?? '') === String(osSerie))
            );
            if (next[areaId][techName][conditionLabel].length === 0) delete next[areaId][techName][conditionLabel];
            return next;
          });
          setSelectedOs(null);
          setPatrimonioMenuOpen(false);
          setShowAtendimentosModal(false);
          const dataResumo = await refreshCheckoutResumo();
          if (dataResumo?.osByAreaTechCondition) {
            setSelectedTechKeys((prev) => {
              const next = new Set(prev);
              for (const key of prev) {
                const [aid, tname] = key.split('|');
                const condMap = dataResumo.osByAreaTechCondition[aid]?.[tname];
                if (!condMap) { next.delete(key); continue; }
                let total = 0;
                for (const condLabel of Object.keys(condMap)) total += (condMap[condLabel] || []).length;
                if (total === 0) next.delete(key);
              }
              return next;
            });
          }
          setTimeout(() => setConcluirFeedback(null), 2200);
        }
      } else {
        setConcluirFeedback('error');
        setTimeout(() => setConcluirFeedback(null), 2800);
      }
    } catch {
      setConcluirFeedback('error');
      setTimeout(() => setConcluirFeedback(null), 2800);
    } finally {
      setConcluirStepMessage(null);
      setIsConcluirLoading(false);
    }
  }, [reincidenciaModal, cycleServiceCode, selectedOs?.os, refreshCheckoutResumo, setSelectedTechKeys, setSelectedOs, setPatrimonioMenuOpen, setShowAtendimentosModal, closeReincidenciaModal]);

  const handleTentarRoteirizar = useCallback(() => {
    if (reincidenciaModal.askService && !cycleServiceCode) return;
    if (!reincidenciaModal.novaOs?.trim()) {
      setRoteirizarMessage('Número da OS de reincidência não disponível.');
      return;
    }
    setRoteirizarMessage(null);
    setRoteirizarAgendaOpen(true);
  }, [reincidenciaModal.novaOs, reincidenciaModal.askService, cycleServiceCode]);

  const handleRoteirizarAgendaConfirm = useCallback(async ({ technicianId, scheduleType, scheduleDate, semPrevisao, standbyNotes }) => {
    const numeroOS = reincidenciaModal.novaOs?.trim();
    if (!numeroOS || scheduleType === 'apenas_tecnico') return;
    const codTec = technicianId != null && String(technicianId).trim() !== '' ? String(technicianId).trim() : null;
    if (!codTec) {
      setRoteirizarAgendaOpen(false);
      setRoteirizarMessage('Selecione um técnico.');
      return;
    }
    setRoteirizarLoading(true);
    setRoteirizarMessage(null);
    let ok = false;
    try {
      const res = await fetch(`${getApiBaseUrl()}/api/checkout-view/programar-os-reincidencia`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${localStorage.getItem('authToken')}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          numeroOS,
          scheduleType,
          codTec,
          scheduleDate: scheduleType === 'future' && !semPrevisao && scheduleDate ? scheduleDate : undefined,
          standbyNotes: scheduleType === 'future' && semPrevisao && standbyNotes ? standbyNotes : undefined
        })
      });
      const data = await res.json().catch(() => ({}));
      ok = !!data.success;
      if (!ok) setRoteirizarMessage(data.message || 'Erro ao programar.');
    } catch (e) {
      setRoteirizarMessage(e.message || 'Erro ao programar.');
    } finally {
      setRoteirizarLoading(false);
      setRoteirizarAgendaOpen(false);
    }
    if (ok) await handleReincidenciaConfirm();
  }, [reincidenciaModal.novaOs, handleReincidenciaConfirm]);

  useEffect(() => {
    if (!showAlterarStatusModal) return;
    const token = localStorage.getItem('authToken');
    if (!token) return;
    let cancelled = false;
    setIsOpcoesStatusLoading(true);
    const base = getApiBaseUrl();
    fetch(`${base}/api/checkout-view/opcoes-status-conclusao`, {
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }
    })
      .then((r) => r.json())
      .then((d) => {
        if (!cancelled && d.success && Array.isArray(d.data)) setOpcoesStatusConclusao(d.data);
      })
      .catch(() => { if (!cancelled) setOpcoesStatusConclusao([]); })
      .finally(() => { if (!cancelled) setIsOpcoesStatusLoading(false); });
    return () => { cancelled = true; };
  }, [showAlterarStatusModal]);

  useEffect(() => {
    if (!showGerarPedidoModal || !selectedOs?.os || pecasPendentes.length === 0) return;
    const codEmp = selectedOs.os.TB02122_CODEMP != null ? String(selectedOs.os.TB02122_CODEMP).trim() : '';
    const codFechamento = selectedOs.os.TB02122_CODIGO;
    const stockScope = getConditionStockValidationScope(selectedOs, apiConditionsByAreaTech);
    let cancelled = false;
    setIsLoadingGerarPedidoModal(true);
    setGerarPedidoDisponiveis([]);
    setGerarPedidoIndisponiveis([]);
    (async () => {
      const disponiveis = [];
      const indisponiveis = [];
      for (const peca of pecasPendentes) {
        if (cancelled || !peca?.codigo) continue;
        if (codFechamento != null && (pedidosGeradosPorPeca[`${codFechamento}-${peca.codigo}`] ?? peca.pedidoGerado)) continue;
        try {
          const token = localStorage.getItem('authToken');
          if (!token) break;
          const rows = await fetchDisponibilidadeEstoque(token, peca.codigo);
          let naEmpresa = 0;
          let outrasEmpresas = 0;
          for (const r of rows || []) {
            const qtde = typeof r.QTDE === 'number' ? r.QTDE : parseInt(r.QTDE, 10) || 0;
            const emp = r.EMP != null ? String(r.EMP).trim() : '';
            if (emp === codEmp) naEmpresa += qtde;
            else outrasEmpresas += qtde;
          }
          const saldo = resolveStockValidationQty(naEmpresa, outrasEmpresas, stockScope);
          const item = { peca, saldo };
          if (saldo > 0) disponiveis.push(item);
          else indisponiveis.push(item);
        } catch {
          indisponiveis.push({ peca, saldo: 0 });
        }
      }
      if (!cancelled) {
        setGerarPedidoDisponiveis(disponiveis);
        setGerarPedidoIndisponiveis(indisponiveis);
      }
    })().finally(() => {
      if (!cancelled) {
        gerarPedidoLoadCompletedRef.current = true;
        setIsLoadingGerarPedidoModal(false);
      }
    });
    return () => { cancelled = true; };
  }, [showGerarPedidoModal, selectedOs?.os?.TB02122_CODEMP, selectedOs?.os?.TB02122_CODIGO, selectedOs?.areaId, selectedOs?.techName, selectedOs?.conditionLabel, apiConditionsByAreaTech, pecasPendentes, pedidosGeradosPorPeca]);

  useEffect(() => {
    if (!showGerarPedidoModal) {
      gerarPedidoLoadCompletedRef.current = false;
      setGerarPedidoSelectedDisponiveis(new Set());
      setGerarPedidoSelectedIndisponiveis(new Set());
    }
  }, [showGerarPedidoModal]);

  useEffect(() => {
    if (showGerarPedidoModal && gerarPedidoLoadCompletedRef.current && !isLoadingGerarPedidoModal && gerarPedidoDisponiveis.length === 0 && gerarPedidoIndisponiveis.length === 0) {
      setShowGerarPedidoModal(false);
    }
  }, [showGerarPedidoModal, isLoadingGerarPedidoModal, gerarPedidoDisponiveis.length, gerarPedidoIndisponiveis.length]);

  const toggleGerarPedidoSelection = useCallback((list, index) => {
    if (list === 'disponiveis') {
      setGerarPedidoSelectedDisponiveis((prev) => {
        const next = new Set(prev);
        if (next.has(index)) next.delete(index); else next.add(index);
        return next;
      });
    } else {
      setGerarPedidoSelectedIndisponiveis((prev) => {
        const next = new Set(prev);
        if (next.has(index)) next.delete(index); else next.add(index);
        return next;
      });
    }
  }, []);

  const handleGerarPedidoDrop = useCallback((targetList) => {
    if (gerarPedidoDragSource == null) return;
    const { list, index } = gerarPedidoDragSource;
    if (list === targetList) return;
    const getter = targetList === 'disponiveis' ? gerarPedidoDisponiveis : gerarPedidoIndisponiveis;
    const setter = targetList === 'disponiveis' ? setGerarPedidoDisponiveis : setGerarPedidoIndisponiveis;
    const source = list === 'disponiveis' ? [...gerarPedidoDisponiveis] : [...gerarPedidoIndisponiveis];
    const sourceSetter = list === 'disponiveis' ? setGerarPedidoDisponiveis : setGerarPedidoIndisponiveis;
    const item = source[index];
    if (!item) return;
    const newSource = source.filter((_, i) => i !== index);
    sourceSetter(newSource);
    setter([...getter, item]);
    setGerarPedidoDragSource(null);
    setGerarPedidoSelectedDisponiveis(new Set());
    setGerarPedidoSelectedIndisponiveis(new Set());
  }, [gerarPedidoDragSource, gerarPedidoDisponiveis, gerarPedidoIndisponiveis]);

  const clearGerarPedidoSuccessTimer = useCallback(() => {
    if (gerarPedidoSuccessTimerRef.current) {
      clearTimeout(gerarPedidoSuccessTimerRef.current);
      gerarPedidoSuccessTimerRef.current = null;
    }
  }, []);

  const showGerarPedidoSuccess = useCallback((msg) => {
    clearGerarPedidoSuccessTimer();
    setReservaPendente(null);
    setGerarPedidoSuccessMsg(msg);
    gerarPedidoSuccessTimerRef.current = setTimeout(() => {
      setGerarPedidoSuccessMsg(null);
      gerarPedidoSuccessTimerRef.current = null;
    }, 3000);
  }, [clearGerarPedidoSuccessTimer]);

  const applyReservaFromGerarResult = useCallback((result) => {
    const reserva = result?.reserva;
    if (reserva?.attempted && !reserva.ok) {
      clearGerarPedidoSuccessTimer();
      setGerarPedidoSuccessMsg(null);
      setReservaPendente({
        pedidoCodigo: result.pedidoGerado || '',
        erros: Array.isArray(reserva.erros) ? reserva.erros : [],
        tentativas: reserva.tentativas || 0
      });
      return;
    }
    showGerarPedidoSuccess(
      result.pedidoGerado ? `Pedido ${result.pedidoGerado} gerado.` : 'Pedido gerado.'
    );
  }, [clearGerarPedidoSuccessTimer, showGerarPedidoSuccess]);

  const handleRetryReservarPedido = useCallback(async () => {
    const codFechamento = selectedOs?.os?.TB02122_CODIGO;
    const pedidoCodigo = reservaPendente?.pedidoCodigo;
    if (codFechamento == null || !pedidoCodigo) return;
    const token = localStorage.getItem('authToken');
    if (!token) return;
    setIsReservarPedidoLoading(true);
    try {
      const result = await fetchReservarPedido(token, { codFechamento, pedidoCodigo });
      if (result.reserva?.ok) {
        setReservaPendente(null);
        showGerarPedidoSuccess(`Pedido ${pedidoCodigo} reservado.`);
        return;
      }
      setReservaPendente({
        pedidoCodigo,
        erros: Array.isArray(result.reserva?.erros) ? result.reserva.erros : [{ message: 'Erro ao reservar estoque' }],
        tentativas: result.reserva?.tentativas || 0
      });
    } catch (err) {
      setReservaPendente((prev) => ({
        pedidoCodigo: prev?.pedidoCodigo || pedidoCodigo,
        erros: [{ message: err.message || 'Erro ao reservar estoque' }],
        tentativas: prev?.tentativas || 0
      }));
    } finally {
      setIsReservarPedidoLoading(false);
    }
  }, [selectedOs?.os?.TB02122_CODIGO, reservaPendente?.pedidoCodigo, showGerarPedidoSuccess]);

  const handleGerarPedidoClick = useCallback(async (list) => {
    const codFechamento = selectedOs?.os?.TB02122_CODIGO;
    const numeroOS = selectedOs?.os?.numeroOS;
    if (codFechamento == null || !numeroOS) return;
    const isDisp = list === 'disponiveis';
    const indices = isDisp ? Array.from(gerarPedidoSelectedDisponiveis) : Array.from(gerarPedidoSelectedIndisponiveis);
    const items = isDisp ? gerarPedidoDisponiveis : gerarPedidoIndisponiveis;
    if (indices.length === 0) return;
    const produtos = indices.map((idx) => {
      const item = items[idx];
      const codigo = item?.peca?.codigo;
      const qtde = item?.peca?.qtde != null ? (parseInt(item.peca.qtde, 10) || 1) : 1;
      return codigo ? { codigo, qtde } : null;
    }).filter(Boolean);
    if (produtos.length === 0) return;
    const token = localStorage.getItem('authToken');
    if (!token) return;
    setGerarPedidoError(null);
    setGerarPedidoSuccessMsg(null);
    setReservaPendente(null);
    setIsGerarPedidoLoading(true);
    try {
      const result = await fetchGerarPedido(token, { numeroOS, codFechamento, list, produtos });
      if (result.success) {
        const numeroPedidoUnico = result.pedidoGerado || `P-${Date.now()}`;
        setPedidosGeradosPorPeca((prev) => {
          const next = { ...prev };
          indices.forEach((idx) => {
            const item = items[idx];
            if (item?.peca?.codigo) next[`${codFechamento}-${item.peca.codigo}`] = numeroPedidoUnico;
          });
          return next;
        });
        if (isDisp) setGerarPedidoSelectedDisponiveis(new Set());
        else setGerarPedidoSelectedIndisponiveis(new Set());
        applyReservaFromGerarResult(result);
      } else {
        setGerarPedidoError(result.error || { message: 'Erro ao gerar pedido' });
      }
    } catch (err) {
      setGerarPedidoError({ message: err.message || 'Erro ao gerar pedido' });
    } finally {
      setIsGerarPedidoLoading(false);
    }
  }, [selectedOs?.os?.TB02122_CODIGO, selectedOs?.os?.numeroOS, gerarPedidoSelectedDisponiveis, gerarPedidoSelectedIndisponiveis, gerarPedidoDisponiveis, gerarPedidoIndisponiveis, applyReservaFromGerarResult]);

  const handleGerarPedidoDireto = useCallback(async () => {
    const codFechamento = selectedOs?.os?.TB02122_CODIGO;
    const numeroOS = selectedOs?.os?.numeroOS;
    const codEmp = selectedOs?.os?.TB02122_CODEMP != null ? String(selectedOs.os.TB02122_CODEMP).trim() : '';
    const stockScope = getConditionStockValidationScope(selectedOs, apiConditionsByAreaTech);
    if (codFechamento == null || !numeroOS) return;
    const semPedido = (pecasPendentes || []).filter((peca) => {
      if (!peca?.codigo) return false;
      const key = `${codFechamento}-${peca.codigo}`;
      return !(pedidosGeradosPorPeca[key] ?? peca.pedidoGerado);
    });
    if (semPedido.length === 0) return;
    const token = localStorage.getItem('authToken');
    if (!token) return;
    setPedidoMenuOpen(false);
    setGerarPedidoError(null);
    setGerarPedidoSuccessMsg(null);
    setReservaPendente(null);
    setIsGerarPedidoDiretoLoading(true);
    setGerarPedidoDiretoMessage('Verificando estoque...');
    try {
      const produtosPedido = [];
      let algumSemEstoque = false;
      for (const peca of semPedido) {
        const qtdeRequerida = parseInt(peca.qtde, 10) || 1;
        try {
          const rows = await fetchDisponibilidadeEstoque(token, peca.codigo);
          let naEmpresa = 0;
          let outrasEmpresas = 0;
          for (const r of rows || []) {
            const q = typeof r.QTDE === 'number' ? r.QTDE : parseInt(r.QTDE, 10) || 0;
            const emp = r.EMP != null ? String(r.EMP).trim() : '';
            if (emp === codEmp) naEmpresa += q;
            else outrasEmpresas += q;
          }
          const saldoValidacao = resolveStockValidationQty(naEmpresa, outrasEmpresas, stockScope);
          if (saldoValidacao < qtdeRequerida) algumSemEstoque = true;
        } catch {
          algumSemEstoque = true;
        }
        produtosPedido.push({ peca, qtde: qtdeRequerida });
      }
      const list = algumSemEstoque ? 'indisponiveis' : 'disponiveis';
      setGerarPedidoDiretoMessage('Gerando pedido...');
      const result = await fetchGerarPedido(token, {
        numeroOS,
        codFechamento,
        list,
        produtos: produtosPedido.map(({ peca, qtde }) => ({ codigo: peca.codigo, qtde }))
      });
      if (!result.success) throw new Error(result.error?.message || 'Erro ao gerar pedido');
      const pedidoGerado = result.pedidoGerado || '';
      setPedidosGeradosPorPeca((prev) => {
        const next = { ...prev };
        produtosPedido.forEach(({ peca }) => {
          next[`${codFechamento}-${peca.codigo}`] = pedidoGerado;
        });
        return next;
      });
      applyReservaFromGerarResult(result);
    } catch (err) {
      setGerarPedidoError({ message: err.message || 'Erro ao gerar pedido' });
    } finally {
      setGerarPedidoDiretoMessage('');
      setIsGerarPedidoDiretoLoading(false);
    }
  }, [selectedOs, apiConditionsByAreaTech, pecasPendentes, pedidosGeradosPorPeca, applyReservaFromGerarResult]);

  useEffect(() => {
    setReservaPendente(null);
    clearGerarPedidoSuccessTimer();
    setGerarPedidoSuccessMsg(null);
  }, [selectedOs?.os?.TB02122_CODIGO, clearGerarPedidoSuccessTimer]);

  const pecasPendentesSorted = React.useMemo(() => {
    const codFechamento = selectedOs?.os?.TB02122_CODIGO;
    const list = (pecasPendentes || []).map((peca, originalIndex) => {
      const key = codFechamento != null ? `${codFechamento}-${peca.codigo}` : null;
      const numeroPedido = key ? (pedidosGeradosPorPeca[key] ?? peca.pedidoGerado ?? '') : '';
      return { peca, originalIndex, numeroPedido };
    });
    list.sort((a, b) => {
      if (a.numeroPedido !== b.numeroPedido) return (a.numeroPedido || '\uffff').localeCompare(b.numeroPedido || '\uffff');
      return String(a.peca.codigo).localeCompare(String(b.peca.codigo));
    });
    list.forEach((row, i) => {
      const np = row.numeroPedido;
      let count = 1;
      if (np) { for (let j = i + 1; j < list.length && list[j].numeroPedido === np; j++) count++; }
      row.samePedidoCount = count;
      row.isFirstOfGroup = !np || i === 0 || list[i - 1].numeroPedido !== np;
    });
    return list;
  }, [pecasPendentes, selectedOs?.os?.TB02122_CODIGO, pedidosGeradosPorPeca]);

  const podeGerarPedido = selectedOs?.os?.TB02122_CODIGO != null && pecasPendentes.length > 0 && pecasPendentes.some((peca) => {
    const key = `${selectedOs.os.TB02122_CODIGO}-${peca.codigo}`;
    const jaTemPedido = pedidosGeradosPorPeca[key] ?? peca.pedidoGerado;
    return !jaTemPedido;
  });

  useEffect(() => {
    if (!pedidoMenuOpen) return;
    const onDocClick = (e) => {
      if (pedidoMenuRef.current && !pedidoMenuRef.current.contains(e.target)) setPedidoMenuOpen(false);
    };
    document.addEventListener('click', onDocClick);
    return () => document.removeEventListener('click', onDocClick);
  }, [pedidoMenuOpen]);

  useEffect(() => {
    if (!podeGerarPedido && pedidoMenuOpen) setPedidoMenuOpen(false);
  }, [podeGerarPedido, pedidoMenuOpen]);

  const handleConfirmarAlterarStatus = useCallback(async () => {
    const isLote = alterarStatusLoteOs && alterarStatusLoteOs.length > 0;
    if (!selectedCodStatusConclusao || alterarStatusLoading) return;
    if (!isLote && !selectedOs?.os) return;
    const token = localStorage.getItem('authToken');
    if (!token) return;
    setAlterarStatusLoading(true);
    setAlterarStatusFeedback(null);
    try {
      const base = getApiBaseUrl();
      if (isLote) {
        let allOk = true;
        for (const os of alterarStatusLoteOs) {
          const res = await fetch(`${base}/api/checkout-view/alterar-status-conclusao`, {
            method: 'POST',
            headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
            body: JSON.stringify({
              numeroOS: os.numeroOS,
              codNovoStatus: selectedCodStatusConclusao,
              codCli: os.codCli,
              codEmp: os.codEmp
            })
          });
          const data = await res.json().catch(() => ({}));
          if (!res.ok || !data.success) allOk = false;
        }
        if (allOk) {
          setAlterarStatusFeedback('success');
          setTimeout(async () => {
            setShowAlterarStatusModal(false);
            setAlterarStatusLoteOs(null);
            let data = null;
            try {
              data = await refreshCheckoutResumo();
            } catch {}
            if (data && data.osByAreaTechCondition) {
              setSelectedTechKeys((prev) => {
                const next = new Set(prev);
                for (const key of prev) {
                  const [areaId, techName] = key.split('|');
                  const condMap = data.osByAreaTechCondition[areaId]?.[techName];
                  if (!condMap) { next.delete(key); continue; }
                  let total = 0;
                  for (const condLabel of Object.keys(condMap)) total += (condMap[condLabel] || []).length;
                  if (total === 0) next.delete(key);
                }
                return next;
              });
              if (selectedOs) {
                const condMap = data.osByAreaTechCondition[selectedOs.areaId]?.[selectedOs.techName];
                const hasAny = condMap && Object.values(condMap).some((arr) => Array.isArray(arr) && arr.length > 0);
                if (!hasAny) {
                  setSelectedOs(null);
                  setPatrimonioMenuOpen(false);
                  setShowAtendimentosModal(false);
                  setExpandedConditionKey(null);
                } else {
                  const stillInList = (condMap?.[selectedOs.conditionLabel] || []).some(
                    (o) => o.numeroOS === selectedOs.os?.numeroOS && String(o.numeroSerie || '') === String(selectedOs.os?.numeroSerie || '')
                  );
                  if (!stillInList) {
                    setSelectedOs(null);
                    setPatrimonioMenuOpen(false);
                    setShowAtendimentosModal(false);
                  }
                }
              }
            }
          }, 1800);
        } else {
          setAlterarStatusFeedback('error');
          setTimeout(() => setAlterarStatusFeedback(null), 2800);
        }
      } else {
        const res = await fetch(`${base}/api/checkout-view/alterar-status-conclusao`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({
            numeroOS: selectedOs.os.numeroOS,
            codNovoStatus: selectedCodStatusConclusao,
            codCli: selectedOs.os.TB02122_CODCLI,
            codEmp: selectedOs.os.TB02122_CODEMP
          })
        });
        const data = await res.json().catch(() => ({}));
        if (res.ok && data.success) {
          setAlterarStatusFeedback('success');
          setTimeout(() => {
            setShowAlterarStatusModal(false);
            setSelectedOs(null);
            setPatrimonioMenuOpen(false);
            setShowAtendimentosModal(false);
            refreshCheckoutResumo();
          }, 1800);
        } else {
          setAlterarStatusFeedback('error');
          setTimeout(() => setAlterarStatusFeedback(null), 2800);
        }
      }
    } catch {
      setAlterarStatusFeedback('error');
      setTimeout(() => setAlterarStatusFeedback(null), 2800);
    } finally {
      setAlterarStatusLoading(false);
    }
  }, [selectedOs, selectedCodStatusConclusao, alterarStatusLoteOs, alterarStatusLoading, setSelectedOs, setPatrimonioMenuOpen, setShowAtendimentosModal, setExpandedConditionKey, refreshCheckoutResumo]);

  const closeAlterarStatusModal = useCallback(() => {
    setShowAlterarStatusModal(false);
    setSelectedOs(null);
    setPatrimonioMenuOpen(false);
    setShowAtendimentosModal(false);
    refreshCheckoutResumo();
  }, [setSelectedOs, setPatrimonioMenuOpen, setShowAtendimentosModal, refreshCheckoutResumo]);

  const startHorariosEdit = () => {
    if (!selectedOs) return;
    const os = selectedOs.os;
    setHorariosEdit({
      dataIni: osDateToYyyyMmDdInput(os.TB02122_DATAINI),
      horaIni: formatHoraHhMm(os.TB02122_HORAINI) || '00:00',
      dataFim: osDateToYyyyMmDdInput(os.TB02122_DATAFIM),
      horaFim: formatHoraHhMm(os.TB02122_HORAFIM) || '00:00'
    });
    setIsHorariosEditing(true);
    setHorariosSaveFeedback(null);
    setHorariosSaveMessage('');
  };

  const cancelHorariosEdit = () => {
    setIsHorariosEditing(false);
    setHorariosSaveFeedback(null);
    setHorariosSaveMessage('');
  };

  const confirmHorariosEdit = useCallback(async () => {
    if (!selectedOs?.os?.numeroOS || isHorariosSaving) return;
    const token = localStorage.getItem('authToken');
    if (!token) return;

    const v = validateHorariosAtendimentoClient(
      horariosEdit.dataIni,
      horariosEdit.horaIni,
      horariosEdit.dataFim,
      horariosEdit.horaFim
    );
    if (!v.ok) {
      setHorariosSaveFeedback('error');
      setHorariosSaveMessage(v.message);
      setTimeout(() => {
        setHorariosSaveFeedback(null);
        setHorariosSaveMessage('');
      }, 3500);
      return;
    }

    setIsHorariosSaving(true);
    setHorariosSaveFeedback(null);
    setHorariosSaveMessage('');
    try {
      const base = getApiBaseUrl();
      const res = await fetch(`${base}/api/checkout-view/horarios-atendimento`, {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          numeroOS: selectedOs.os.numeroOS,
          dataIni: horariosEdit.dataIni.trim(),
          horaIni: v.horaIni,
          dataFim: horariosEdit.dataFim.trim(),
          horaFim: v.horaFim
        })
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok && data.success) {
        const di = horariosEdit.dataIni.trim();
        const df = horariosEdit.dataFim.trim();
        const [yi, mi, diD] = di.split('-').map((x) => parseInt(x, 10));
        const [yf, mf, dfD] = df.split('-').map((x) => parseInt(x, 10));
        const TB02122_DATAINI = new Date(Date.UTC(yi, mi - 1, diD)).toISOString();
        const TB02122_DATAFIM = new Date(Date.UTC(yf, mf - 1, dfD)).toISOString();
        const updatedOs = {
          ...selectedOs.os,
          TB02122_DATAINI,
          TB02122_HORAINI: v.horaIni,
          TB02122_DATAFIM,
          TB02122_HORAFIM: v.horaFim
        };
        const areaId = selectedOs.areaId;
        const techName = selectedOs.techName;
        const condLabel = selectedOs.conditionLabel;
        setSelectedOs({ ...selectedOs, os: updatedOs });
        setApiOsByAreaTechCondition((prev) => {
          const next = JSON.parse(JSON.stringify(prev || {}));
          const arr = next[areaId]?.[techName]?.[condLabel];
          if (!Array.isArray(arr)) return next;
          const idx = arr.findIndex(
            (o) =>
              String(o.numeroOS) === String(updatedOs.numeroOS) &&
              (o.numeroSerie || '') === (updatedOs.numeroSerie || '')
          );
          if (idx >= 0) arr[idx] = { ...arr[idx], ...updatedOs };
          return next;
        });
        setHorariosSaveFeedback('success');
        setIsHorariosEditing(false);
        setTimeout(() => {
          setHorariosSaveFeedback(null);
          setHorariosSaveMessage('');
        }, 2200);
      } else {
        setHorariosSaveFeedback('error');
        setHorariosSaveMessage(data.message || 'Erro ao atualizar. Tente novamente.');
        setTimeout(() => {
          setHorariosSaveFeedback(null);
          setHorariosSaveMessage('');
        }, 3200);
      }
    } catch {
      setHorariosSaveFeedback('error');
      setHorariosSaveMessage('Erro ao atualizar. Tente novamente.');
      setTimeout(() => {
        setHorariosSaveFeedback(null);
        setHorariosSaveMessage('');
      }, 3200);
    } finally {
      setIsHorariosSaving(false);
    }
  }, [selectedOs, horariosEdit, isHorariosSaving]);

  const startLaudoEdit = () => {
    if (!selectedOs) return;
    setLaudoEditValue(selectedOs.os?.TB02122_OBS ?? '');
    setIsLaudoEditing(true);
    setLaudoSaveFeedback(null);
  };

  const cancelLaudoEdit = () => {
    setIsLaudoEditing(false);
    setLaudoEditValue(selectedOs?.os?.TB02122_OBS ?? '');
    setLaudoSaveFeedback(null);
  };

  const startContadoresEdit = () => {
    if (!selectedOs) return;
    setContadoresErrorMsg('');
    if (selectedOs.os?.isNative) {
      setContadoresEditValues(
        Object.fromEntries((selectedOs.os.meters || []).map((m) => [m.key, m.value != null ? String(m.value) : '']))
      );
      setIsContadoresEditing(true);
      setContadoresFeedback(null);
      return;
    }
    setContadoresEditValues({
      TB02122_CONTADOR: selectedOs.os?.TB02122_CONTADOR ?? '',
      TB02122_CONTADORC: selectedOs.os?.TB02122_CONTADORC ?? '',
      TB02122_CONTADORDG: selectedOs.os?.TB02122_CONTADORDG ?? '',
      TB02122_CONTADORGF: selectedOs.os?.TB02122_CONTADORGF ?? '',
      TB02122_CONTADORGFC: selectedOs.os?.TB02122_CONTADORGFC ?? ''
    });
    setIsContadoresEditing(true);
    setContadoresFeedback(null);
  };

  const cancelContadoresEdit = () => {
    setIsContadoresEditing(false);
    setContadoresEditValues(null);
    setContadoresFeedback(null);
  };

  const handleContadorChange = (field, value) => {
    setContadoresEditValues((prev) => ({
      ...(prev || {}),
      [field]: value
    }));
  };

  const startCondicaoEdit = () => {
    if (!selectedOs) return;
    setCondicaoEditValue(selectedOs.conditionLabel ?? '');
    setIsCondicaoEditing(true);
    setCondicaoFeedback(null);
    setCondicaoSearchResults([]);
    setTimeout(() => condicaoInputRef.current?.focus(), 0);
  };

  const cancelCondicaoEdit = () => {
    setIsCondicaoEditing(false);
    setCondicaoEditValue(selectedOs?.conditionLabel ?? '');
    setCondicaoFeedback(null);
    setCondicaoSearchResults([]);
    if (condicaoSearchTimeoutRef.current) clearTimeout(condicaoSearchTimeoutRef.current);
  };

  useEffect(() => {
    if (!isCondicaoEditing || !condicaoEditValue || String(condicaoEditValue).trim().length < 4) {
      setCondicaoSearchResults([]);
      return;
    }
    if (condicaoSearchTimeoutRef.current) clearTimeout(condicaoSearchTimeoutRef.current);
    condicaoSearchTimeoutRef.current = setTimeout(async () => {
      const token = localStorage.getItem('authToken');
      if (!token) return;
      setCondicaoSearchLoading(true);
      try {
        const rows = await fetchCondicoesSearch(token, condicaoEditValue.trim(), selectedOs?.os?.TB02122_CODIGO);
        setCondicaoSearchResults(rows || []);
      } catch {
        setCondicaoSearchResults([]);
      } finally {
        setCondicaoSearchLoading(false);
      }
    }, 280);
    return () => { if (condicaoSearchTimeoutRef.current) clearTimeout(condicaoSearchTimeoutRef.current); };
  }, [isCondicaoEditing, condicaoEditValue]);

  useEffect(() => {
    if (!pedidoEditModal.open || pedidoEditModal.loading) return;
    const q = String(pedidoEditModal.transpInput || '').trim();
    if (q.length < 4) {
      setPedidoEditModal((prev) => ({ ...prev, transpResults: [], transpSearchLoading: false }));
      return;
    }
    if (!pedidoTranspTouched && q === String(pedidoEditModal.initialTranspNome || '').trim()) {
      setPedidoEditModal((prev) => ({ ...prev, transpResults: [], transpSearchLoading: false }));
      return;
    }
    if (pedidoEditModal.transpPickNome != null && q === String(pedidoEditModal.transpPickNome).trim()) {
      setPedidoEditModal((prev) => ({ ...prev, transpResults: [], transpSearchLoading: false }));
      return;
    }
    if (pedidoTranspSearchTimeoutRef.current) clearTimeout(pedidoTranspSearchTimeoutRef.current);
    pedidoTranspSearchTimeoutRef.current = setTimeout(async () => {
      const token = localStorage.getItem('authToken');
      if (!token) return;
      const runGen = ++pedidoTranspSearchGenRef.current;
      setPedidoEditModal((prev) => ({ ...prev, transpSearchLoading: true }));
      try {
        const rows = await fetchTransportadorasPedido(token, q);
        if (runGen !== pedidoTranspSearchGenRef.current) return;
        setPedidoEditModal((prev) => ({ ...prev, transpResults: rows || [], transpSearchLoading: false }));
      } catch {
        if (runGen !== pedidoTranspSearchGenRef.current) return;
        setPedidoEditModal((prev) => ({ ...prev, transpResults: [], transpSearchLoading: false }));
      }
    }, 280);
    return () => {
      if (pedidoTranspSearchTimeoutRef.current) clearTimeout(pedidoTranspSearchTimeoutRef.current);
    };
  }, [
    pedidoEditModal.open,
    pedidoEditModal.loading,
    pedidoEditModal.transpInput,
    pedidoEditModal.initialTranspNome,
    pedidoEditModal.transpPickNome,
    pedidoTranspTouched
  ]);

  useEffect(() => {
    if (!pedidoEditModal.open || pedidoEditModal.loading) return;
    const q = String(pedidoEditModal.classifInput || '').trim();
    if (q.length < 4) {
      setPedidoEditModal((prev) => ({ ...prev, classifResults: [], classifSearchLoading: false }));
      return;
    }
    if (!pedidoClassifTouched && q === String(pedidoEditModal.initialClassifNome || '').trim()) {
      setPedidoEditModal((prev) => ({ ...prev, classifResults: [], classifSearchLoading: false }));
      return;
    }
    if (pedidoEditModal.classifPickNome != null && q === String(pedidoEditModal.classifPickNome).trim()) {
      setPedidoEditModal((prev) => ({ ...prev, classifResults: [], classifSearchLoading: false }));
      return;
    }
    if (pedidoClassifSearchTimeoutRef.current) clearTimeout(pedidoClassifSearchTimeoutRef.current);
    pedidoClassifSearchTimeoutRef.current = setTimeout(async () => {
      const token = localStorage.getItem('authToken');
      if (!token) return;
      const runGen = ++pedidoClassifSearchGenRef.current;
      setPedidoEditModal((prev) => ({ ...prev, classifSearchLoading: true }));
      try {
        const rows = await fetchClassificacoesPedido(token, q);
        if (runGen !== pedidoClassifSearchGenRef.current) return;
        setPedidoEditModal((prev) => ({ ...prev, classifResults: rows || [], classifSearchLoading: false }));
      } catch {
        if (runGen !== pedidoClassifSearchGenRef.current) return;
        setPedidoEditModal((prev) => ({ ...prev, classifResults: [], classifSearchLoading: false }));
      }
    }, 280);
    return () => {
      if (pedidoClassifSearchTimeoutRef.current) clearTimeout(pedidoClassifSearchTimeoutRef.current);
    };
  }, [
    pedidoEditModal.open,
    pedidoEditModal.loading,
    pedidoEditModal.classifInput,
    pedidoEditModal.initialClassifNome,
    pedidoEditModal.classifPickNome,
    pedidoClassifTouched
  ]);

  const closePedidoEdit = useCallback(() => {
    if (pedidoTranspSearchTimeoutRef.current) clearTimeout(pedidoTranspSearchTimeoutRef.current);
    if (pedidoClassifSearchTimeoutRef.current) clearTimeout(pedidoClassifSearchTimeoutRef.current);
    pedidoTranspSearchGenRef.current += 1;
    pedidoClassifSearchGenRef.current += 1;
    setPedidoTranspTouched(false);
    setPedidoClassifTouched(false);
    setPedidoEditModal(emptyPedidoEditModal());
  }, []);

  const openPedidoEdit = useCallback(
    async (codPedido) => {
      const numeroOS = selectedOs?.os?.numeroOS;
      if (!numeroOS || !codPedido) return;
      const token = localStorage.getItem('authToken');
      if (!token) return;
      setPedidoTranspTouched(false);
      setPedidoClassifTouched(false);
      pedidoTranspSearchGenRef.current += 1;
      pedidoClassifSearchGenRef.current += 1;
      setPedidoEditModal({
        ...emptyPedidoEditModal(),
        open: true,
        codPedido: String(codPedido),
        numeroOS: String(numeroOS),
        loading: true
      });
      try {
        const data = await fetchPedidoFechamentoDados(token, numeroOS, codPedido);
        setPedidoEditModal((prev) => ({
          ...prev,
          loading: false,
          loadError: null,
          solicitante: data.solicitante || '',
          foneDisplay: formatPhoneBrDisplay(data.fone || ''),
          email: data.email || '',
          transpInput: data.transportadoraNome || '',
          classifInput: data.classificacaoNome || '',
          obsNfe: data.obsNfeSugerida || '',
          observacao: data.observacao == null ? '' : String(data.observacao),
          obsInt: data.obsInterna || '',
          initialTranspNome: data.transportadoraNome || '',
          initialTranspCodigo: data.codigoTransportadora || '',
          initialClassifNome: data.classificacaoNome || '',
          initialClassifCodigo: data.codigoClassificacao || '',
          transpPickNome: null,
          transpPickCodigo: null,
          classifPickNome: null,
          classifPickCodigo: null,
          transpResults: [],
          classifResults: []
        }));
      } catch (e) {
        setPedidoEditModal((prev) => ({
          ...prev,
          loading: false,
          loadError: e.message || 'Erro ao carregar'
        }));
      }
    },
    [selectedOs]
  );

  const handlePedidoEditConfirm = useCallback(async () => {
    if (pedidoEditModal.saving || !pedidoEditModal.open) return;
    const token = localStorage.getItem('authToken');
    if (!token) return;
    const snap = pedidoEditModal;
    if (!String(snap.solicitante || '').trim()) {
      setPedidoEditModal((prev) => ({ ...prev, saveError: 'Informe o solicitante.', saveSuccess: false }));
      return;
    }
    const foneDig = phoneDigitsOnly(
      pedidoEditFoneInputRef.current?.value != null ? pedidoEditFoneInputRef.current.value : snap.foneDisplay
    );
    if (foneDig.length > 0 && foneDig.length !== 10 && foneDig.length !== 11) {
      setPedidoEditModal((prev) => ({ ...prev, saveError: 'Informe telefone com 10 ou 11 dígitos.', saveSuccess: false }));
      return;
    }
    const emailTrim = String(snap.email || '').trim();
    if (emailTrim !== '' && !isValidEmailPedido(emailTrim)) {
      setPedidoEditModal((prev) => ({ ...prev, saveError: 'E-mail inválido.', saveSuccess: false }));
      return;
    }
    const ti = snap.transpInput.trim();
    const itn = (snap.initialTranspNome || '').trim();
    const itc = (snap.initialTranspCodigo || '').trim();
    const tp = snap.transpPickNome;
    const tpc = snap.transpPickCodigo;
    let codigoTransportadora;
    if (ti === itn) codigoTransportadora = itc;
    else if (tp != null && ti === String(tp).trim()) codigoTransportadora = tpc != null ? String(tpc).trim() : '';
    else if (ti === '' && itn === '') {
      setPedidoEditModal((prev) => ({
        ...prev,
        saveError: 'Informe e selecione uma transportadora na lista.',
        saveSuccess: false
      }));
      return;
    } else {
      setPedidoEditModal((prev) => ({
        ...prev,
        saveError: 'Selecione uma transportadora na lista.',
        saveSuccess: false
      }));
      return;
    }
    if (!String(codigoTransportadora || '').trim()) {
      setPedidoEditModal((prev) => ({
        ...prev,
        saveError: 'Transportadora é obrigatória.',
        saveSuccess: false
      }));
      return;
    }
    const ci = snap.classifInput.trim();
    const icn = (snap.initialClassifNome || '').trim();
    const icc = (snap.initialClassifCodigo || '').trim();
    const cp = snap.classifPickNome;
    const cpc = snap.classifPickCodigo;
    let codigoClassif;
    if (ci === icn) codigoClassif = icc;
    else if (cp != null && ci === String(cp).trim()) codigoClassif = cpc != null ? String(cpc).trim() : '';
    else if (ci === '' && icn === '') {
      setPedidoEditModal((prev) => ({
        ...prev,
        saveError: 'Informe e selecione uma classificação na lista.',
        saveSuccess: false
      }));
      return;
    } else {
      setPedidoEditModal((prev) => ({
        ...prev,
        saveError: 'Selecione uma classificação na lista.',
        saveSuccess: false
      }));
      return;
    }
    if (!String(codigoClassif || '').trim()) {
      setPedidoEditModal((prev) => ({
        ...prev,
        saveError: 'Classificação é obrigatória.',
        saveSuccess: false
      }));
      return;
    }
    setPedidoEditModal((prev) => ({ ...prev, saving: true, saveError: null, saveSuccess: false }));
    try {
      await updatePedidoFechamentoApi(token, {
        numeroOS: snap.numeroOS,
        codPedido: snap.codPedido,
        solicitante: snap.solicitante,
        fone: foneDig,
        email: emailTrim,
        codigoTransportadora,
        codigoClassif,
        textoObsNFe: snap.obsNfe,
        textoObservacao: snap.observacao,
        textoObsInterno: snap.obsInt
      });
      setPedidoEditModal((prev) => ({ ...prev, saving: false, saveSuccess: true, saveError: null }));
      setTimeout(() => {
        closePedidoEdit();
      }, 1400);
    } catch (e) {
      setPedidoEditModal((prev) => ({
        ...prev,
        saving: false,
        saveError: e.message || 'Erro ao salvar',
        saveSuccess: false
      }));
    }
  }, [pedidoEditModal, closePedidoEdit]);

  const handleSelectCondicao = useCallback(async (item) => {
    if (!selectedOs?.os?.TB02122_CODIGO || isCondicaoSaving) return;
    const token = localStorage.getItem('authToken');
    if (!token) return;
    const oldConditionLabel = selectedOs.conditionLabel;
    const areaId = selectedOs.areaId;
    const techName = selectedOs.techName;
    const osItem = selectedOs.os;
    setIsCondicaoSaving(true);
    setCondicaoFeedback(null);
    try {
      const data = await updateCondicao(token, selectedOs.os.TB02122_CODIGO, item.codigo);
      setSelectedOs((prev) => (prev ? { ...prev, conditionLabel: data.conditionLabel } : null));
      setSelectedOsCondFuncClose(data.func_close);
      setCondicaoFeedback('success');
      setIsCondicaoEditing(false);
      setCondicaoSearchResults([]);
      setCondicaoEditValue(data.conditionLabel);
      setApiOsByAreaTechCondition((prev) => {
        const next = JSON.parse(JSON.stringify(prev || {}));
        if (!next[areaId]?.[techName]) return next;
        if (next[areaId][techName][oldConditionLabel]) {
          next[areaId][techName][oldConditionLabel] = next[areaId][techName][oldConditionLabel].filter(
            (o) => o.numeroOS !== osItem.numeroOS || (o.numeroSerie || '') !== (osItem.numeroSerie || '')
          );
          if (next[areaId][techName][oldConditionLabel].length === 0) delete next[areaId][techName][oldConditionLabel];
        }
        if (!next[areaId][techName][data.conditionLabel]) next[areaId][techName][data.conditionLabel] = [];
        next[areaId][techName][data.conditionLabel].push(osItem);
        return next;
      });
      setApiConditionsByAreaTech((prev) => {
        const next = JSON.parse(JSON.stringify(prev || {}));
        if (!next[areaId]) next[areaId] = {};
        if (!next[areaId][techName]) next[areaId][techName] = [];
        const techConds = next[areaId][techName];
        const oldIdx = techConds.findIndex((c) => c.label === oldConditionLabel);
        if (oldIdx >= 0) {
          techConds[oldIdx] = { ...techConds[oldIdx], count: Math.max(0, (techConds[oldIdx].count || 1) - 1) };
          if (techConds[oldIdx].count === 0) techConds.splice(oldIdx, 1);
        }
        const newIdx = techConds.findIndex((c) => c.label === data.conditionLabel);
        if (newIdx >= 0) {
          techConds[newIdx] = { ...techConds[newIdx], count: (techConds[newIdx].count || 0) + 1 };
        } else {
          techConds.push({ label: data.conditionLabel, count: 1, func_close: data.func_close });
        }
        return next;
      });
      setTimeout(() => setCondicaoFeedback(null), 2200);
    } catch {
      setCondicaoFeedback('error');
      setTimeout(() => setCondicaoFeedback(null), 2200);
    } finally {
      setIsCondicaoSaving(false);
    }
  }, [selectedOs, isCondicaoSaving]);

  const confirmLaudoEdit = useCallback(async () => {
    if (!selectedOs?.os?.numeroOS || isLaudoSaving) return;
    const token = localStorage.getItem('authToken');
    if (!token) return;
    setIsLaudoSaving(true);
    setLaudoSaveFeedback(null);
    try {
      const base = getApiBaseUrl();
      const res = await fetch(`${base}/api/checkout-view/laudo`, {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ numeroOS: selectedOs.os.numeroOS, textoEditado: laudoEditValue })
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok && data.success) {
        setSelectedOs({ ...selectedOs, os: { ...selectedOs.os, TB02122_OBS: laudoEditValue } });
        setLaudoSaveFeedback('success');
        setIsLaudoEditing(false);
        setTimeout(() => setLaudoSaveFeedback(null), 2200);
      } else {
        setLaudoSaveFeedback('error');
        setTimeout(() => setLaudoSaveFeedback(null), 2200);
      }
    } catch {
      setLaudoSaveFeedback('error');
      setTimeout(() => setLaudoSaveFeedback(null), 2200);
    } finally {
      setIsLaudoSaving(false);
    }
  }, [selectedOs, laudoEditValue, isLaudoSaving]);

  const confirmContadoresEdit = useCallback(async () => {
    if (!selectedOs?.os?.numeroOS || !contadoresEditValues || isContadoresSaving) return;
    const token = localStorage.getItem('authToken');
    if (!token) return;

    const numeroOS = selectedOs.os.numeroOS;
    if (selectedOs.os.isNative) {
      const meters = {};
      (selectedOs.os.meters || []).forEach((m) => {
        const raw = contadoresEditValues[m.key];
        if (raw == null || String(raw).trim() === '') return;
        const n = Number(raw);
        if (Number.isNaN(n) || n === m.value) return;
        meters[m.key] = n;
      });
      if (!Object.keys(meters).length) {
        setIsContadoresEditing(false);
        setContadoresFeedback(null);
        return;
      }
      setIsContadoresSaving(true);
      setContadoresFeedback(null);
      setContadoresErrorMsg('');
      try {
        const res = await fetch(`${getApiBaseUrl()}/api/checkout-view/contadores`, {
          method: 'PATCH',
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({ numeroOS, meters })
        });
        const data = await res.json().catch(() => ({}));
        if (res.ok && data.success) {
          const updatedMeters = (selectedOs.os.meters || []).map((m) =>
            Object.prototype.hasOwnProperty.call(meters, m.key) ? { ...m, value: meters[m.key] } : m
          );
          setSelectedOs({ ...selectedOs, os: { ...selectedOs.os, meters: updatedMeters, equipmentNotStarting: false } });
          setContadoresFeedback('success');
          setIsContadoresEditing(false);
          setTimeout(() => setContadoresFeedback(null), 2200);
        } else {
          setContadoresErrorMsg(res.status === 400 && data.message ? data.message : '');
          setContadoresFeedback('error');
          setTimeout(() => setContadoresFeedback(null), 4000);
        }
      } catch {
        setContadoresFeedback('error');
        setTimeout(() => setContadoresFeedback(null), 2200);
      } finally {
        setIsContadoresSaving(false);
      }
      return;
    }
    const fieldsMap = [
      { field: 'TB02122_CONTADOR', payloadKey: 'contadorA4PB' },
      { field: 'TB02122_CONTADORC', payloadKey: 'contadorA4Cor' },
      { field: 'TB02122_CONTADORDG', payloadKey: 'contadorDG' },
      { field: 'TB02122_CONTADORGF', payloadKey: 'contadorA3PB' },
      { field: 'TB02122_CONTADORGFC', payloadKey: 'contadorA3Cor' }
    ];

    const body = { numeroOS };
    let hasChange = false;

    fieldsMap.forEach(({ field, payloadKey }) => {
      const raw = contadoresEditValues[field];
      if (raw == null || raw === '') return;
      const n = typeof raw === 'number' ? raw : parseInt(String(raw).trim(), 10);
      if (Number.isNaN(n)) return;
      const currentVal =
        selectedOs.os[field] != null ? parseInt(String(selectedOs.os[field]).trim(), 10) : null;
      if (currentVal === n) return;
      body[payloadKey] = n;
      hasChange = true;
    });

    if (!hasChange) {
      setIsContadoresEditing(false);
      setContadoresFeedback(null);
      return;
    }

    setIsContadoresSaving(true);
    setContadoresFeedback(null);
    try {
      const base = getApiBaseUrl();
      const res = await fetch(`${base}/api/checkout-view/contadores`, {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok && data.success) {
        const updatedOs = { ...selectedOs.os };
        fieldsMap.forEach(({ field, payloadKey }) => {
          if (Object.prototype.hasOwnProperty.call(body, payloadKey)) {
            updatedOs[field] = body[payloadKey];
          }
        });
        setSelectedOs({ ...selectedOs, os: updatedOs });
        setContadoresFeedback('success');
        setIsContadoresEditing(false);
        setTimeout(() => setContadoresFeedback(null), 2200);
      } else {
        setContadoresFeedback('error');
        setTimeout(() => setContadoresFeedback(null), 2200);
      }
    } catch {
      setContadoresFeedback('error');
      setTimeout(() => setContadoresFeedback(null), 2200);
    } finally {
      setIsContadoresSaving(false);
    }
  }, [selectedOs, contadoresEditValues, isContadoresSaving, setSelectedOs]);

  const handleExcluirPeca = useCallback(async (index) => {
    if (!selectedOs?.os?.TB02122_CODIGO || excluirPecaLoadingIndex != null) return;
    const peca = pecasPendentes[index];
    if (!peca?.codigo) return;
    const token = localStorage.getItem('authToken');
    if (!token) return;
    setExcluirPecaLoadingIndex(index);
    setExcluirPecaFeedback(null);
    try {
      await deletePecaPendente(token, selectedOs.os.TB02122_CODIGO, peca.codigo);
      const data = await fetchPecasPendentes(token, selectedOs.os.TB02122_CODIGO);
      setPecasPendentes(data || []);
      if (selectedOs?.os?.TB02122_CODIGO != null) {
        const codFech = selectedOs.os.TB02122_CODIGO;
        setPedidosGeradosPorPeca((prev) => {
          const next = { ...prev };
          Object.keys(next).forEach((k) => { if (String(k).startsWith(`${codFech}-`)) delete next[k]; });
          (data || []).forEach((p) => {
            if (p.pedidoGerado != null && p.pedidoGerado !== '') next[`${codFech}-${p.codigo}`] = String(p.pedidoGerado);
          });
          return next;
        });
      }
      setExpandedDisponibilidadeIndex(null);
      setExpandedCompatibilidadeIndex(null);
      setExcluirPecaFeedback('success');
      setTimeout(() => setExcluirPecaFeedback(null), 2200);
    } catch {
      setExcluirPecaFeedback('error');
      setTimeout(() => setExcluirPecaFeedback(null), 2200);
    } finally {
      setExcluirPecaLoadingIndex(null);
    }
  }, [selectedOs?.os?.TB02122_CODIGO, pecasPendentes, excluirPecaLoadingIndex]);

  useEffect(() => {
    if (!isOpen) return;
    setExpandedAreaIds(new Set());
    setSelectedTechKeys(new Set());
    setSelectedOs(null);
    setExpandedConditionKey(null);
    setSelectedAreaIds([]);
    const token = localStorage.getItem('authToken');
    if (!token) {
      setApiAreas([]);
      setApiTechniciansByArea({});
      return;
    }
    let cancelled = false;
    setIsLoadingCheckout(true);
    fetchCheckoutResumo(token)
      .then((data) => {
        if (!cancelled && data) {
          const arr = data.areas || [];
          const techs = data.techniciansByArea || {};
          const conds = data.conditionsByAreaTech || {};
          const osByCond = data.osByAreaTechCondition || {};
          setApiAreas(arr);
          setApiTechniciansByArea(techs);
          setApiConditionsByAreaTech(conds);
          setApiOsByAreaTechCondition(osByCond);
          setSelectedAreaIds(arr.length > 0 ? arr.map((a) => a.id) : []);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setApiAreas([]);
          setApiTechniciansByArea({});
          setApiConditionsByAreaTech({});
          setApiOsByAreaTechCondition({});
          setSelectedAreaIds([]);
        }
      })
      .finally(() => {
        if (!cancelled) setIsLoadingCheckout(false);
      });
    return () => { cancelled = true; };
  }, [isOpen]);

  useEffect(() => {
    const codigo = selectedOs?.os?.TB02122_CODIGO;
    if (!codigo) {
      setPecasPendentes([]);
      return;
    }
    const token = localStorage.getItem('authToken');
    if (!token) return;
    let cancelled = false;
    fetchPecasPendentes(token, codigo)
      .then((data) => {
        if (!cancelled) {
          setPecasPendentes(data || []);
          if (codigo != null) {
            setPedidosGeradosPorPeca((prev) => {
              const next = { ...prev };
              Object.keys(next).forEach((k) => { if (String(k).startsWith(`${codigo}-`)) delete next[k]; });
              (data || []).forEach((p) => {
                if (p.pedidoGerado != null && p.pedidoGerado !== '') next[`${codigo}-${p.codigo}`] = String(p.pedidoGerado);
              });
              return next;
            });
          }
        }
      })
      .catch(() => { if (!cancelled) setPecasPendentes([]); });
    return () => { cancelled = true; };
  }, [selectedOs?.os?.TB02122_CODIGO]);

  useEffect(() => {
    const codigo = selectedOs?.os?.TB02122_CODIGO;
    if (!codigo) {
      setPecasTrocadas([]);
      return;
    }
    const token = localStorage.getItem('authToken');
    if (!token) return;
    let cancelled = false;
    fetchPecasTrocadas(token, codigo)
      .then((data) => { if (!cancelled) setPecasTrocadas(data || []); })
      .catch(() => { if (!cancelled) setPecasTrocadas([]); });
    return () => { cancelled = true; };
  }, [selectedOs?.os?.TB02122_CODIGO]);

  const areas = isLoadingCheckout ? [] : (Array.isArray(apiAreas) ? apiAreas : []);
  const techniciansByArea = isLoadingCheckout ? {} : (apiTechniciansByArea && Object.keys(apiTechniciansByArea).length ? apiTechniciansByArea : {});
  const conditionsByAreaTech = isLoadingCheckout ? null : (apiConditionsByAreaTech && Object.keys(apiConditionsByAreaTech).length ? apiConditionsByAreaTech : null);
  const osByAreaTechCondition = isLoadingCheckout ? null : (apiOsByAreaTechCondition && Object.keys(apiOsByAreaTechCondition).length ? apiOsByAreaTechCondition : null);

  useEffect(() => {
    if (!expandedConditionKey || !osByAreaTechCondition) return undefined;
    const parts = expandedConditionKey.split('|');
    if (parts.length < 3) return undefined;
    const areaId = parts[0];
    const techName = parts[1];
    const condLabel = parts.slice(2).join('|');
    const list = getOsList(areaId, techName, condLabel, osByAreaTechCondition) || [];
    const pendingSerials = [...new Set(
      list
        .filter((o) => o.isAdhoc)
        .map(getAdhocOsSerialKey)
        .filter(Boolean)
        .filter((s) => !adhocEnrichFetchedRef.current.has(s))
    )];
    if (!pendingSerials.length) return undefined;

    pendingSerials.forEach((s) => adhocEnrichFetchedRef.current.add(s));
    const token = localStorage.getItem('authToken');
    if (!token) return undefined;

    let cancelled = false;
    fetchAdhocOsEnrichBySerial(token, pendingSerials)
      .then((data) => {
        if (cancelled || !data || typeof data !== 'object') return;
        setAdhocOsEnrichBySerial((prev) => ({ ...prev, ...data }));
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [expandedConditionKey, osByAreaTechCondition]);

  const conditionFilterOptions = (() => {
    if (!conditionsByAreaTech || !osByAreaTechCondition || !selectedTechKeys || selectedTechKeys.size === 0) return [];
    const map = new Map();
    for (const key of selectedTechKeys) {
      const [areaId, techName] = key.split('|');
      const conds = getConditions(areaId, techName, conditionsByAreaTech);
      for (const c of conds) {
        const osList = filterOsListByTipo(
          getOsList(areaId, techName, c.label, osByAreaTechCondition) || [],
          tipoOsFilterSelected
        );
        if (!osList.length) continue;
        const existing = map.get(c.label);
        if (existing) {
          existing.count += osList.length;
        } else {
          map.set(c.label, { label: c.label, count: osList.length });
        }
      }
    }
    return Array.from(map.values()).sort((a, b) => {
      if (b.count !== a.count) return b.count - a.count;
      return a.label.localeCompare(b.label, 'pt-BR');
    });
  })();

  const tipoOsFilterOptions = (() => {
    if (!conditionsByAreaTech || !osByAreaTechCondition || !selectedTechKeys || selectedTechKeys.size === 0) return [];
    const map = new Map();
    for (const key of selectedTechKeys) {
      const [areaId, techName] = key.split('|');
      const conds = getConditions(areaId, techName, conditionsByAreaTech);
      for (const c of conds) {
        if (conditionFilterSelected.size > 0 && !conditionFilterSelected.has(c.label)) continue;
        const osList = getOsList(areaId, techName, c.label, osByAreaTechCondition) || [];
        for (const os of osList) {
          const tipo = getCheckoutOsTipoLetter(os);
          const existing = map.get(tipo);
          if (existing) existing.count += 1;
          else map.set(tipo, { tipo, label: TIPO_OS_NAMES[tipo] || tipo, count: 1 });
        }
      }
    }
    return Array.from(map.values()).sort((a, b) => {
      if (b.count !== a.count) return b.count - a.count;
      return a.label.localeCompare(b.label, 'pt-BR');
    });
  })();

  const temCondicaoFuncOrderOp = selectedOs && conditionsByAreaTech && !!getConditions(selectedOs.areaId, selectedOs.techName, conditionsByAreaTech).find((c) => c.label === selectedOs.conditionLabel && c.func_order_op != null);
  const concluirDisabledPorPecasSemPedido = temCondicaoFuncOrderOp && podeGerarPedido;

  const closeTechSearch = () => {
    setTechSearchOpen(false);
    setTechSearchQuery('');
  };

  const closeDetailSearch = () => {
    setDetailSearchOpen(false);
    setDetailSearchQuery('');
  };

  const runDetailSearch = () => {
    const q = detailSearchQuery.trim().toLowerCase();
    if (!q || !osByAreaTechCondition) return;
    for (const key of selectedTechKeys) {
      const [areaId, techName] = key.split('|');
      const conditions = getConditions(areaId, techName, conditionsByAreaTech);
      for (const c of conditions) {
        const osList = getOsList(areaId, techName, c.label, osByAreaTechCondition) || [];
        const os = osList.find(
          (o) =>
            String(o.patrimonio || '').toLowerCase().includes(q) ||
            String(o.numeroSerie || '').toLowerCase().includes(q) ||
            String(o.numeroOS || '').toLowerCase().includes(q)
        );
        if (os) {
          setExpandedConditionKey(conditionKey(areaId, techName, c.label));
          setSelectedOs({ areaId, techName, conditionLabel: c.label, os });
          setDetailSearchOpen(false);
          setDetailSearchQuery('');
          return;
        }
      }
    }
  };

  const matchesTechSearch = (name) => {
    if (!techSearchQuery.trim()) return true;
    return titleCase(name).toLowerCase().includes(techSearchQuery.trim().toLowerCase());
  };

  const isOsSelected = (areaId, techName, condLabel, os) =>
    selectedOs &&
    selectedOs.areaId === areaId &&
    selectedOs.techName === techName &&
    selectedOs.conditionLabel === condLabel &&
    selectedOs.os.numeroOS === os.numeroOS;

  const handleOsRowClick = (areaId, techName, condLabel, os) => {
    if (os?.isAdhoc) {
      const serial = getAdhocOsSerialKey(os);
      setAdhocOsCreateFlow(null);
      setAdhocEquipSearch({
        open: true,
        serial: serial || '',
        os,
        areaId,
        techName,
        condLabel,
      });
      return;
    }
    const alreadySelected = isOsSelected(areaId, techName, condLabel, os);
    setSelectedOs(alreadySelected ? null : { areaId, techName, conditionLabel: condLabel, os });
  };
  const [contextMenu, setContextMenu] = useState(null); // { x, y, areaId, isSelected }
  const contextMenuRef = useRef(null);

  const toggleConditionExpand = (areaId, techName, condLabel) =>
    setExpandedConditionKey((prev) => {
      const key = conditionKey(areaId, techName, condLabel);
      return prev === key ? null : key;
    });

  const handleConditionDragStart = useCallback((e, areaId, techName, conditionLabel) => {
    conditionDragJustEndedRef.current = false;
    setDragConditionPayload({ areaId, techName, conditionLabel });
    setIsDraggingCondition(true);
    e.dataTransfer.setData('text/plain', conditionKey(areaId, techName, conditionLabel));
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setDragImage(e.currentTarget, 0, 0);
  }, []);

  const handleConditionDragEnd = useCallback(() => {
    setIsDraggingCondition(false);
    setIsDragOverDropZone(false);
    setDragConditionPayload(null);
    conditionDragJustEndedRef.current = true;
    setTimeout(() => { conditionDragJustEndedRef.current = false; }, 120);
  }, []);

  const handleDropZoneDragOver = useCallback((e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setIsDragOverDropZone(true);
  }, []);

  const handleDropZoneDragLeave = useCallback((e) => {
    if (!e.currentTarget.contains(e.relatedTarget)) setIsDragOverDropZone(false);
  }, []);

  const handleDropZoneDrop = useCallback(async (e) => {
    e.preventDefault();
    setIsDragOverDropZone(false);
    const payload = dragConditionPayload;
    setDragConditionPayload(null);
    setIsDraggingCondition(false);
    conditionDragJustEndedRef.current = true;
    setTimeout(() => { conditionDragJustEndedRef.current = false; }, 120);
    if (!payload) return;
    const token = localStorage.getItem('authToken');
    if (!token) return;
    const osList = getOsList(payload.areaId, payload.techName, payload.conditionLabel, osByAreaTechCondition) || [];
    const toConcluir = osList.filter((os) => os != null && os.TB02122_CODIGO != null && os.numeroOS);
    if (toConcluir.length === 0) return;
    setIsConcluirLoteLoading(true);
    setConcluirLoteProgress({ current: 0, total: toConcluir.length });
    setConcluirLoteFeedback(null);
    const base = getApiBaseUrl();
    let hasError = false;
    for (let i = 0; i < toConcluir.length; i++) {
      setConcluirLoteProgress({ current: i + 1, total: toConcluir.length });
      const os = toConcluir[i];
      try {
        const res = await fetch(`${base}/api/checkout-view/concluir`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({
            codFechamento: os.TB02122_CODIGO,
            numeroOS: os.numeroOS,
            numeroSerie: os.numeroSerie || '',
            codCli: os.TB02122_CODCLI,
            codEmp: os.TB02122_CODEMP
          })
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok || !data.success) hasError = true;
      } catch {
        hasError = true;
      }
    }
    setConcluirLoteProgress(null);
    setIsConcluirLoteLoading(false);
    setConcluirLoteFeedback(hasError ? 'error' : 'success');
    if (!hasError && toConcluir.length > 0) {
      const cods = toConcluir.map((os) => os.TB02122_CODIGO).filter((c) => c != null);
      if (cods.length > 0) {
        (async () => {
          try {
            const t = localStorage.getItem('authToken');
            if (t) {
              const r = await sendOSConcluida(t, cods);
              console.log('[Email OS concluída]', r.success ? r.data : r.message);
            }
          } catch (e) {
            console.warn('[Email OS concluída]', e.message);
          }
        })();
      }
      let codstatus = '00';
      try {
        const configRes = await fetch(`${base}/api/config/troca-status-conclusao`, {
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }
        });
        const configData = await configRes.json().catch(() => ({}));
        codstatus = (configData.success && configData.data && configData.data.codstatus) ? String(configData.data.codstatus) : '00';
      } catch (_) {}
      if (codstatus === '01') {
        setAlterarStatusLoteOs(toConcluir.map((os) => ({
          numeroOS: os.numeroOS,
          codCli: os.TB02122_CODCLI,
          codEmp: os.TB02122_CODEMP
        })));
        setShowAlterarStatusModal(true);
        setSelectedCodStatusConclusao('');
        setOpcoesStatusConclusao([]);
        setAlterarStatusFeedback(null);
        setTimeout(() => setConcluirLoteFeedback(null), 1500);
        return;
      }
    }
    let data = null;
    try {
      data = await refreshCheckoutResumo();
    } catch {}
    if (data && data.osByAreaTechCondition) {
      setSelectedTechKeys((prev) => {
        const next = new Set(prev);
        for (const key of prev) {
          const [areaId, techName] = key.split('|');
          const condMap = data.osByAreaTechCondition[areaId]?.[techName];
          if (!condMap) {
            next.delete(key);
            continue;
          }
          let total = 0;
          for (const condLabel of Object.keys(condMap)) {
            total += (condMap[condLabel] || []).length;
          }
          if (total === 0) next.delete(key);
        }
        return next;
      });
      if (selectedOs) {
        const condMap = data.osByAreaTechCondition[selectedOs.areaId]?.[selectedOs.techName];
        const hasAny = condMap && Object.values(condMap).some((arr) => Array.isArray(arr) && arr.length > 0);
        if (!hasAny) {
          setSelectedOs(null);
          setPatrimonioMenuOpen(false);
          setShowAtendimentosModal(false);
          setExpandedConditionKey(null);
        } else {
          const stillInList = (condMap?.[selectedOs.conditionLabel] || []).some(
            (o) => o.numeroOS === selectedOs.os?.numeroOS && String(o.numeroSerie || '') === String(selectedOs.os?.numeroSerie || '')
          );
          if (!stillInList) {
            setSelectedOs(null);
            setPatrimonioMenuOpen(false);
            setShowAtendimentosModal(false);
          }
        }
      }
    }
    setTimeout(() => setConcluirLoteFeedback(null), 2400);
  }, [dragConditionPayload, osByAreaTechCondition, selectedOs, refreshCheckoutResumo]);

  const toggleTechSelection = (areaId, techName) => {
    // Ao trocar o técnico, fechar qualquer OS atualmente em detalhe
    setExpandedConditionKey(null);
    setSelectedOs(null);
    setPatrimonioMenuOpen(false);
    setShowAtendimentosModal(false);
    setSelectedTechKeys((prev) => {
      const key = techKey(areaId, techName);
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const toggleAreaExpand = (areaId) =>
    setExpandedAreaIds((prev) => {
      const next = new Set(prev);
      if (next.has(areaId)) next.delete(areaId);
      else next.add(areaId);
      return next;
    });

  const closeContextMenu = useCallback(() => setContextMenu(null), []);

  useEffect(() => {
    if (!contextMenu) return;
    const onGlobalClick = (e) => {
      if (contextMenuRef.current && contextMenuRef.current.contains(e.target)) return;
      closeContextMenu();
    };
    const t = setTimeout(() => document.addEventListener('click', onGlobalClick, true), 0);
    return () => {
      clearTimeout(t);
      document.removeEventListener('click', onGlobalClick, true);
    };
  }, [contextMenu, closeContextMenu]);

  useEffect(() => {
    if (!techFilterOpen) return;
    const onGlobalClick = (e) => {
      if (techFilterRef.current && techFilterRef.current.contains(e.target)) return;
      setTechFilterOpen(false);
    };
    const t = setTimeout(() => document.addEventListener('click', onGlobalClick, true), 0);
    return () => {
      clearTimeout(t);
      document.removeEventListener('click', onGlobalClick, true);
    };
  }, [techFilterOpen]);

  useEffect(() => {
    if (!patrimonioMenuOpen) return;
    const onGlobalClick = (e) => {
      if (patrimonioMenuRef.current && patrimonioMenuRef.current.contains(e.target)) return;
      setPatrimonioMenuOpen(false);
    };
    const t = setTimeout(() => document.addEventListener('click', onGlobalClick, true), 0);
    return () => {
      clearTimeout(t);
      document.removeEventListener('click', onGlobalClick, true);
    };
  }, [patrimonioMenuOpen]);

  if (!asPage && !isOpen) return null;

  const handleAreaClick = (areaId) => {
    setSelectedAreaIds([areaId]);
    setSelectedTechKeys(new Set());
    setExpandedConditionKey(null);
    setSelectedOs(null);
  };
  const handleAreaContextMenu = (e, areaId) => {
    e.preventDefault();
    const isSelected = selectedAreaIds.includes(areaId);
    if (!isSelected && selectedAreaIds.length === 0) return;
    setContextMenu({ x: e.clientX, y: e.clientY, areaId, isSelected });
  };
  const handleSelectAlso = (areaId) => {
    setSelectedAreaIds((prev) => (prev.includes(areaId) ? prev : [...prev, areaId]));
    closeContextMenu();
  };
  const handleRemoveSelection = (areaId) => {
    setSelectedAreaIds((prev) => prev.filter((id) => id !== areaId));
    closeContextMenu();
  };

  const allIds = areas.map((a) => a.id);
  const allSelected = allIds.length > 0 && allIds.every((id) => selectedAreaIds.includes(id));
  const lastActiveAreaId = [...areas].reverse().find((a) => selectedAreaIds.includes(a.id))?.id;
  const allTechKeysInList = selectedAreaIds.flatMap((areaId) =>
    (techniciansByArea[areaId] || []).map((t) => techKey(areaId, t.name))
  );
  const allTechSelected =
    allTechKeysInList.length > 0 && allTechKeysInList.every((k) => selectedTechKeys.has(k));
  const handleTechSelectAll = () => {
    setExpandedConditionKey(null);
    if (allTechSelected) {
      setSelectedTechKeys(new Set());
      setSelectedOs(null);
    } else {
      setSelectedTechKeys(new Set(allTechKeysInList));
    }
    setTechFilterOpen(false);
  };
  const handleToggleAll = () => {
    if (allSelected) {
      setSelectedAreaIds([]);
      setSelectedTechKeys(new Set());
      setExpandedConditionKey(null);
      setSelectedOs(null);
    } else {
      setSelectedAreaIds(allIds);
    }
  };

  const innerContent = (
    <>
      <div
        className={`checkout-view-container ${asPage ? 'checkout-view-container--page' : ''}`}
        onClick={(e) => e.stopPropagation()}
      >
        {concluirFeedback === 'success' && (
          <div className="checkout-view-concluir-feedback-toast-wrap">
            <div className="checkout-view-concluir-feedback success checkout-view-concluir-feedback-toast" role="status">
              <CheckCircle size={18} strokeWidth={2} aria-hidden />
              <span>{concluirMessage || 'OS concluída com sucesso'}</span>
            </div>
          </div>
        )}
        {isRefreshingData && (
          <div className="checkout-view-refresh-overlay" aria-live="polite" aria-busy="true">
            <div className="checkout-view-refresh-spinner" aria-hidden />
            <span className="checkout-view-refresh-label">Atualizando...</span>
          </div>
        )}
        {isConcluirLoteLoading && concluirLoteProgress && (
          <div className="checkout-view-concluir-lote-overlay" aria-live="polite" aria-busy="true">
            <div className="checkout-view-refresh-spinner" aria-hidden />
            <span className="checkout-view-refresh-label">
              Concluindo {concluirLoteProgress.current} de {concluirLoteProgress.total}
            </span>
          </div>
        )}
        {concluirLoteFeedback && (
          <div className="checkout-view-concluir-feedback-toast-wrap">
            <div
              className={`checkout-view-concluir-feedback ${concluirLoteFeedback === 'success' ? 'success' : 'error'} checkout-view-concluir-feedback-toast`}
              role="status"
            >
              {concluirLoteFeedback === 'success' ? (
                <>
                  <CheckCircle size={18} strokeWidth={2} aria-hidden />
                  <span>Ordens concluídas com sucesso</span>
                </>
              ) : (
                <>
                  <X size={18} strokeWidth={2} aria-hidden />
                  <span>Algumas ordens não puderam ser concluídas</span>
                </>
              )}
            </div>
          </div>
        )}
        {isLoadingCheckout ? (
          <CheckoutViewSkeleton />
        ) : (
          <>
        {/* Item 1: Barra lateral – áreas com avatar */}
        <div className="checkout-view-sidebar" ref={areaSidebarRef}>
          <button
            type="button"
            className={`checkout-view-select-all-btn ${allSelected ? 'active' : ''}`}
            onClick={handleToggleAll}
            title={allSelected ? 'Desmarcar todas áreas' : 'Selecionar todas áreas'}
            aria-label={allSelected ? 'Desmarcar todas áreas' : 'Selecionar todas áreas'}
          >
            <span className="checkout-view-select-all-content">
              {allSelected ? <ListX size={18} strokeWidth={2} /> : <TextAlignStart size={18} strokeWidth={2} />}
              <span className="checkout-view-count">{getTotalCheckoutCount(areas, techniciansByArea)}</span>
            </span>
          </button>
          {areas.map((area) => (
            <GoHoverTooltip key={area.id} text={area.name}>
              <button
                type="button"
                className={`checkout-view-area-avatar ${selectedAreaIds.includes(area.id) ? 'active' : ''} ${asPage && pageTechCardFlushSelector && area.id === lastActiveAreaId ? 'checkout-view-area-avatar--flush-bottom' : ''}`}
                onClick={() => handleAreaClick(area.id)}
                onContextMenu={(e) => handleAreaContextMenu(e, area.id)}
                aria-label={`Área ${area.name}`}
              >
                <span className="checkout-view-area-label">{((area.abreviacao != null && area.abreviacao !== '') ? String(area.abreviacao).trim() : (area.name || '')).toString().trim().slice(0, 4)}</span>
                <span className="checkout-view-count">{getAreaCheckoutCount(area.id, areas, techniciansByArea)}</span>
              </button>
            </GoHoverTooltip>
          ))}
        </div>

        {/* Item 2: Painel central – lista de técnicos com título da área */}
        <div
          className="checkout-view-main"
          ref={techListMainRef}
          style={
            asPage && pageTechCardHeight != null
              ? {
                  alignSelf: 'flex-start',
                  minHeight: `${pageTechCardHeight.min}px`,
                  maxHeight: `${pageTechCardHeight.max}px`,
                }
              : undefined
          }
        >
          <div className="checkout-view-technician-list-header" ref={techFilterRef}>
            {techSearchOpen ? (
              <>
                <input
                  ref={techSearchInputRef}
                  type="text"
                  className="checkout-view-tech-search-input"
                  placeholder="Buscar técnico..."
                  value={techSearchQuery}
                  onChange={(e) => setTechSearchQuery(e.target.value)}
                />
                <button
                  type="button"
                  className="checkout-view-tech-filter-btn"
                  onClick={closeTechSearch}
                  title="Fechar busca"
                  aria-label="Fechar busca"
                >
                  <X size={14} strokeWidth={2} />
                </button>
              </>
            ) : (
              <>
                <span>Técnicos</span>
                <div className="checkout-view-tech-header-actions">
                  <GoHoverTooltip text="Buscar técnico">
                    <button
                      type="button"
                      className="checkout-view-tech-filter-btn"
                      onClick={() => setTechSearchOpen(true)}
                      aria-label="Buscar técnico"
                    >
                      <Search size={14} strokeWidth={2} />
                    </button>
                  </GoHoverTooltip>
                  {asPage ? (
                    <GoHoverTooltip text={allTechSelected ? 'Desmarcar todos' : 'Selecionar todos'}>
                      <button
                        type="button"
                        className={`checkout-view-tech-filter-btn ${allTechSelected ? 'is-active' : ''}`}
                        onClick={handleTechSelectAll}
                        aria-label={allTechSelected ? 'Desmarcar todos' : 'Selecionar todos'}
                        aria-pressed={allTechSelected}
                      >
                        {allTechSelected ? <ListX size={14} strokeWidth={2} /> : <ListChecks size={14} strokeWidth={2} />}
                      </button>
                    </GoHoverTooltip>
                  ) : (
                    <button
                      type="button"
                      className="checkout-view-tech-filter-btn"
                      onClick={() => setTechFilterOpen((v) => !v)}
                      title="Opções"
                      aria-label="Opções"
                      aria-expanded={techFilterOpen}
                    >
                      <ListFilter size={14} strokeWidth={2} />
                    </button>
                  )}
                </div>
              </>
            )}
            {techFilterOpen && (
              <div className="checkout-view-tech-filter-menu">
                <button
                  type="button"
                  className="checkout-view-context-menu-item"
                  onClick={handleTechSelectAll}
                >
                  {allTechSelected ? 'Desmarcar todos' : 'Selecionar todos'}
                </button>
              </div>
            )}
          </div>
          <div className={`checkout-view-technician-list ${selectedAreaIds.length === 0 ? 'empty' : ''} ${asPage && pageTechCardFlushSelector ? 'checkout-view-technician-list--flush-selector' : ''}`}>
            {selectedAreaIds.length === 0 ? (
              <div className="checkout-view-detail-placeholder">
                <MapPin size={20} strokeWidth={1.8} className="checkout-view-detail-placeholder-icon" aria-hidden />
                <span>Selecione uma área para visualizar os técnicos</span>
              </div>
            ) : (
              selectedAreaIds.map((areaId) => {
              const area = areas.find((a) => a.id === areaId);
              const technicians = [...(techniciansByArea[areaId] || [])]
                .filter((t) => matchesTechSearch(t.name))
                .sort((a, b) => {
                  const ka = getEarliestOsSortKeyForTech(areaId, a.name, osByAreaTechCondition);
                  const kb = getEarliestOsSortKeyForTech(areaId, b.name, osByAreaTechCondition);
                  if (ka !== kb) return ka - kb;
                  return a.name.localeCompare(b.name, 'pt-BR');
                });
              return (
                technicians.length > 0 && (
                <div key={areaId} className="checkout-view-area-block">
                  <button
                    type="button"
                    className="checkout-view-list-title-btn"
                    onClick={() => toggleAreaExpand(areaId)}
                    aria-expanded={expandedAreaIds.has(areaId)}
                  >
                    <span className="checkout-view-list-title-text">{area?.name}</span>
                    {expandedAreaIds.has(areaId) ? (
                      <ChevronUp size={16} strokeWidth={2} />
                    ) : (
                      <ChevronDown size={16} strokeWidth={2} />
                    )}
                  </button>
                  {technicians.map((tec) => {
                    const techItemEl = (
                      <div
                        role="button"
                        tabIndex={0}
                        className={`checkout-view-technician-item ${selectedTechKeys.has(techKey(areaId, tec.name)) ? 'selected' : ''}`}
                        onClick={() => toggleTechSelection(areaId, tec.name)}
                        onKeyDown={(e) => e.key === 'Enter' && toggleTechSelection(areaId, tec.name)}
                      >
                        <div className="icon-wrap">
                          <User size={14} strokeWidth={2} />
                        </div>
                        <span className="name">{titleCase(tec.name)}</span>
                        <span className="count">
                          <CheckoutSplitCount adhocCount={tec.adhocCount} count={tec.count} />
                        </span>
                      </div>
                    );
                    return (
                    <div key={`${areaId}-${tec.name}`} className="checkout-view-technician-wrap">
                      {asPage ? (
                        <GoHoverTooltip
                          text="Selecione o técnico para visualizar as ordens de serviço ao lado"
                          delayMs={1000}
                          className="checkout-view-tech-item-tip-wrap"
                        >
                          {techItemEl}
                        </GoHoverTooltip>
                      ) : techItemEl}
                      {expandedAreaIds.has(areaId) && (
                        <div className="checkout-view-conditions-list">
                          {[...getConditions(areaId, tec.name, conditionsByAreaTech)]
                            .sort((c1, c2) => {
                              const k1 = getEarliestOsSortKeyInList(getOsList(areaId, tec.name, c1.label, osByAreaTechCondition) || []);
                              const k2 = getEarliestOsSortKeyInList(getOsList(areaId, tec.name, c2.label, osByAreaTechCondition) || []);
                              if (k1 !== k2) return k1 - k2;
                              return c1.label.localeCompare(c2.label, 'pt-BR');
                            })
                            .map((c) => (
                            <div key={c.label} className="checkout-view-condition-item">
                              <span className="checkout-view-condition-label">{c.label}</span>
                              <CheckoutSplitCount adhocCount={c.adhocCount} count={c.count} className="checkout-view-condition-count-split" />
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                    );
                  })}
                </div>
                )
              );
            })
            )}
          </div>
        </div>

        {/* Painel direito – header (busca) + cards/placeholder + painel OS (30%) */}
        <div className={`checkout-view-detail ${selectedTechKeys.size === 0 ? 'empty' : ''} ${selectedOs ? 'has-os-panel' : ''}`}>
          <div className="checkout-view-detail-content">
            <div className="checkout-view-detail-header">
              <div className="checkout-view-detail-search-wrap">
                <TipoOsFilterMulti
                  options={tipoOsFilterOptions}
                  selected={tipoOsFilterSelected}
                  onChange={setTipoOsFilterSelected}
                />
                <ConditionFilterMulti
                  options={conditionFilterOptions}
                  selected={conditionFilterSelected}
                  onChange={setConditionFilterSelected}
                />
                {asPage ? (
                  <div
                    className="checkout-view-detail-expand-search"
                    onClick={() => detailSearchInputRef.current && detailSearchInputRef.current.focus()}
                  >
                    <Search size={13} strokeWidth={2} className="checkout-view-detail-expand-search-icon" aria-hidden />
                    <input
                      ref={detailSearchInputRef}
                      type="text"
                      className="checkout-view-detail-expand-search-input"
                      placeholder="Pesquise por patrimônio, série ou OS..."
                      value={detailSearchQuery}
                      onChange={(e) => setDetailSearchQuery(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && runDetailSearch()}
                    />
                  </div>
                ) : (
                  <input
                    ref={detailSearchInputRef}
                    type="text"
                    className="checkout-view-tech-search-input checkout-view-detail-search-input"
                    placeholder="Pesquise por patrimônio, série ou OS..."
                    value={detailSearchQuery}
                    onChange={(e) => setDetailSearchQuery(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && runDetailSearch()}
                  />
                )}
                <GoHoverTooltip text="Atualizar dados">
                  <button
                    type="button"
                    className="checkout-view-detail-refresh-btn"
                    onClick={handleAtualizarDados}
                    disabled={isRefreshingData}
                    aria-label="Atualizar dados"
                  >
                    <RefreshCw size={14} strokeWidth={2} className={isRefreshingData ? 'checkout-view-detail-refresh-icon-spin' : ''} />
                    <span>Atualizar</span>
                  </button>
                </GoHoverTooltip>
              </div>
            </div>
            <div className={`checkout-view-detail-main ${selectedTechKeys.size === 0 ? 'empty' : ''}`}>
            {selectedTechKeys.size === 0 ? (
              asPage ? (
                <div className="checkout-view-detail-placeholder checkout-view-detail-placeholder--modern">
                  <User size={48} strokeWidth={1.25} className="checkout-view-detail-placeholder-icon" aria-hidden />
                  <h3 className="checkout-view-detail-placeholder-title">Selecione um técnico</h3>
                  <p className="checkout-view-detail-placeholder-desc">
                    Escolha um técnico em uma área para visualizar os check-outs.
                  </p>
                </div>
              ) : (
                <div className="checkout-view-detail-placeholder">
                  <User size={20} strokeWidth={1.8} className="checkout-view-detail-placeholder-icon" aria-hidden />
                  <span>Selecione um técnico em uma área para visualizar os check-outs</span>
                </div>
              )
            ) : (
              <div className="checkout-view-detail-cards">
              {[...selectedTechKeys].sort((keyA, keyB) => {
                const [areaIdA, techNameA] = keyA.split('|');
                const [areaIdB, techNameB] = keyB.split('|');
                const ka = getEarliestOsSortKeyForTech(
                  areaIdA,
                  techNameA,
                  osByAreaTechCondition,
                  conditionFilterSelected,
                  tipoOsFilterSelected
                );
                const kb = getEarliestOsSortKeyForTech(
                  areaIdB,
                  techNameB,
                  osByAreaTechCondition,
                  conditionFilterSelected,
                  tipoOsFilterSelected
                );
                if (ka !== kb) return ka - kb;
                return keyA.localeCompare(keyB, 'pt-BR');
              }).map((key) => {
                const [areaId, techName] = key.split('|');
                const area = areas.find((a) => a.id === areaId);
                const tec = (techniciansByArea[areaId] || []).find((t) => t.name === techName);
                const conditions = getConditions(areaId, techName, conditionsByAreaTech);
                const hasAnyOs =
                  osByAreaTechCondition &&
                  osByAreaTechCondition[areaId] &&
                  osByAreaTechCondition[areaId][techName] &&
                  Object.entries(osByAreaTechCondition[areaId][techName]).some(
                    ([condLabel, list]) =>
                      Array.isArray(list) &&
                      list.length > 0 &&
                      (conditionFilterSelected.size === 0 || conditionFilterSelected.has(condLabel)) &&
                      filterOsListByTipo(list, tipoOsFilterSelected).length > 0
                  );
                if (!hasAnyOs) return null;
                const cardHasExpanded = expandedConditionKey != null && expandedConditionKey.startsWith(key + '|');
                return (
                  <div key={key} className={`checkout-view-tech-card ${cardHasExpanded ? 'expanded' : ''}`}>
                    <div className="checkout-view-tech-card-title">{titleCase(techName)}</div>
                    <div className="checkout-view-tech-card-subtitle">{area?.name}</div>
                    <div className="checkout-view-tech-card-conditions">
                      {[...conditions]
                        .filter((c) => conditionFilterSelected.size === 0 || conditionFilterSelected.has(c.label))
                        .sort((c1, c2) => {
                          const k1 = getEarliestOsSortKeyInList(
                            filterOsListByTipo(
                              getOsList(areaId, techName, c1.label, osByAreaTechCondition) || [],
                              tipoOsFilterSelected
                            )
                          );
                          const k2 = getEarliestOsSortKeyInList(
                            filterOsListByTipo(
                              getOsList(areaId, techName, c2.label, osByAreaTechCondition) || [],
                              tipoOsFilterSelected
                            )
                          );
                          if (k1 !== k2) return k1 - k2;
                          return c1.label.localeCompare(c2.label, 'pt-BR');
                        })
                        .map((c) => {
                        const isExpanded = expandedConditionKey === conditionKey(areaId, techName, c.label);
                        const osList = filterOsListByTipo(
                          getOsList(areaId, techName, c.label, osByAreaTechCondition) || [],
                          tipoOsFilterSelected
                        );
                        if (!osList.length) return null;
                        const osListToShow = [...osList].sort((a, b) => {
                              const ka = getOsSortKey(a);
                              const kb = getOsSortKey(b);
                              if (ka !== kb) return ka - kb;
                              return String(a.numeroOS ?? '').localeCompare(String(b.numeroOS ?? ''), undefined, { numeric: true });
                            });
                        const filteredAdhocCount = osList.filter((o) => o.isAdhoc).length;
                        const filteredNormalCount = osList.length - filteredAdhocCount;
                        const canDragCondition =
                          c.func_close === 1 &&
                          !selectedOs &&
                          !c.func_order_op &&
                          !c.func_order_st_retorno &&
                          !(filteredAdhocCount > 0);
                        return (
                          <div key={c.label} className="checkout-view-tech-card-condition-wrap">
                            <div
                              role="button"
                              tabIndex={0}
                              className={`checkout-view-tech-card-condition ${isExpanded ? 'expanded' : ''} ${canDragCondition ? 'checkout-view-tech-card-condition-draggable' : ''}`}
                              draggable={canDragCondition}
                              onDragStart={canDragCondition ? (e) => handleConditionDragStart(e, areaId, techName, c.label) : undefined}
                              onDragEnd={canDragCondition ? handleConditionDragEnd : undefined}
                              onClick={() => {
                                if (conditionDragJustEndedRef.current) return;
                                toggleConditionExpand(areaId, techName, c.label);
                              }}
                              onKeyDown={(e) => e.key === 'Enter' && toggleConditionExpand(areaId, techName, c.label)}
                            >
                              <span className="checkout-view-tech-card-condition-label-wrap">
                                {c.func_close === 1 && (
                                  <span
                                    className={`checkout-view-tech-card-condition-dot ${c.func_order_op || c.func_order_st_retorno ? 'checkout-view-tech-card-condition-dot-special' : ''}`}
                                    aria-hidden
                                  />
                                )}
                                <span className="checkout-view-tech-card-condition-label">{c.label}</span>
                              </span>
                              <CheckoutSplitCount
                                adhocCount={filteredAdhocCount}
                                count={filteredNormalCount}
                                className="checkout-view-tech-card-condition-count-split"
                              />
                            </div>
                            {isExpanded && osListToShow.length > 0 && (
                              <div className="checkout-view-tech-card-os-list">
                                {osListToShow.map((os, i) => (
                                  <div
                                    key={i}
                                    role="button"
                                    tabIndex={0}
                                    className={`checkout-view-tech-card-os-row${os.isAdhoc ? ' checkout-view-tech-card-os-row--adhoc' : ''}${!os.isAdhoc && isOsSelected(areaId, techName, c.label, os) ? ' selected' : ''}`}
                                    onClick={() => handleOsRowClick(areaId, techName, c.label, os)}
                                    onKeyDown={(e) => e.key === 'Enter' && handleOsRowClick(areaId, techName, c.label, os)}
                                  >
                                    <span className={`checkout-view-os-numero-wrap${os.isAdhoc ? ' checkout-view-os-numero-wrap--adhoc' : ''}`}>
                                      {os.isAdhoc ? (
                                        <span className="checkout-view-os-adhoc-dot" aria-hidden />
                                      ) : null}
                                      {os.isAdhoc ? (
                                        <GoHoverTooltip
                                          text={ADHOC_OS_TOOLTIP}
                                          className="checkout-view-adhoc-os-num-tip go-hover-tip-wrap--contents"
                                        >
                                          <span className="checkout-view-os-numero">{os.numeroOS}</span>
                                        </GoHoverTooltip>
                                      ) : (
                                        <span className="checkout-view-os-numero">{os.numeroOS}</span>
                                      )}
                                    </span>
                                    {(() => {
                                      const p = patrimônioOuSerieParaLista(os);
                                      return (
                                        <GoHoverTooltip text={p.titulo || undefined}>
                                          <span className="checkout-view-os-patrimonio">{p.valor}</span>
                                        </GoHoverTooltip>
                                      );
                                    })()}
                                    {(() => {
                                      const clienteDisplay = getAdhocOsClienteDisplay(os, adhocOsEnrichBySerial);
                                      return (
                                        <GoHoverTooltip text={!os.isAdhoc && os.clienteNome && os.cliente !== os.clienteNome ? os.clienteNome : clienteDisplay || undefined}>
                                          <span className="checkout-view-os-cliente">{clienteDisplay || '—'}</span>
                                        </GoHoverTooltip>
                                      );
                                    })()}
                                    {(() => {
                                      const modeloDisplay = getAdhocOsModeloDisplay(os, adhocOsEnrichBySerial);
                                      return (
                                        <GoHoverTooltip text={modeloDisplay || undefined}>
                                          <span className="checkout-view-os-modelo">{modeloDisplay || '—'}</span>
                                        </GoHoverTooltip>
                                      );
                                    })()}
                                    <span className={`checkout-view-os-dt ${os.PECAPENDENTE === 1 || os.PECAPENDENTE === '1' ? 'checkout-view-os-dt-with-wrench' : ''}`}>
                                      {formatDataHoraFimOsDt(os)}
                                      {os.PECAPENDENTE === 1 || os.PECAPENDENTE === '1' ? <Wrench size={10} className="checkout-view-os-wrench" aria-hidden /> : null}
                                    </span>
                                  </div>
                                ))}
</div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
              {isDraggingCondition && (
                <div
                  className={`checkout-view-conclude-drop-zone ${isDragOverDropZone ? 'checkout-view-conclude-drop-zone-active' : ''}`}
                  onDragOver={handleDropZoneDragOver}
                  onDragLeave={handleDropZoneDragLeave}
                  onDrop={handleDropZoneDrop}
                >
                  <CheckCircle size={32} strokeWidth={2} className="checkout-view-conclude-drop-zone-icon" aria-hidden />
                  <span className="checkout-view-conclude-drop-zone-label">Arraste aqui para Concluir</span>
                </div>
              )}
              </div>
            )}
            </div>
          </div>
          {selectedOs && (
            <div className="checkout-view-os-detail-panel">
              <div className="checkout-view-os-detail-content">
                <div className="checkout-view-os-detail-title-row">
                  <div className="checkout-view-os-detail-title-top">
                    <div className="checkout-view-os-detail-os-row">
                      <Ticket size={16} strokeWidth={2} />
                      <span className="checkout-view-os-detail-os-numero">{selectedOs.os.numeroOS}</span>
                    </div>
                    <button
                      type="button"
                      className="checkout-view-os-detail-panel-close"
                      onClick={() => { setSelectedOs(null); setPatrimonioMenuOpen(false); setShowAtendimentosModal(false); setShowOsRastreioModal(false); }}
                      title="Fechar detalhes"
                      aria-label="Fechar detalhes"
                    >
                      <X size={18} strokeWidth={2} />
                    </button>
                  </div>
                  <div className="checkout-view-os-detail-title-actions">
                    {!isFotosLoading && (
                      <GoHoverTooltip text={fotos.length > 0 ? 'Ver fotos' : 'Nenhuma foto'}>
                        <button
                          type="button"
                          className="checkout-view-fotos-btn"
                          onClick={() => fotos.length > 0 && setShowFotosModal(true)}
                          disabled={fotos.length === 0}
                        >
                          <ImageIcon size={11} strokeWidth={2} aria-hidden />
                          <span>Fotos</span>
                        </button>
                      </GoHoverTooltip>
                    )}
                    {!isAssinaturaLoading && (
                      <GoHoverTooltip text={assinaturaSvg ? 'Ver assinatura' : 'Nenhuma assinatura'}>
                        <button
                          type="button"
                          className="checkout-view-fotos-btn"
                          onClick={() => assinaturaSvg && setShowAssinaturaModal(true)}
                          disabled={!assinaturaSvg}
                        >
                          <PenLine size={11} strokeWidth={2} aria-hidden />
                          <span>Assinatura</span>
                        </button>
                      </GoHoverTooltip>
                    )}
                    {!isOsRastreioLoading && osRastreio?.hasTrack ? (
                      <GoHoverTooltip
                        text={
                          osRastreio.outsideRadius
                            ? 'Atendimento iniciado fora do raio de atendimento'
                            : 'Ver rastreio do atendimento'
                        }
                      >
                        <button
                          type="button"
                          className={`checkout-view-fotos-btn${
                            osRastreio.outsideRadius ? ' checkout-view-rastreio-btn--outside' : ''
                          }`}
                          onClick={() => setShowOsRastreioModal(true)}
                        >
                          <Route size={11} strokeWidth={2} aria-hidden />
                          <span>Rastreio</span>
                        </button>
                      </GoHoverTooltip>
                    ) : null}
                  </div>
                </div>
                <div className="checkout-view-os-detail-separator">
                  <div className="checkout-view-os-detail-item checkout-view-patrimonio-wrap" ref={patrimonioMenuRef}>
                    <span className="checkout-view-os-detail-label">Patrimônio</span>
                    <span className="checkout-view-os-detail-value checkout-view-patrimonio-with-icon">
                      {patrimônioESérie(selectedOs.os)}
                      <button
                        type="button"
                        className="checkout-view-patrimonio-btn"
                        onClick={() => setPatrimonioMenuOpen((v) => !v)}
                        aria-expanded={patrimonioMenuOpen}
                        aria-label="Abrir menu"
                      >
                        <ChevronDown size={16} strokeWidth={2} />
                      </button>
                      {selectedOs.os.endereco && (
                        <GoHoverTooltip text="Endereço do equipamento">
                          <button
                            type="button"
                            className="checkout-view-patrimonio-btn checkout-view-patrimonio-map-btn"
                            onClick={() => setShowEnderecoMapModal(true)}
                            aria-label="Endereço do equipamento"
                          >
                            <MapIcon size={16} strokeWidth={2} />
                          </button>
                        </GoHoverTooltip>
                      )}
                    </span>
                    {patrimonioMenuOpen && (
                      <div className="checkout-view-patrimonio-menu">
                        <GoHoverTooltip text="Ver histórico de serviço">
                          <button type="button" className="checkout-view-patrimonio-menu-btn" aria-label="Ver histórico de serviço" onClick={() => { setShowAtendimentosModal(true); setPatrimonioMenuOpen(false); }}>
                            <History size={14} strokeWidth={2} />
                            <span>Histórico de serviço</span>
                          </button>
                        </GoHoverTooltip>
                      </div>
                    )}
                  </div>
                  <div className="checkout-view-os-detail-item">
                    <span className="checkout-view-os-detail-label">Cliente</span>
                    <span className="checkout-view-os-detail-value">{selectedOs.os.cliente || '—'}</span>
                  </div>
                  <div className="checkout-view-os-detail-item">
                    <span className="checkout-view-os-detail-label">Modelo</span>
                    <span className="checkout-view-os-detail-value">{selectedOs.os.modeloEquipamento || '—'}</span>
                  </div>
                </div>
                <div className="checkout-view-os-detail-separator">
                  <div className="checkout-view-os-detail-item">
                    <div className="checkout-view-os-detail-laudo-header">
                      <span className="checkout-view-os-detail-label">Horários de atendimento</span>
                      <div className="checkout-view-os-detail-laudo-actions">
                        {!isHorariosEditing ? (
                          <GoHoverTooltip text="Editar horários">
                            <button
                              type="button"
                              className="checkout-view-laudo-btn checkout-view-laudo-btn-edit checkout-view-condicao-edit-btn"
                              onClick={startHorariosEdit}
                              aria-label="Editar horários"
                            >
                              <Pencil size={14} strokeWidth={2} />
                            </button>
                          </GoHoverTooltip>
                        ) : (
                          <>
                            <button
                              type="button"
                              className="checkout-view-laudo-btn checkout-view-laudo-btn-check"
                              onClick={confirmHorariosEdit}
                              disabled={isHorariosSaving}
                              title="Salvar horários"
                              aria-label="Salvar horários"
                            >
                              {isHorariosSaving ? (
                                <span className="checkout-view-laudo-spinner" aria-hidden />
                              ) : (
                                <Check size={14} strokeWidth={2} />
                              )}
                            </button>
                            <button
                              type="button"
                              className="checkout-view-laudo-btn checkout-view-laudo-btn-cancel"
                              onClick={cancelHorariosEdit}
                              disabled={isHorariosSaving}
                              title="Cancelar"
                              aria-label="Cancelar"
                            >
                              <X size={14} strokeWidth={2} />
                            </button>
                          </>
                        )}
                      </div>
                    </div>
                    {!isHorariosEditing ? (
                      <span className="checkout-view-os-detail-value checkout-view-horarios-with-icon">
                        {horáriosAtendimento(selectedOs.os)}
                        {(() => {
                          const tp = tempoProduzidoHhMm(selectedOs.os);
                          return tp != null ? (
                            <GoHoverTooltip text={`Tempo produzido: ${tp}`}>
                              <span className="checkout-view-timer-wrap">
                                <Timer size={14} className="checkout-view-timer-icon" aria-hidden />
                              </span>
                            </GoHoverTooltip>
                          ) : null;
                        })()}
                      </span>
                    ) : (
                      <div className="checkout-view-horarios-edit-wrap">
                        <div className="checkout-view-horarios-edit-block">
                          <span className="checkout-view-horarios-edit-caption">Início</span>
                          <div className="checkout-view-horarios-edit-row">
                            <input
                              type="date"
                              className="checkout-view-horarios-edit-input checkout-view-horarios-edit-input-date"
                              value={horariosEdit.dataIni}
                              onChange={(e) =>
                                setHorariosEdit((h) => ({ ...h, dataIni: e.target.value }))
                              }
                              disabled={isHorariosSaving}
                            />
                            <input
                              type="time"
                              className="checkout-view-horarios-edit-input checkout-view-horarios-edit-input-time"
                              value={horariosEdit.horaIni}
                              onChange={(e) =>
                                setHorariosEdit((h) => ({ ...h, horaIni: e.target.value }))
                              }
                              disabled={isHorariosSaving}
                            />
                          </div>
                        </div>
                        <div className="checkout-view-horarios-edit-block">
                          <span className="checkout-view-horarios-edit-caption">Fim</span>
                          <div className="checkout-view-horarios-edit-row">
                            <input
                              type="date"
                              className="checkout-view-horarios-edit-input checkout-view-horarios-edit-input-date"
                              value={horariosEdit.dataFim}
                              onChange={(e) =>
                                setHorariosEdit((h) => ({ ...h, dataFim: e.target.value }))
                              }
                              disabled={isHorariosSaving}
                            />
                            <input
                              type="time"
                              className="checkout-view-horarios-edit-input checkout-view-horarios-edit-input-time"
                              value={horariosEdit.horaFim}
                              onChange={(e) =>
                                setHorariosEdit((h) => ({ ...h, horaFim: e.target.value }))
                              }
                              disabled={isHorariosSaving}
                            />
                          </div>
                        </div>
                        {isHorariosSaving && (
                          <div className="checkout-view-condicao-saving" role="status">
                            <span className="checkout-view-laudo-spinner" aria-hidden />
                            <span>Atualizando horários…</span>
                          </div>
                        )}
                      </div>
                    )}
                    {horariosSaveFeedback && (
                      <div className={`checkout-view-laudo-feedback ${horariosSaveFeedback}`} role="status">
                        {horariosSaveFeedback === 'success'
                          ? 'Horários atualizados com sucesso'
                          : horariosSaveMessage || 'Erro ao atualizar. Tente novamente.'}
                      </div>
                    )}
                  </div>
                  <div className="checkout-view-os-detail-item checkout-view-condicao-wrap" ref={condicaoDropdownRef}>
                    <span className="checkout-view-os-detail-label">Condição</span>
                    {!isCondicaoEditing ? (
                      <span className="checkout-view-os-detail-value checkout-view-condicao-with-icon">
                        {selectedOs.conditionLabel}
                        <GoHoverTooltip text="Editar condição">
                          <button
                            type="button"
                            className="checkout-view-laudo-btn checkout-view-laudo-btn-edit checkout-view-condicao-edit-btn"
                            onClick={startCondicaoEdit}
                            aria-label="Editar condição"
                          >
                            <Pencil size={14} strokeWidth={2} />
                          </button>
                        </GoHoverTooltip>
                      </span>
                    ) : (
                      <div className="checkout-view-condicao-edit-wrap">
                        <div className="checkout-view-condicao-edit-row">
                          <input
                          ref={condicaoInputRef}
                          type="text"
                          className="checkout-view-condicao-input"
                          value={condicaoEditValue}
                          onChange={(e) => setCondicaoEditValue(e.target.value)}
                          placeholder="Digite 4+ caracteres para buscar..."
                          disabled={isCondicaoSaving}
                        />
                          <button
                            type="button"
                            className="checkout-view-laudo-btn checkout-view-laudo-btn-cancel"
                            onClick={cancelCondicaoEdit}
                            disabled={isCondicaoSaving}
                            title="Cancelar"
                            aria-label="Cancelar"
                          >
                            <X size={14} strokeWidth={2} />
                          </button>
                        </div>
                        {isCondicaoSaving && (
                          <div className="checkout-view-condicao-saving" role="status">
                            <span className="checkout-view-laudo-spinner" aria-hidden />
                            <span>Atualizando condição...</span>
                          </div>
                        )}
                        {!isCondicaoSaving && condicaoSearchLoading && (
                          <div className="checkout-view-condicao-search-loading" role="status">
                            <span className="checkout-view-laudo-spinner" aria-hidden />
                            <span>Buscando...</span>
                          </div>
                        )}
                        {!isCondicaoSaving && !condicaoSearchLoading && condicaoSearchResults.length > 0 && (
                          <ul className="checkout-view-condicao-dropdown" role="listbox">
                            {condicaoSearchResults.map((r) => (
                              <li key={r.codigo} role="option">
                                <button
                                  type="button"
                                  className="checkout-view-condicao-option"
                                  onClick={() => handleSelectCondicao(r)}
                                >
                                  {r.nome}
                                </button>
                              </li>
                            ))}
                          </ul>
                        )}
                        {condicaoFeedback && (
                          <div className={`checkout-view-laudo-feedback ${condicaoFeedback}`} role="status">
                            {condicaoFeedback === 'success' ? 'Condição atualizada com sucesso' : 'Erro ao atualizar. Tente novamente.'}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                  <div className="checkout-view-os-detail-item">
                    <div className="checkout-view-os-detail-laudo-header">
                      <span className="checkout-view-os-detail-label">Texto do laudo</span>
                      <div className="checkout-view-os-detail-laudo-actions">
                        {!isLaudoEditing ? (
                          <GoHoverTooltip text="Editar laudo">
                            <button
                              type="button"
                              className="checkout-view-laudo-btn checkout-view-laudo-btn-edit"
                              onClick={startLaudoEdit}
                              aria-label="Editar laudo"
                            >
                              <Pencil size={14} strokeWidth={2} />
                            </button>
                          </GoHoverTooltip>
                        ) : (
                          <>
                            <button
                              type="button"
                              className="checkout-view-laudo-btn checkout-view-laudo-btn-check"
                              onClick={confirmLaudoEdit}
                              disabled={isLaudoSaving}
                              title="Salvar"
                              aria-label="Salvar"
                            >
                              {isLaudoSaving ? (
                                <span className="checkout-view-laudo-spinner" aria-hidden />
                              ) : (
                                <Check size={14} strokeWidth={2} />
                              )}
                            </button>
                            <button
                              type="button"
                              className="checkout-view-laudo-btn checkout-view-laudo-btn-cancel"
                              onClick={cancelLaudoEdit}
                              disabled={isLaudoSaving}
                              title="Cancelar"
                              aria-label="Cancelar"
                            >
                              <X size={14} strokeWidth={2} />
                            </button>
                          </>
                        )}
                      </div>
                    </div>
                    <textarea
                      className={`checkout-view-os-detail-laudo-text ${isLaudoEditing ? 'editing' : ''}`}
                      rows={4}
                      readOnly={!isLaudoEditing}
                      value={isLaudoEditing ? laudoEditValue : (selectedOs.os.TB02122_OBS ?? '')}
                      onChange={isLaudoEditing ? (e) => setLaudoEditValue(e.target.value) : undefined}
                      placeholder="Nenhum laudo registrado."
                    />
                    {laudoSaveFeedback && (
                      <div className={`checkout-view-laudo-feedback ${laudoSaveFeedback}`} role="status">
                        {laudoSaveFeedback === 'success' ? 'Laudo atualizado com sucesso' : 'Erro ao atualizar. Tente novamente.'}
                      </div>
                    )}
                  </div>
                  <div className="checkout-view-os-detail-item">
                    <div className="checkout-view-os-detail-laudo-header">
                      <span className="checkout-view-os-detail-label">Contadores</span>
                      <div className="checkout-view-os-detail-laudo-actions">
                        {!isContadoresEditing ? (
                          <button
                            type="button"
                            className="checkout-view-laudo-btn checkout-view-laudo-btn-edit"
                            onClick={startContadoresEdit}
                            title="Editar contadores"
                            aria-label="Editar contadores"
                          >
                            <Pencil size={14} strokeWidth={2} />
                          </button>
                        ) : (
                          <>
                            <button
                              type="button"
                              className="checkout-view-laudo-btn checkout-view-laudo-btn-check"
                              onClick={confirmContadoresEdit}
                              disabled={isContadoresSaving}
                              title="Salvar contadores"
                              aria-label="Salvar contadores"
                            >
                              {isContadoresSaving ? (
                                <span className="checkout-view-laudo-spinner" aria-hidden />
                              ) : (
                                <Check size={14} strokeWidth={2} />
                              )}
                            </button>
                            <button
                              type="button"
                              className="checkout-view-laudo-btn checkout-view-laudo-btn-cancel"
                              onClick={cancelContadoresEdit}
                              disabled={isContadoresSaving}
                              title="Cancelar edição de contadores"
                              aria-label="Cancelar edição de contadores"
                            >
                              <X size={14} strokeWidth={2} />
                            </button>
                          </>
                        )}
                      </div>
                    </div>
                    {selectedOs.os.isNative ? (
                    <div className="checkout-view-os-detail-contadores">
                      {(selectedOs.os.meters || []).length === 0 && (
                        <span className="checkout-view-os-detail-value">
                          {selectedOs.os.equipmentNotStarting ? 'Equipamento não liga' : 'Nenhum medidor cadastrado para o modelo'}
                        </span>
                      )}
                      {(selectedOs.os.meters || []).map((m) => (
                        <div
                          key={m.key}
                          className="checkout-view-contador-cell"
                          title={m.previous != null ? `Leitura anterior: ${formatContador(m.previous)}` : undefined}
                        >
                          <span className="checkout-view-os-detail-label">{m.name}</span>
                          {!isContadoresEditing ? (
                            <span className="checkout-view-os-detail-value">{formatContador(m.value)}</span>
                          ) : (
                            <input
                              type="number"
                              className="checkout-view-contador-input"
                              value={contadoresEditValues?.[m.key] ?? ''}
                              onChange={(e) => handleContadorChange(m.key, e.target.value)}
                              min={m.preventDecrease && m.previous != null ? m.previous : 0}
                              inputMode="numeric"
                            />
                          )}
                        </div>
                      ))}
                    </div>
                    ) : (
                    <div className="checkout-view-contadores-stack">
                      {(selectedOs.os.goMeters || []).length > 0 && (
                        <span className="checkout-view-contadores-origem">ERP</span>
                      )}
                      <div className="checkout-view-os-detail-contadores">
                      {[
                        { field: 'TB02122_CONTADOR', label: 'A4 P&B' },
                        { field: 'TB02122_CONTADORC', label: 'A4 Cor' },
                        { field: 'TB02122_CONTADORDG', label: 'Dig.' },
                        { field: 'TB02122_CONTADORGF', label: 'A3 P&B' },
                        { field: 'TB02122_CONTADORGFC', label: 'A3 Cor' },
                      ].map(({ field, label }) => {
                        const currentVal = selectedOs.os[field];
                        const editVal =
                          contadoresEditValues && Object.prototype.hasOwnProperty.call(contadoresEditValues, field)
                            ? contadoresEditValues[field]
                            : currentVal != null
                              ? String(currentVal)
                              : '';
                        return (
                          <div key={field} className="checkout-view-contador-cell">
                            <span className="checkout-view-os-detail-label">{label}</span>
                            {!isContadoresEditing ? (
                              <span className="checkout-view-os-detail-value">
                                {formatContador(currentVal)}
                              </span>
                            ) : (
                              <input
                                type="number"
                                className="checkout-view-contador-input"
                                value={editVal ?? ''}
                                onChange={(e) => handleContadorChange(field, e.target.value)}
                                min="0"
                                inputMode="numeric"
                              />
                            )}
                          </div>
                        );
                      })}
                      </div>
                      {(selectedOs.os.goMeters || []).length > 0 && (
                        <>
                          <span className="checkout-view-contadores-origem">Medições App Go</span>
                          <div className="checkout-view-os-detail-contadores">
                            {(selectedOs.os.goMeters || []).map((m) => (
                              <div key={m.key} className="checkout-view-contador-cell" title={m.name}>
                                <span className="checkout-view-os-detail-label">{m.name}</span>
                                <span className="checkout-view-os-detail-value">{formatContador(m.value)}</span>
                              </div>
                            ))}
                          </div>
                        </>
                      )}
                    </div>
                    )}
                    {contadoresFeedback && (
                      <div className={`checkout-view-laudo-feedback ${contadoresFeedback}`} role="status">
                        {contadoresFeedback === 'success'
                          ? 'Contadores atualizados com sucesso'
                          : contadoresErrorMsg || 'Erro ao atualizar contadores. Tente novamente.'}
                      </div>
                    )}
                  </div>
                </div>
                {selectedOs && (
                <div className="checkout-view-os-detail-pecas">
                  {(incluirPecasFeedback || trocarPecaFeedback) && (
                    <div className={`checkout-view-laudo-feedback incluir-pecas-panel-feedback ${incluirPecasFeedback || trocarPecaFeedback}`} role="status">
                      {trocarPecaFeedback ? (
                        trocarPecaFeedback === 'success' ? (
                          <><CheckCircle size={16} strokeWidth={2} aria-hidden /> Peça trocada com sucesso.</>
                        ) : (
                          <><X size={16} strokeWidth={2} aria-hidden /> Erro ao trocar peça. Tente novamente.</>
                        )
                      ) : incluirPecasFeedback === 'success' ? (
                        <><CheckCircle size={16} strokeWidth={2} aria-hidden /> Peças incluídas com sucesso.</>
                      ) : (
                        <><X size={16} strokeWidth={2} aria-hidden /> Erro ao incluir. Tente novamente.</>
                      )}
                    </div>
                  )}
                  {isRefreshingPecasPendentes && (
                    <div className="incluir-pecas-refresh-hint" role="status">
                      <span className="checkout-view-laudo-spinner" aria-hidden />
                      <span>Atualizando lista...</span>
                    </div>
                  )}
                  <div className="checkout-view-os-detail-os-row">
                    <Wrench size={16} strokeWidth={2} />
                    <span>Peças pendentes</span>
                    <GoHoverTooltip text="Incluir peças">
                      <button
                        type="button"
                        className="checkout-view-incluir-pecas-btn"
                        onClick={handleOpenIncluirPecasModal}
                        aria-label="Incluir peças"
                      >
                        <Plus size={10} strokeWidth={2} />
                      </button>
                    </GoHoverTooltip>
                  </div>
                  {pecasPendentes.length > 0 && (
                  <div className="checkout-view-os-detail-pecas-table-wrap checkout-view-os-detail-pecas-table-wrap-pendentes checkout-view-pecas-pendentes-wrap">
                    {isTrocarPecaLoading && (
                      <div className="checkout-view-pecas-trocar-overlay" aria-busy="true">
                        <div className="checkout-view-pecas-trocar-overlay-spinner" />
                        <span className="checkout-view-pecas-trocar-overlay-text">Processando troca...</span>
                      </div>
                    )}
                    <table className="checkout-view-pecas-table">
                      <thead>
                        <tr>
                          <th className="checkout-view-pecas-th">Código</th>
                          <th className="checkout-view-pecas-th">Referência</th>
                          <th className="checkout-view-pecas-th checkout-view-pecas-th-qtde">Qtde</th>
                          <th className="checkout-view-pecas-th checkout-view-pecas-th-actions" aria-label="Ações" />
                        </tr>
                      </thead>
                      <tbody>
                        {pecasPendentesSorted.map((row, displayIndex) => {
                          const { peca, originalIndex, numeroPedido, samePedidoCount, isFirstOfGroup } = row;
                          const i = originalIndex;
                          const isPedidoGerado = !!numeroPedido;
                          const qtdeNecessaria = parseInt(peca.qtde, 10) || 0;
                          const stockScope = getConditionStockValidationScope(selectedOs, apiConditionsByAreaTech);
                          const saldoTotal = peca.saldo != null ? parseInt(peca.saldo, 10) || 0 : null;
                          const saldoEmpresa = peca.saldoNaEmpresa != null ? parseInt(peca.saldoNaEmpresa, 10) || 0 : null;
                          const saldoValidacao = stockScope === 'os_company'
                            ? (saldoEmpresa != null ? saldoEmpresa : saldoTotal)
                            : saldoTotal;
                          const showEstoqueIndisponivel = saldoValidacao != null && saldoValidacao < qtdeNecessaria;
                          return (
                          <React.Fragment key={i}>
                            <tr className={`checkout-view-peca-tr ${isPedidoGerado ? 'checkout-view-peca-tr-pedido-gerado' : ''} ${isFirstOfGroup && displayIndex > 0 ? 'checkout-view-peca-tr-group-sep' : ''}`}>
                              <td className="checkout-view-pecas-td checkout-view-pecas-td-codigo">
                                <span className="checkout-view-pecas-cod-wrap">
                                  <span>{peca.codigo}</span>
                                  {showEstoqueIndisponivel && (
                                    <span
                                      className="checkout-view-pecas-estoque-alert-dot"
                                      onMouseEnter={(e) => {
                                        const r = e.currentTarget.getBoundingClientRect();
                                        setRefTooltip({
                                          text: 'Indisponível em estoque',
                                          x: r.left + r.width / 2,
                                          y: r.top,
                                          place: 'above'
                                        });
                                      }}
                                      onMouseLeave={() => setRefTooltip(null)}
                                    />
                                  )}
                                </span>
                              </td>
                              <td className="checkout-view-pecas-td checkout-view-pecas-td-ref">
                                <span
                                  className="checkout-view-tooltip-ref"
                                  onMouseEnter={(e) => {
                                    const text = peca.nome || peca.referencia;
                                    if (text) { const r = e.currentTarget.getBoundingClientRect(); setRefTooltip({ text, x: r.left + r.width / 2, y: r.bottom - 4 }); }
                                  }}
                                  onMouseLeave={() => setRefTooltip(null)}
                                >{peca.referencia}</span>
                              </td>
                              <td className="checkout-view-pecas-td checkout-view-pecas-td-qtde">{peca.qtde}</td>
                              {isPedidoGerado && !isFirstOfGroup ? null : (
                              <td
                                className="checkout-view-pecas-td checkout-view-pecas-td-actions"
                                rowSpan={isPedidoGerado && isFirstOfGroup ? samePedidoCount : undefined}
                              >
                                {isPedidoGerado ? (
                                  <span className="checkout-view-pecas-pedido-gerado-wrap">
                                    <GoHoverTooltip text={`Pedido ${numeroPedido}`}>
                                      <span className="checkout-view-pecas-pedido-gerado-icon-wrap">
                                        <Tag size={14} strokeWidth={2} className="checkout-view-pecas-pedido-gerado-icon" aria-hidden />
                                      </span>
                                    </GoHoverTooltip>
                                    <span className="checkout-view-pecas-pedido-gerado-badge">{numeroPedido}</span>
                                    <GoHoverTooltip text="Editar dados do pedido">
                                      <button
                                        type="button"
                                        className="checkout-view-pecas-pedido-edit-btn"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          openPedidoEdit(String(numeroPedido));
                                        }}
                                        aria-label={`Editar pedido ${numeroPedido}`}
                                      >
                                        <Pencil size={12} strokeWidth={2} aria-hidden />
                                      </button>
                                    </GoHoverTooltip>
                                  </span>
                                ) : (
                                  <>
                                    <button
                                      type="button"
                                      className={`checkout-view-pecas-action-btn ${expandedDisponibilidadeIndex === i ? 'checkout-view-pecas-action-btn-active' : ''}`}
                                      onClick={() => handleDisponibilidadeClick(i)}
                                      disabled={isLoadingDisponibilidade && expandedDisponibilidadeIndex === i}
                                      title="Disponibilidade de estoque"
                                      aria-label="Disponibilidade de estoque"
                                      aria-expanded={expandedDisponibilidadeIndex === i}
                                    >
                                      <Box size={14} strokeWidth={2} />
                                    </button>
                                    <button
                                      type="button"
                                      className={`checkout-view-pecas-action-btn ${expandedCompatibilidadeIndex === i ? 'checkout-view-pecas-action-btn-active' : ''}`}
                                      onClick={() => handleCompatibilidadeClick(i)}
                                      disabled={isLoadingCompatibilidade && expandedCompatibilidadeIndex === i}
                                      title="Ver compatibilidade"
                                      aria-label="Ver compatibilidade"
                                      aria-expanded={expandedCompatibilidadeIndex === i}
                                    >
                                      <Boxes size={14} strokeWidth={2} />
                                    </button>
                                    <button
                                      type="button"
                                      className="checkout-view-pecas-action-btn checkout-view-pecas-action-btn-danger"
                                      onClick={() => handleExcluirPeca(i)}
                                      disabled={excluirPecaLoadingIndex !== null}
                                      title="Excluir"
                                      aria-label="Excluir"
                                    >
                                      {excluirPecaLoadingIndex === i ? (
                                        <span className="checkout-view-laudo-spinner" aria-hidden />
                                      ) : (
                                        <Trash2 size={14} strokeWidth={2} />
                                      )}
                                    </button>
                                  </>
                                )}
                              </td>
                              )}
                            </tr>
                            {expandedDisponibilidadeIndex === i && (
                              <tr className="checkout-view-pecas-disponibilidade-tr">
                                <td colSpan={4} className="checkout-view-pecas-disponibilidade-td">
                                  <div className="checkout-view-pecas-disponibilidade-panel">
                                    {disponibilidadeData == null && isLoadingDisponibilidade && (
                                      <span className="checkout-view-pecas-disponibilidade-loading">Carregando...</span>
                                    )}
                                    {disponibilidadeData != null && (
                                      <div className="checkout-view-pecas-disponibilidade-grid">
                                        <div className="checkout-view-pecas-disponibilidade-item">
                                          <span className="checkout-view-pecas-disponibilidade-label">Na empresa</span>
                                          <span className="checkout-view-pecas-disponibilidade-value">{disponibilidadeData.naEmpresa}</span>
                                        </div>
                                        <div className="checkout-view-pecas-disponibilidade-item">
                                          <span className="checkout-view-pecas-disponibilidade-label">Outras empresas</span>
                                          <span className="checkout-view-pecas-disponibilidade-value">{disponibilidadeData.outrasEmpresas}</span>
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                </td>
                              </tr>
                            )}
                            {expandedCompatibilidadeIndex === i && (
                              <tr className="checkout-view-pecas-disponibilidade-tr">
                                <td colSpan={4} className="checkout-view-pecas-disponibilidade-td">
                                  <div className="checkout-view-pecas-disponibilidade-panel checkout-view-pecas-compatibilidade-panel">
                                    {compatibilidadeData == null && isLoadingCompatibilidade && (
                                      <span className="checkout-view-pecas-disponibilidade-loading">Carregando...</span>
                                    )}
                                    {compatibilidadeData != null && (
                                      <div className="checkout-view-pecas-compatibilidade-wrap">
                                        <table className="checkout-view-pecas-compatibilidade-table">
                                          <thead>
                                            <tr>
                                              <th>Cód.</th>
                                              <th>Ref.</th>
                                              <th className="checkout-view-pecas-compatibilidade-num" title="Estoque mesma empresa">Est.=</th>
                                              <th className="checkout-view-pecas-compatibilidade-num" title="Estoque outras empresas">{'Est.<>'}</th>
                                              <th className="checkout-view-pecas-compatibilidade-th-action" aria-label="Ação" />
                                            </tr>
                                          </thead>
                                          <tbody>
                                            {compatibilidadeData.length === 0 ? (
                                              <tr><td colSpan={5} className="checkout-view-pecas-compatibilidade-empty">Nenhum registro</td></tr>
                                            ) : (
                                              compatibilidadeData.map((item, idx) => (
                                                <tr key={idx}>
                                                  <td>{item.TB01101_SUPRIMENTO ?? ''}</td>
                                                  <td title={item.TB01010_NOME ?? ''}>{item.TB01010_REFERENCIA ?? ''}</td>
                                                  <td className="checkout-view-pecas-compatibilidade-num">{Number.parseInt(item.EstEmp_Mesma, 10) || 0}</td>
                                                  <td className="checkout-view-pecas-compatibilidade-num">{Number.parseInt(item.EstEmp_Outras, 10) || 0}</td>
                                                  <td className="checkout-view-pecas-compatibilidade-td-action">
                                                    <button
                                                      type="button"
                                                      className="checkout-view-pecas-trocar-btn"
                                                      onClick={() => handleTrocarPeca(i, item.TB01101_SUPRIMENTO)}
                                                      disabled={isTrocarPecaLoading}
                                                      title="Trocar peça pendente por esta"
                                                      aria-label="Trocar"
                                                    >
                                                      Trocar
                                                    </button>
                                                  </td>
                                                </tr>
                                              ))
                                            )}
                                          </tbody>
                                        </table>
                                      </div>
                                    )}
                                  </div>
                                </td>
                              </tr>
                            )}
                          </React.Fragment>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                  )}
                  {excluirPecaFeedback && (
                    <div className={`checkout-view-laudo-feedback ${excluirPecaFeedback}`} role="status">
                      {excluirPecaFeedback === 'success' ? 'Peça excluída.' : 'Erro ao excluir. Tente novamente.'}
                    </div>
                  )}
                </div>
                )}
                {pecasTrocadas.length > 0 && (
                <div className="checkout-view-os-detail-pecas">
                  <div className="checkout-view-os-detail-os-row">
                    <Wrench size={16} strokeWidth={2} />
                    <span>Peças trocadas</span>
                  </div>
                  <div className="checkout-view-os-detail-pecas-table-wrap checkout-view-os-detail-pecas-table-wrap-trocadas">
                    <table className="checkout-view-pecas-table">
                      <thead>
                        <tr>
                          <th className="checkout-view-pecas-th">Código</th>
                          <th className="checkout-view-pecas-th">Referência</th>
                          <th className="checkout-view-pecas-th checkout-view-pecas-th-qtde">Qtde</th>
                        </tr>
                      </thead>
                      <tbody>
                        {pecasTrocadas.map((peca, i) => (
                          <tr key={i} className="checkout-view-peca-tr">
                            <td className="checkout-view-pecas-td">{peca.codigo}</td>
                            <td className="checkout-view-pecas-td">
                              <span
                                className="checkout-view-tooltip-ref"
                                onMouseEnter={(e) => {
                                  const text = peca.nome || peca.referencia;
                                  if (text) { const r = e.currentTarget.getBoundingClientRect(); setRefTooltip({ text, x: r.left + r.width / 2, y: r.bottom - 4 }); }
                                }}
                                onMouseLeave={() => setRefTooltip(null)}
                              >{peca.referencia}</span>
                            </td>
                            <td className="checkout-view-pecas-td checkout-view-pecas-td-qtde">{peca.qtde}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
                )}
              </div>
              {isConcluirLoading && concluirStepMessage && (
                <div className="checkout-view-concluir-overlay" aria-live="polite" aria-busy="true">
                  <div className="checkout-view-refresh-spinner" aria-hidden />
                  <span className="checkout-view-refresh-label">{concluirStepMessage}</span>
                </div>
              )}
              {isGerarPedidoDiretoLoading && (
                <div className="checkout-view-concluir-overlay" aria-live="polite" aria-busy="true">
                  <div className="checkout-view-refresh-spinner" aria-hidden />
                  <span className="checkout-view-refresh-label">{gerarPedidoDiretoMessage || 'Gerando pedido...'}</span>
                </div>
              )}
              <div className="checkout-view-os-detail-footer">
                {reservaPendente && !showGerarPedidoModal && (
                  <div className="checkout-view-concluir-feedback warning checkout-view-gerar-pedido-reserva-footer" role="alert">
                    <AlertTriangle size={18} strokeWidth={2} aria-hidden />
                    <div className="checkout-view-gerar-pedido-reserva-body">
                      <span className="checkout-view-gerar-pedido-reserva-title">
                        Pedido {reservaPendente.pedidoCodigo || ''} gerado, mas a reserva de estoque falhou
                        {reservaPendente.tentativas > 0 ? ` (${reservaPendente.tentativas} tentativa${reservaPendente.tentativas > 1 ? 's' : ''})` : ''}.
                      </span>
                      {Array.isArray(reservaPendente.erros) && reservaPendente.erros.length > 0 && (
                        <ul className="checkout-view-gerar-pedido-reserva-erros">
                          {reservaPendente.erros.map((err, i) => (
                            <li key={`res-err-f-${i}`}>
                              {err?.message || 'Erro na reserva'}
                              {err?.number != null ? ` [#${err.number}]` : ''}
                              {err?.procedure ? ` (${err.procedure})` : ''}
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                    <button
                      type="button"
                      className="checkout-view-gerar-pedido-reserva-retry"
                      disabled={isReservarPedidoLoading}
                      onClick={handleRetryReservarPedido}
                    >
                      {isReservarPedidoLoading ? <Loader2 size={14} strokeWidth={2} className="checkout-view-gerar-pedido-reserva-spin" /> : <RefreshCw size={14} strokeWidth={2} />}
                      <span>Tentar reservar</span>
                    </button>
                    <button
                      type="button"
                      className="checkout-view-gerar-pedido-reserva-dismiss"
                      aria-label="Dispensar aviso"
                      onClick={() => setReservaPendente(null)}
                    >
                      <X size={14} strokeWidth={2} />
                    </button>
                  </div>
                )}
                {gerarPedidoSuccessMsg && !showGerarPedidoModal && !reservaPendente && (
                  <div className="checkout-view-concluir-feedback success checkout-view-gerar-pedido-success-footer" role="status">
                    <CheckCircle size={18} strokeWidth={2} aria-hidden />
                    <span>{gerarPedidoSuccessMsg}</span>
                  </div>
                )}
                {concluirFeedback === 'success' && (
                  <div className="checkout-view-concluir-feedback success" role="status">
                    <CheckCircle size={18} strokeWidth={2} aria-hidden />
                    <span>{concluirMessage || 'OS concluída com sucesso'}</span>
                  </div>
                )}
                {concluirFeedback === 'error' && (
                  <div className="checkout-view-concluir-feedback error" role="alert">
                    <X size={18} strokeWidth={2} aria-hidden />
                    <span>{concluirMessage || 'Não foi possível concluir. Tente novamente.'}</span>
                  </div>
                )}
                <div className="checkout-view-concluir-actions">
                  {(selectedOsCondFuncClose !== null
                    ? selectedOsCondFuncClose === 1 &&
                      (conditionsByAreaTech &&
                        !!getConditions(selectedOs.areaId, selectedOs.techName, conditionsByAreaTech).find(
                          (c) => c.label === selectedOs.conditionLabel && c.func_order_op
                        ))
                    : (conditionsByAreaTech &&
                        !!getConditions(selectedOs.areaId, selectedOs.techName, conditionsByAreaTech).find(
                          (c) => c.label === selectedOs.conditionLabel && c.func_close === 1 && c.func_order_op
                        ))) && (
                    <div className="checkout-view-pedido-wrap" ref={pedidoMenuRef}>
                      <span
                        className="checkout-view-pedido-btn-tooltip-wrap"
                        onMouseEnter={(e) => {
                          if (!podeGerarPedido) {
                            const r = e.currentTarget.getBoundingClientRect();
                            setRefTooltip({ text: 'Não há peças pendentes para gerar pedido.', x: r.left + r.width / 2, y: r.top, place: 'above' });
                          }
                        }}
                        onMouseLeave={() => setRefTooltip(null)}
                      >
                        <button
                          type="button"
                          className="checkout-view-gerar-pedido-btn"
                          disabled={isConcluirLoading || !podeGerarPedido}
                          onClick={() => podeGerarPedido && setPedidoMenuOpen((o) => !o)}
                          aria-expanded={pedidoMenuOpen}
                          aria-haspopup="true"
                        >
                          <Wrench size={14} strokeWidth={2} aria-hidden />
                          <span>Pedido</span>
                          <ChevronUp size={12} strokeWidth={2} className="checkout-view-pedido-chevron" aria-hidden />
                        </button>
                      </span>
                      {pedidoMenuOpen && (
                        <div className="checkout-view-pedido-dropdown" role="menu">
                          <button
                            type="button"
                            role="menuitem"
                            className="checkout-view-pedido-dropdown-item"
                            onClick={() => { setShowGerarPedidoModal(true); setPedidoMenuOpen(false); }}
                          >
                            <Columns2 size={16} strokeWidth={2} />
                            <span>Preparar pedido</span>
                          </button>
                          <span
                            className="checkout-view-pedido-dropdown-item-tooltip-wrap"
                            onMouseEnter={(e) => {
                              if (pedidosParaConcluir.length > 0) {
                                const r = e.currentTarget.getBoundingClientRect();
                                setRefTooltip({
                                  text: 'Termine de gerar os pedidos em Preparar Pedido',
                                  x: r.left + r.width / 2,
                                  y: r.top,
                                  place: 'above'
                                });
                              }
                            }}
                            onMouseLeave={() => setRefTooltip(null)}
                          >
                            <button
                              type="button"
                              role="menuitem"
                              className="checkout-view-pedido-dropdown-item"
                              onClick={() => { handleGerarPedidoDireto(); }}
                              disabled={!podeGerarPedido || isGerarPedidoDiretoLoading || pedidosParaConcluir.length > 0}
                            >
                              <PackagePlus size={16} strokeWidth={2} />
                              <span>Gerar pedido</span>
                            </button>
                          </span>
                        </div>
                      )}
                    </div>
                  )}
                  {(selectedOsCondFuncClose !== null
                    ? selectedOsCondFuncClose === 1
                    : (conditionsByAreaTech &&
                        getConditions(selectedOs.areaId, selectedOs.techName, conditionsByAreaTech).find(
                          (c) => c.label === selectedOs.conditionLabel
                        )?.func_close === 1)) && (
                    <span
                      className="checkout-view-concluir-btn-tooltip-wrap"
                      onMouseEnter={(e) => {
                        if (concluirDisabledPorPecasSemPedido) {
                          const r = e.currentTarget.getBoundingClientRect();
                          setRefTooltip({ text: 'Gere pedido para todas as peças pendentes antes de concluir.', x: r.left + r.width / 2, y: r.top, place: 'above' });
                        }
                      }}
                      onMouseLeave={() => setRefTooltip(null)}
                    >
                      <button
                        type="button"
                        className="checkout-view-concluir-btn"
                        onClick={handleConcluir}
                        disabled={isConcluirLoading || concluirDisabledPorPecasSemPedido}
                        aria-busy={isConcluirLoading}
                      >
                        {isConcluirLoading ? (
                          <span className="checkout-view-concluir-spinner" aria-hidden />
                        ) : (
                          <CheckCircle size={14} strokeWidth={2} aria-hidden />
                        )}
                        <span>{isConcluirLoading ? 'Processando...' : 'Concluir'}</span>
                      </button>
                    </span>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
          </>
        )}
      </div>

      {showGerarPedidoModal && selectedOs?.os && (
        <div
          className="checkout-view-gerar-pedido-backdrop"
          role="dialog"
          aria-modal="true"
          aria-labelledby="gerar-pedido-title"
          onClick={(e) => e.target === e.currentTarget && setShowGerarPedidoModal(false)}
        >
          <div className="checkout-view-gerar-pedido-modal" onClick={(e) => e.stopPropagation()}>
            <div className="checkout-view-gerar-pedido-header">
              <h2 id="gerar-pedido-title" className="checkout-view-gerar-pedido-title">
                Gerar pedido para a OS {selectedOs.os.numeroOS}
              </h2>
              <button
                type="button"
                className="checkout-view-gerar-pedido-close"
                onClick={() => setShowGerarPedidoModal(false)}
                aria-label="Fechar"
              >
                <X size={18} strokeWidth={2} />
              </button>
            </div>
            <div className="checkout-view-gerar-pedido-body">
              {isLoadingGerarPedidoModal ? (
                <div className="checkout-view-gerar-pedido-loading">Carregando produtos...</div>
              ) : (
                <>
                {isGerarPedidoLoading && (
                  <div className="checkout-view-gerar-pedido-progress-overlay" aria-busy="true">
                    <div className="checkout-view-gerar-pedido-progress-spinner" aria-hidden />
                    <span className="checkout-view-gerar-pedido-progress-text">Gerando pedido...</span>
                  </div>
                )}
                {reservaPendente && (
                  <div className="checkout-view-gerar-pedido-reserva-toast" role="alert">
                    <AlertTriangle size={18} strokeWidth={2} aria-hidden />
                    <div className="checkout-view-gerar-pedido-reserva-body">
                      <span className="checkout-view-gerar-pedido-reserva-title">
                        Pedido {reservaPendente.pedidoCodigo || ''} gerado; reserva de estoque falhou
                        {reservaPendente.tentativas > 0 ? ` (${reservaPendente.tentativas} tentativa${reservaPendente.tentativas > 1 ? 's' : ''})` : ''}.
                      </span>
                      {Array.isArray(reservaPendente.erros) && reservaPendente.erros.length > 0 && (
                        <ul className="checkout-view-gerar-pedido-reserva-erros">
                          {reservaPendente.erros.map((err, i) => (
                            <li key={`res-err-m-${i}`}>
                              {err?.message || 'Erro na reserva'}
                              {err?.number != null ? ` [#${err.number}]` : ''}
                              {err?.procedure ? ` (${err.procedure})` : ''}
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                    <button
                      type="button"
                      className="checkout-view-gerar-pedido-reserva-retry"
                      disabled={isReservarPedidoLoading}
                      onClick={handleRetryReservarPedido}
                    >
                      {isReservarPedidoLoading ? <Loader2 size={14} strokeWidth={2} className="checkout-view-gerar-pedido-reserva-spin" /> : <RefreshCw size={14} strokeWidth={2} />}
                      <span>Tentar reservar</span>
                    </button>
                  </div>
                )}
                {gerarPedidoSuccessMsg && !reservaPendente && (
                  <div className="checkout-view-gerar-pedido-success-toast" role="status">
                    <CheckCircle size={18} strokeWidth={2} aria-hidden />
                    <span>{gerarPedidoSuccessMsg}</span>
                  </div>
                )}
                <div className="checkout-view-gerar-pedido-columns">
                  <div
                    className="checkout-view-gerar-pedido-col"
                    onDragOver={(e) => { e.preventDefault(); e.currentTarget.classList.add('checkout-view-gerar-pedido-col-drag-over'); }}
                    onDragLeave={(e) => { e.currentTarget.classList.remove('checkout-view-gerar-pedido-col-drag-over'); }}
                    onDrop={(e) => { e.preventDefault(); e.currentTarget.classList.remove('checkout-view-gerar-pedido-col-drag-over'); handleGerarPedidoDrop('disponiveis'); }}
                  >
                    <h3 className="checkout-view-gerar-pedido-col-title">Produtos disponíveis</h3>
                    <div className="checkout-view-gerar-pedido-table-wrap">
                      <table className="checkout-view-gerar-pedido-table">
                        <thead>
                          <tr>
                            <th className="checkout-view-gerar-pedido-th checkout-view-gerar-pedido-th-check" />
                            <th className="checkout-view-gerar-pedido-th">Código</th>
                            <th className="checkout-view-gerar-pedido-th">Referência</th>
                            <th className="checkout-view-gerar-pedido-th checkout-view-gerar-pedido-th-num">Qtde</th>
                            <th className="checkout-view-gerar-pedido-th checkout-view-gerar-pedido-th-num">Saldo</th>
                            <th className="checkout-view-gerar-pedido-th checkout-view-gerar-pedido-th-action" />
                          </tr>
                        </thead>
                        <tbody>
                          {gerarPedidoDisponiveis.map((item, i) => {
                            const selectedCount = gerarPedidoSelectedDisponiveis.size;
                            const isSelected = gerarPedidoSelectedDisponiveis.has(i);
                            const showRowBtn = selectedCount === 1 && isSelected;
                            return (
                              <tr
                                key={`d-${i}-${item.peca.codigo}`}
                                className="checkout-view-gerar-pedido-tr"
                                draggable
                                onDragStart={() => setGerarPedidoDragSource({ list: 'disponiveis', index: i })}
                                onDragEnd={() => setGerarPedidoDragSource(null)}
                              >
                                <td className="checkout-view-gerar-pedido-td checkout-view-gerar-pedido-td-check">
                                  <input
                                    type="checkbox"
                                    checked={isSelected}
                                    onChange={() => toggleGerarPedidoSelection('disponiveis', i)}
                                    onClick={(e) => e.stopPropagation()}
                                    aria-label="Selecionar para gerar pedido"
                                  />
                                </td>
                                <td className="checkout-view-gerar-pedido-td">{item.peca.codigo}</td>
                                <td className="checkout-view-gerar-pedido-td checkout-view-gerar-pedido-td-ref">
                                  <span
                                    className="checkout-view-tooltip-ref"
                                    onMouseEnter={(e) => {
                                      const text = item.peca.nome || item.peca.referencia;
                                      if (text) { const r = e.currentTarget.getBoundingClientRect(); setRefTooltip({ text, x: r.left + r.width / 2, y: r.bottom - 4 }); }
                                    }}
                                    onMouseLeave={() => setRefTooltip(null)}
                                  >{item.peca.referencia}</span>
                                </td>
                                <td className="checkout-view-gerar-pedido-td checkout-view-gerar-pedido-td-num">{item.peca.qtde}</td>
                                <td className="checkout-view-gerar-pedido-td checkout-view-gerar-pedido-td-num">{item.saldo}</td>
                                <td className="checkout-view-gerar-pedido-td checkout-view-gerar-pedido-td-action">
                                  {showRowBtn && (
                                    <button type="button" className="checkout-view-gerar-pedido-row-btn" onClick={() => handleGerarPedidoClick('disponiveis')}>
                                      <PackagePlus size={12} strokeWidth={2} />
                                      <span>Gerar pedido</span>
                                    </button>
                                  )}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                    {gerarPedidoSelectedDisponiveis.size > 1 && (
                      <div className="checkout-view-gerar-pedido-col-footer">
                        <button type="button" className="checkout-view-gerar-pedido-row-btn checkout-view-gerar-pedido-footer-btn" onClick={() => handleGerarPedidoClick('disponiveis')}>
                          <PackagePlus size={12} strokeWidth={2} />
                          <span>Gerar pedido</span>
                        </button>
                      </div>
                    )}
                  </div>
                  <div
                    className="checkout-view-gerar-pedido-col"
                    onDragOver={(e) => { e.preventDefault(); e.currentTarget.classList.add('checkout-view-gerar-pedido-col-drag-over'); }}
                    onDragLeave={(e) => { e.currentTarget.classList.remove('checkout-view-gerar-pedido-col-drag-over'); }}
                    onDrop={(e) => { e.preventDefault(); e.currentTarget.classList.remove('checkout-view-gerar-pedido-col-drag-over'); handleGerarPedidoDrop('indisponiveis'); }}
                  >
                    <h3 className="checkout-view-gerar-pedido-col-title">Produtos indisponíveis</h3>
                    <div className="checkout-view-gerar-pedido-table-wrap">
                      <table className="checkout-view-gerar-pedido-table">
                        <thead>
                          <tr>
                            <th className="checkout-view-gerar-pedido-th checkout-view-gerar-pedido-th-check" />
                            <th className="checkout-view-gerar-pedido-th">Código</th>
                            <th className="checkout-view-gerar-pedido-th">Referência</th>
                            <th className="checkout-view-gerar-pedido-th checkout-view-gerar-pedido-th-num">Qtde</th>
                            <th className="checkout-view-gerar-pedido-th checkout-view-gerar-pedido-th-num">Saldo</th>
                            <th className="checkout-view-gerar-pedido-th checkout-view-gerar-pedido-th-action" />
                          </tr>
                        </thead>
                        <tbody>
                          {gerarPedidoIndisponiveis.map((item, i) => {
                            const selectedCount = gerarPedidoSelectedIndisponiveis.size;
                            const isSelected = gerarPedidoSelectedIndisponiveis.has(i);
                            const showRowBtn = selectedCount === 1 && isSelected;
                            return (
                              <tr
                                key={`i-${i}-${item.peca.codigo}`}
                                className="checkout-view-gerar-pedido-tr"
                                draggable
                                onDragStart={() => setGerarPedidoDragSource({ list: 'indisponiveis', index: i })}
                                onDragEnd={() => setGerarPedidoDragSource(null)}
                              >
                                <td className="checkout-view-gerar-pedido-td checkout-view-gerar-pedido-td-check">
                                  <input
                                    type="checkbox"
                                    checked={isSelected}
                                    onChange={() => toggleGerarPedidoSelection('indisponiveis', i)}
                                    onClick={(e) => e.stopPropagation()}
                                    aria-label="Selecionar para gerar pedido"
                                  />
                                </td>
                                <td className="checkout-view-gerar-pedido-td">{item.peca.codigo}</td>
                                <td className="checkout-view-gerar-pedido-td checkout-view-gerar-pedido-td-ref">
                                  <span
                                    className="checkout-view-tooltip-ref"
                                    onMouseEnter={(e) => {
                                      const text = item.peca.nome || item.peca.referencia;
                                      if (text) { const r = e.currentTarget.getBoundingClientRect(); setRefTooltip({ text, x: r.left + r.width / 2, y: r.bottom - 4 }); }
                                    }}
                                    onMouseLeave={() => setRefTooltip(null)}
                                  >{item.peca.referencia}</span>
                                </td>
                                <td className="checkout-view-gerar-pedido-td checkout-view-gerar-pedido-td-num">{item.peca.qtde}</td>
                                <td className="checkout-view-gerar-pedido-td checkout-view-gerar-pedido-td-num">{item.saldo}</td>
                                <td className="checkout-view-gerar-pedido-td checkout-view-gerar-pedido-td-action">
                                  {showRowBtn && (
                                    <button type="button" className="checkout-view-gerar-pedido-row-btn" onClick={() => handleGerarPedidoClick('indisponiveis')}>
                                      <PackagePlus size={12} strokeWidth={2} />
                                      <span>Gerar pedido</span>
                                    </button>
                                  )}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                    {gerarPedidoSelectedIndisponiveis.size > 1 && (
                      <div className="checkout-view-gerar-pedido-col-footer">
                        <button type="button" className="checkout-view-gerar-pedido-row-btn checkout-view-gerar-pedido-footer-btn" onClick={() => handleGerarPedidoClick('indisponiveis')}>
                          <PackagePlus size={12} strokeWidth={2} />
                          <span>Gerar pedido</span>
                        </button>
                      </div>
                    )}
                  </div>
                </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {pedidoEditModal.open && (
        <div className="checkout-view-pedido-edit-backdrop" role="presentation">
          <div
            className="checkout-view-pedido-edit-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="pedido-edit-title"
          >
            <div className="checkout-view-pedido-edit-header">
              <div>
                <h3 id="pedido-edit-title" className="checkout-view-pedido-edit-title">Editar pedido {pedidoEditModal.codPedido}</h3>
                <p className="checkout-view-pedido-edit-sub">OS {pedidoEditModal.numeroOS}</p>
              </div>
              <button
                type="button"
                className="checkout-view-pedido-edit-close"
                onClick={closePedidoEdit}
                disabled={pedidoEditModal.saving}
                aria-label="Fechar"
              >
                <X size={18} strokeWidth={2} aria-hidden />
              </button>
            </div>
            <div className="checkout-view-pedido-edit-body">
              {pedidoEditModal.loading && (
                <div className="checkout-view-pedido-edit-loading" role="status">
                  <Loader2 className="checkout-view-pedido-edit-loader" size={28} strokeWidth={2} aria-hidden />
                  <span>Carregando dados...</span>
                </div>
              )}
              {!pedidoEditModal.loading && pedidoEditModal.loadError && (
                <div className="checkout-view-pedido-edit-banner checkout-view-pedido-edit-banner--error" role="alert">
                  {pedidoEditModal.loadError}
                </div>
              )}
              {!pedidoEditModal.loading && !pedidoEditModal.loadError && (
                <>
                  <div className="checkout-view-pedido-edit-grid">
                    <label className="checkout-view-pedido-edit-field">
                      <span className="checkout-view-pedido-edit-label">Solicitante</span>
                      <input
                        type="text"
                        className="checkout-view-pedido-edit-input"
                        value={pedidoEditModal.solicitante}
                        onChange={(e) => setPedidoEditModal((p) => ({ ...p, solicitante: e.target.value }))}
                        disabled={pedidoEditModal.saving}
                        autoComplete="off"
                      />
                    </label>
                    <label className="checkout-view-pedido-edit-field">
                      <span className="checkout-view-pedido-edit-label">Telefone</span>
                      <input
                        ref={pedidoEditFoneInputRef}
                        type="text"
                        inputMode="numeric"
                        className="checkout-view-pedido-edit-input"
                        value={pedidoEditModal.foneDisplay}
                        onChange={(e) =>
                          setPedidoEditModal((p) => ({
                            ...p,
                            foneDisplay: formatPhoneBrDisplay(e.target.value)
                          }))
                        }
                        disabled={pedidoEditModal.saving}
                        placeholder="(00) 00000-0000"
                        autoComplete="off"
                      />
                    </label>
                    <label className="checkout-view-pedido-edit-field checkout-view-pedido-edit-field--full">
                      <span className="checkout-view-pedido-edit-label">E-mail</span>
                      <input
                        type="email"
                        className="checkout-view-pedido-edit-input"
                        value={pedidoEditModal.email}
                        onChange={(e) => setPedidoEditModal((p) => ({ ...p, email: e.target.value }))}
                        disabled={pedidoEditModal.saving}
                        autoComplete="off"
                      />
                    </label>
                    <div className="checkout-view-pedido-edit-field checkout-view-pedido-edit-field--full" ref={pedidoEditTranspWrapRef}>
                      <span className="checkout-view-pedido-edit-label">Transportadora</span>
                      <div className="checkout-view-condicao-edit-wrap checkout-view-pedido-edit-autocomplete">
                        <input
                          type="text"
                          className="checkout-view-condicao-input"
                          value={pedidoEditModal.transpInput}
                          onChange={(e) => {
                            setPedidoTranspTouched(true);
                            setPedidoEditModal((p) => ({
                              ...p,
                              transpInput: e.target.value,
                              transpPickNome: null,
                              transpPickCodigo: null
                            }));
                          }}
                          disabled={pedidoEditModal.saving}
                          placeholder="Digite ao menos 4 caracteres"
                          autoComplete="off"
                        />
                        {!pedidoEditModal.saving && pedidoEditModal.transpSearchLoading && (
                          <div className="checkout-view-condicao-search-loading" role="status">Buscando...</div>
                        )}
                        {!pedidoEditModal.saving &&
                          !pedidoEditModal.transpSearchLoading &&
                          pedidoEditModal.transpResults.length > 0 && (
                          <ul className="checkout-view-condicao-dropdown" role="listbox">
                            {pedidoEditModal.transpResults.map((r) => (
                              <li key={r.codigo}>
                                <button
                                  type="button"
                                  className="checkout-view-condicao-option"
                                  onClick={() => {
                                    pedidoTranspSearchGenRef.current += 1;
                                    setPedidoEditModal((p) => ({
                                      ...p,
                                      transpInput: r.nome,
                                      transpPickNome: r.nome,
                                      transpPickCodigo: r.codigo,
                                      transpResults: [],
                                      transpSearchLoading: false
                                    }));
                                  }}
                                >
                                  {r.nome}
                                </button>
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>
                    </div>
                    <div className="checkout-view-pedido-edit-field checkout-view-pedido-edit-field--full" ref={pedidoEditClassifWrapRef}>
                      <span className="checkout-view-pedido-edit-label">Classificação</span>
                      <div className="checkout-view-condicao-edit-wrap checkout-view-pedido-edit-autocomplete">
                        <input
                          type="text"
                          className="checkout-view-condicao-input"
                          value={pedidoEditModal.classifInput}
                          onChange={(e) => {
                            setPedidoClassifTouched(true);
                            setPedidoEditModal((p) => ({
                              ...p,
                              classifInput: e.target.value,
                              classifPickNome: null,
                              classifPickCodigo: null
                            }));
                          }}
                          disabled={pedidoEditModal.saving}
                          placeholder="Digite ao menos 4 caracteres"
                          autoComplete="off"
                        />
                        {!pedidoEditModal.saving && pedidoEditModal.classifSearchLoading && (
                          <div className="checkout-view-condicao-search-loading" role="status">Buscando...</div>
                        )}
                        {!pedidoEditModal.saving &&
                          !pedidoEditModal.classifSearchLoading &&
                          pedidoEditModal.classifResults.length > 0 && (
                          <ul className="checkout-view-condicao-dropdown" role="listbox">
                            {pedidoEditModal.classifResults.map((r) => (
                              <li key={r.codigo}>
                                <button
                                  type="button"
                                  className="checkout-view-condicao-option"
                                  onClick={() => {
                                    pedidoClassifSearchGenRef.current += 1;
                                    setPedidoEditModal((p) => ({
                                      ...p,
                                      classifInput: r.nome,
                                      classifPickNome: r.nome,
                                      classifPickCodigo: r.codigo,
                                      classifResults: [],
                                      classifSearchLoading: false
                                    }));
                                  }}
                                >
                                  {r.nome}
                                </button>
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>
                    </div>
                    <label className="checkout-view-pedido-edit-field checkout-view-pedido-edit-field--full">
                      <span className="checkout-view-pedido-edit-label">Observação para NFe</span>
                      <textarea
                        className="checkout-view-pedido-edit-textarea"
                        rows={3}
                        value={pedidoEditModal.obsNfe}
                        onChange={(e) => setPedidoEditModal((p) => ({ ...p, obsNfe: e.target.value }))}
                        disabled={pedidoEditModal.saving}
                      />
                    </label>
                    <label className="checkout-view-pedido-edit-field checkout-view-pedido-edit-field--full">
                      <span className="checkout-view-pedido-edit-label">Observação</span>
                      <textarea
                        className="checkout-view-pedido-edit-textarea"
                        rows={2}
                        value={pedidoEditModal.observacao}
                        onChange={(e) => setPedidoEditModal((p) => ({ ...p, observacao: e.target.value }))}
                        disabled={pedidoEditModal.saving}
                      />
                    </label>
                    <label className="checkout-view-pedido-edit-field checkout-view-pedido-edit-field--full">
                      <span className="checkout-view-pedido-edit-label">Observação interna</span>
                      <textarea
                        className="checkout-view-pedido-edit-textarea"
                        rows={2}
                        value={pedidoEditModal.obsInt}
                        onChange={(e) => setPedidoEditModal((p) => ({ ...p, obsInt: e.target.value }))}
                        disabled={pedidoEditModal.saving}
                      />
                    </label>
                  </div>
                  {pedidoEditModal.saveError && (
                    <div className="checkout-view-pedido-edit-banner checkout-view-pedido-edit-banner--error" role="alert">
                      {pedidoEditModal.saveError}
                    </div>
                  )}
                  {pedidoEditModal.saveSuccess && (
                    <div className="checkout-view-pedido-edit-banner checkout-view-pedido-edit-banner--ok" role="status">
                      <CheckCircle size={18} strokeWidth={2} aria-hidden /> Pedido atualizado com sucesso.
                    </div>
                  )}
                </>
              )}
            </div>
            {!pedidoEditModal.loading && !pedidoEditModal.loadError && (
              <div className="checkout-view-pedido-edit-footer">
                <button
                  type="button"
                  className="checkout-view-pedido-edit-btn-cancel"
                  onClick={closePedidoEdit}
                  disabled={pedidoEditModal.saving}
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  className="checkout-view-pedido-edit-btn-confirm"
                  onClick={handlePedidoEditConfirm}
                  disabled={pedidoEditModal.saving || pedidoEditModal.saveSuccess}
                >
                  {pedidoEditModal.saving ? (
                    <>
                      <Loader2 className="checkout-view-pedido-edit-btn-spinner" size={16} strokeWidth={2} aria-hidden />
                      <span>Salvando...</span>
                    </>
                  ) : (
                    <>
                      <Check size={16} strokeWidth={2} aria-hidden />
                      <span>Confirmar</span>
                    </>
                  )}
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {reincidenciaModal.show && (
        <div className="checkout-view-gerar-pedido-backdrop checkout-view-reincidencia-backdrop" role="dialog" aria-modal="true" aria-labelledby="reincidencia-title">
          <div className="checkout-view-gerar-pedido-error-modal checkout-view-reincidencia-modal" onClick={(e) => e.stopPropagation()}>
            <div className="checkout-view-gerar-pedido-error-header">
              <h3 id="reincidencia-title" className="checkout-view-gerar-pedido-error-title checkout-view-reincidencia-title">
                {reincidenciaModal.nativeReturn ? `Retorno ${reincidenciaModal.nativeReturn.returnCount}` : 'Nova OS de reincidência'}
              </h3>
            </div>
            <div className="checkout-view-gerar-pedido-error-body">
              <p className="checkout-view-gerar-pedido-error-msg">
                {reincidenciaModal.nativeReturn ? (
                  <>
                    A OS <strong>{reincidenciaModal.novaOs}</strong> voltou como retorno (ciclo {reincidenciaModal.nativeReturn.cycleNo}), com o mesmo número.
                  </>
                ) : (
                  <>
                    A nova ordem de serviço de reincidência é <strong>{reincidenciaModal.novaOs}</strong>.
                  </>
                )}
              </p>
              {reincidenciaModal.endereco && (
                <div className="checkout-view-reincidencia-endereco-wrap">
                  <p className="checkout-view-reincidencia-endereco">{reincidenciaModal.endereco}</p>
                </div>
              )}
              {roteirizarMessage && (
                <p className="checkout-view-reincidencia-roteirizar-msg">{roteirizarMessage}</p>
              )}
              {reincidenciaModal.askService ? (
                <CheckoutCycleServiceField numeroOS={reincidenciaModal.novaOs} onSaved={setCycleServiceCode} />
              ) : null}
            </div>
            <div className="checkout-view-reincidencia-footer">
              <button
                type="button"
                className="checkout-view-roteirizar-btn"
                onClick={handleTentarRoteirizar}
                disabled={roteirizarLoading || !reincidenciaModal.novaOs?.trim() || (reincidenciaModal.askService && !cycleServiceCode)}
                title="Programar atendimento da OS de reincidência"
              >
                {roteirizarLoading ? (
                  <span className="checkout-view-roteirizar-spinner" aria-hidden />
                ) : (
                  <MapIcon size={14} strokeWidth={2} aria-hidden />
                )}
                <span>{roteirizarLoading ? 'Programando...' : 'Tentar roteirizar'}</span>
              </button>
              <button type="button" className="checkout-view-concluir-btn" onClick={handleReincidenciaConfirm} disabled={roteirizarLoading || (reincidenciaModal.askService && !cycleServiceCode)}>
                <CheckCircle size={14} strokeWidth={2} aria-hidden />
                <span>Entendi</span>
              </button>
            </div>
          </div>
        </div>
      )}

      <BoardEncaminharAgendaOverlay
        open={reincidenciaModal.show && roteirizarAgendaOpen}
        orderIds={reincidenciaOrderIds}
        scheduleOriginAddress={reincidenciaModal.endereco || ''}
        hideApenasTecnico
        onClose={() => setRoteirizarAgendaOpen(false)}
        onConfirm={handleRoteirizarAgendaConfirm}
      />

      {gerarPedidoError && (
        <div className="checkout-view-gerar-pedido-backdrop checkout-view-gerar-pedido-error-backdrop" role="alertdialog" aria-modal="true" onClick={() => setGerarPedidoError(null)}>
          <div className="checkout-view-gerar-pedido-error-modal" onClick={(e) => e.stopPropagation()}>
            <div className="checkout-view-gerar-pedido-error-header">
              <h3 className="checkout-view-gerar-pedido-error-title">Erro ao gerar pedido</h3>
              <button type="button" className="checkout-view-gerar-pedido-close" onClick={() => setGerarPedidoError(null)} aria-label="Fechar">
                <X size={18} strokeWidth={2} />
              </button>
            </div>
            <div className="checkout-view-gerar-pedido-error-body">
              <p className="checkout-view-gerar-pedido-error-msg">{gerarPedidoError.message || 'Erro desconhecido'}</p>
              {gerarPedidoError.number != null && (
                <dl className="checkout-view-gerar-pedido-error-dl">
                  <dt>Número</dt><dd>{gerarPedidoError.number}</dd>
                  {gerarPedidoError.severity != null && (<><dt>Severidade</dt><dd>{gerarPedidoError.severity}</dd></>)}
                  {gerarPedidoError.state != null && (<><dt>Estado</dt><dd>{gerarPedidoError.state}</dd></>)}
                  {gerarPedidoError.procedure && (<><dt>Procedure</dt><dd>{gerarPedidoError.procedure}</dd></>)}
                  {gerarPedidoError.line != null && (<><dt>Linha</dt><dd>{gerarPedidoError.line}</dd></>)}
                </dl>
              )}
            </div>
          </div>
        </div>
      )}

      {showAlterarStatusModal && (
        <div className="checkout-view-alterar-status-backdrop" role="dialog" aria-modal="true" aria-labelledby="alterar-status-title">
          <div className="checkout-view-alterar-status-modal">
            <div className="checkout-view-alterar-status-header">
              <h2 id="alterar-status-title" className="checkout-view-alterar-status-title">Altere o status</h2>
            </div>
            <div className="checkout-view-alterar-status-body">
              {isOpcoesStatusLoading ? (
                <div className="checkout-view-alterar-status-loading">Carregando opções...</div>
              ) : (
                <label className="checkout-view-alterar-status-label">
                  <span className="checkout-view-alterar-status-label-text">Novo status</span>
                  <AlterarStatusSelect
                    value={selectedCodStatusConclusao}
                    onChange={setSelectedCodStatusConclusao}
                    options={opcoesStatusConclusao}
                    disabled={alterarStatusLoading}
                  />
                </label>
              )}
            </div>
            <div className="checkout-view-alterar-status-footer">
              {alterarStatusFeedback === 'success' && (
                <div className="checkout-view-alterar-status-feedback success" role="status">
                  <CheckCircle size={18} strokeWidth={2} />
                  <span>Status alterado com sucesso</span>
                </div>
              )}
              {alterarStatusFeedback === 'error' && (
                <div className="checkout-view-alterar-status-feedback error" role="alert">
                  <X size={18} strokeWidth={2} />
                  <span>Não foi possível alterar. Tente novamente.</span>
                </div>
              )}
              <div className="checkout-view-alterar-status-actions">
                <button
                  type="button"
                  className="checkout-view-alterar-status-btn-confirm"
                  onClick={handleConfirmarAlterarStatus}
                  disabled={alterarStatusLoading || !selectedCodStatusConclusao}
                  aria-busy={alterarStatusLoading}
                >
                  {alterarStatusLoading ? (
                    <span className="checkout-view-alterar-status-spinner" aria-hidden />
                  ) : alterarStatusFeedback === 'success' ? (
                    <CheckCircle size={18} strokeWidth={2} />
                  ) : null}
                  <span>{alterarStatusLoading ? 'Processando...' : alterarStatusFeedback === 'success' ? 'Concluído' : 'Confirmar'}</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {contextMenu && (
        <div
          ref={contextMenuRef}
          className="checkout-view-context-menu"
          style={{ left: contextMenu.x, top: contextMenu.y }}
          onClick={(e) => e.stopPropagation()}
        >
          {contextMenu.isSelected ? (
            <button
              type="button"
              className="checkout-view-context-menu-item"
              onClick={() => handleRemoveSelection(contextMenu.areaId)}
            >
              Retirar seleção
            </button>
          ) : (
            <button
              type="button"
              className="checkout-view-context-menu-item"
              onClick={() => handleSelectAlso(contextMenu.areaId)}
            >
              Selecionar também
            </button>
          )}
        </div>
      )}

      {showIncluirPecasModal && selectedOs && (
        <div className="modal-overlay" style={{ zIndex: 10006 }} onClick={() => !isIncluirPecasLoading && setShowIncluirPecasModal(false)}>
          <div className="modal-history modal-history-wide modal-incluir-pecas modal-incluir-pecas-wrap" onClick={(e) => e.stopPropagation()}>
            {isIncluirPecasLoading && (
              <div className="incluir-pecas-loading-overlay" aria-busy="true">
                <div className="incluir-pecas-loading-spinner" />
                <span className="incluir-pecas-loading-text">Processando...</span>
              </div>
            )}
            <div className="modal-history-header">
              <div className="modal-header-info">
                <h3 className="modal-title">
                  Incluir peças pendentes para ordem de serviço {selectedOs.os?.numeroOS ?? '—'}
                </h3>
              </div>
              <div className="modal-incluir-pecas-header-actions">
                <input
                  type="text"
                  className="modal-incluir-pecas-search"
                  placeholder="Buscar na tabela..."
                  value={incluirPecasSearchValue}
                  onChange={(e) => setIncluirPecasSearchValue(e.target.value)}
                  aria-label="Buscar na tabela"
                />
                <button
                  type="button"
                  className="modal-close-button"
                  onClick={() => setShowIncluirPecasModal(false)}
                >
                  ✕
                </button>
              </div>
            </div>
            <div className="modal-content">
              <div className="ordens-content">
                <div className="ordens-table-wrapper">
                  {isLoadingPecasCompativelModelo ? (
                    <div className="table-summary">
                      <span className="summary-label">Carregando...</span>
                    </div>
                  ) : (
                    (() => {
                      const q = (incluirPecasSearchValue || '').trim().toLowerCase();
                      const matches = (row) => {
                        if (!q) return true;
                        const cod = String(row.TB01010_CODIGO ?? '').toLowerCase();
                        const ref = String(row.TB01010_REFERENCIA ?? '').toLowerCase();
                        const nome = String(row.TB01010_NOME ?? '').toLowerCase();
                        const marca = String(row.TB01047_NOME ?? '').toLowerCase();
                        const est = String(row.ESTOQUE ?? '').toLowerCase();
                        return cod.includes(q) || ref.includes(q) || nome.includes(q) || marca.includes(q) || est.includes(q);
                      };
                      const fieldBySort = { codigo: 'TB01010_CODIGO', referencia: 'TB01010_REFERENCIA', produto: 'TB01010_NOME', marca: 'TB01047_NOME' };
                      let filteredWithIdx = pecasCompativelModeloData
                        .map((row, originalIdx) => ({ row, originalIdx }))
                        .filter(({ row }) => matches(row));
                      if (incluirPecasSortBy && fieldBySort[incluirPecasSortBy]) {
                        const field = fieldBySort[incluirPecasSortBy];
                        const dir = incluirPecasSortDir === 'asc' ? 1 : -1;
                        filteredWithIdx = [...filteredWithIdx].sort((a, b) => {
                          const va = String(a.row[field] ?? '').trim().toLowerCase();
                          const vb = String(b.row[field] ?? '').trim().toLowerCase();
                          return dir * va.localeCompare(vb, undefined, { numeric: true });
                        });
                      }
                      const handleSort = (col) => {
                        if (incluirPecasSortBy === col) {
                          setIncluirPecasSortDir((prev) => (prev === 'asc' ? 'desc' : 'asc'));
                        } else {
                          setIncluirPecasSortBy(col);
                          setIncluirPecasSortDir('asc');
                        }
                      };
                      const SortTh = ({ col, label }) => (
                        <th className="incluir-pecas-sort-th">
                          <button
                            type="button"
                            className="incluir-pecas-sort-btn"
                            onClick={() => handleSort(col)}
                            aria-label={incluirPecasSortBy === col ? `Ordenar ${incluirPecasSortDir === 'asc' ? 'descendente' : 'ascendente'}` : `Ordenar por ${label}`}
                          >
                            <span>{label}</span>
                            {incluirPecasSortBy === col && <span className="incluir-pecas-sort-arrow" aria-hidden>{incluirPecasSortDir === 'asc' ? ' ▲' : ' ▼'}</span>}
                          </button>
                        </th>
                      );
                      return (
                    <table className="ordens-table">
                      <thead>
                        <tr>
                          <SortTh col="codigo" label="Código" />
                          <SortTh col="referencia" label="Referência" />
                          <SortTh col="produto" label="Produto" />
                          <SortTh col="marca" label="Marca" />
                          <th className="incluir-pecas-th-estoque">Estoque</th>
                          <th aria-label="Adicionar" />
                        </tr>
                      </thead>
                      <tbody>
                        {pecasCompativelModeloData.length === 0 ? (
                          <tr>
                            <td colSpan={6} style={{ textAlign: 'center', padding: '16px' }}>
                              Nenhuma peça compatível encontrada para o modelo do equipamento.
                            </td>
                          </tr>
                        ) : filteredWithIdx.length === 0 ? (
                          <tr>
                            <td colSpan={6} style={{ textAlign: 'center', padding: '16px' }}>
                              Nenhum registro corresponde à busca.
                            </td>
                          </tr>
                        ) : (
                          filteredWithIdx.map(({ row, originalIdx }) => (
                            <tr
                              key={originalIdx}
                              className={selectedPecasIncluir.has(originalIdx) ? 'incluir-pecas-row-selected' : ''}
                            >
                              <td>{row.TB01010_CODIGO ?? '—'}</td>
                              <td>{row.TB01010_REFERENCIA ?? '—'}</td>
                              <td>{row.TB01010_NOME ?? '—'}</td>
                              <td>{row.TB01047_NOME ?? '—'}</td>
                              <td className="incluir-pecas-td-estoque">{row.ESTOQUE ?? 0}</td>
                              <td className="incluir-pecas-td-action">
                                {incluirPecaQuantityEditIndex === originalIdx ? (
                                  <div className="incluir-pecas-qty-edit">
                                    <input
                                      type="text"
                                      inputMode="numeric"
                                      className="incluir-pecas-qty-input"
                                      value={incluirPecaQuantityDraft}
                                      onChange={(e) => setIncluirPecaQuantityDraft((e.target.value || '').replace(/\D/g, ''))}
                                      onKeyDown={(e) => {
                                        if (e.key === 'Enter') confirmIncluirPecaQuantity(originalIdx);
                                        if (e.key === 'Escape') cancelIncluirPecaQuantity();
                                      }}
                                      aria-label="Quantidade"
                                    />
                                    <button type="button" className="incluir-pecas-qty-btn incluir-pecas-qty-confirm" title="Confirmar" aria-label="Confirmar" onClick={() => confirmIncluirPecaQuantity(originalIdx)}>
                                      <Check size={14} strokeWidth={2} />
                                    </button>
                                    <button type="button" className="incluir-pecas-qty-btn incluir-pecas-qty-cancel" title="Cancelar" aria-label="Cancelar" onClick={cancelIncluirPecaQuantity}>
                                      <X size={14} strokeWidth={2} />
                                    </button>
                                  </div>
                                ) : selectedPecasIncluir.has(originalIdx) ? (
                                  <>
                                    <span className="incluir-pecas-qty-badge">{incluirPecaQuantities[originalIdx] ?? 1}</span>
                                    <button type="button" className="icon-button" title="Desfazer seleção" aria-label="Desfazer seleção" onClick={() => togglePecaIncluirSelection(originalIdx)}>
                                      <X size={16} strokeWidth={2} />
                                    </button>
                                  </>
                                ) : (
                                  <button type="button" className="icon-button" title="Adicionar" aria-label="Adicionar" onClick={() => startIncluirPecaQuantityEdit(originalIdx)}>
                                    <Plus size={16} strokeWidth={2} />
                                  </button>
                                )}
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                      );
                    })()
                  )}
                </div>
                <div className="table-summary">
                  <button
                    type="button"
                    className="checkout-view-incluir-pecas-confirm-btn"
                    disabled={selectedPecasIncluir.size === 0 || isIncluirPecasLoading}
                    onClick={handleIncluirPecasConfirm}
                  >
                    {isIncluirPecasLoading ? (
                      <span className="checkout-view-laudo-spinner" aria-hidden />
                    ) : (
                      <Plus size={14} strokeWidth={2} />
                    )}
                    <span>{isIncluirPecasLoading ? 'Processando...' : 'Incluir'}</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {selectedOs ? (
        <OpenOsV2EquipmentHistoryOverlay
          isOpen={showAtendimentosModal}
          onClose={() => setShowAtendimentosModal(false)}
          serie={selectedOs.os?.numeroSerie || ''}
          modelo={selectedOs.os?.modeloEquipamento || ''}
          patrimonio={selectedOs.os?.patrimonio || ''}
          zIndex={100050}
        />
      ) : null}

      {showEnderecoMapModal && selectedOs?.os?.endereco && (() => {
        const endereco = selectedOs.os.endereco.trim();
        let enderecoCompleto = endereco;
        if (!enderecoCompleto.toLowerCase().includes('brasil') && !enderecoCompleto.toLowerCase().includes('brazil')) {
          enderecoCompleto += ', Brasil';
        }
        const mapsEmbedUrl = `https://www.google.com/maps?q=${encodeURIComponent(enderecoCompleto)}&output=embed`;
        return (
          <div className="modal-overlay checkout-view-endereco-map-overlay" style={{ zIndex: 10007 }} role="dialog" aria-modal="true" aria-labelledby="checkout-view-endereco-map-title">
            <div className="checkout-view-endereco-map-modal" style={{ width: '70vw', height: '70vh' }} onClick={(e) => e.stopPropagation()}>
              <div className="checkout-view-endereco-map-header">
                <div className="checkout-view-endereco-map-header-info">
                  <span className="checkout-view-endereco-map-os">OS {selectedOs.os.numeroOS}</span>
                  <span className="checkout-view-endereco-map-cliente">{selectedOs.os.cliente || '—'}</span>
                </div>
                <button
                  type="button"
                  className="modal-close-button"
                  onClick={() => setShowEnderecoMapModal(false)}
                  aria-label="Fechar"
                >
                  ✕
                </button>
              </div>
              <h3 id="checkout-view-endereco-map-title" className="checkout-view-endereco-map-title">{endereco}</h3>
              <div className="checkout-view-endereco-map-iframe-wrap">
                <iframe title="Endereço no Google Maps" src={mapsEmbedUrl} className="checkout-view-endereco-map-iframe" />
              </div>
            </div>
          </div>
        );
      })()}

      {showOsRastreioModal && selectedOs && osRastreio?.hasTrack ? (
        <CheckoutViewOsRastreioModal
          open={showOsRastreioModal}
          onClose={() => setShowOsRastreioModal(false)}
          os={selectedOs.os}
          trackData={osRastreio}
        />
      ) : null}

      {showFotosModal && selectedOs && (
        <div className="modal-overlay" style={{ zIndex: 10006 }} onClick={() => setShowFotosModal(false)}>
          <div className="checkout-view-fotos-modal" onClick={(e) => e.stopPropagation()}>
            <div className="checkout-view-fotos-header">
              <div className="checkout-view-fotos-header-info">
                <h3 className="checkout-view-fotos-title">Fotos da OS {selectedOs.os.numeroOS}</h3>
                <div className="checkout-view-fotos-subtitle">
                  <span>{selectedOs.os.modeloEquipamento || '—'}</span>
                  <span>•</span>
                  <span>Série: {selectedOs.os.numeroSerie || '—'}</span>
                </div>
              </div>
              <button
                type="button"
                className="modal-close-button"
                onClick={() => setShowFotosModal(false)}
              >
                ✕
              </button>
            </div>
            <div className="checkout-view-fotos-body checkout-view-fotos-body--gallery">
              {fotos.length === 0 ? (
                <div className="checkout-view-fotos-empty">
                  <span>Nenhuma foto encontrada para esta OS.</span>
                </div>
              ) : (
                <div className="checkout-view-fotos-layout">
                  <div className="checkout-view-fotos-thumbs">
                    {fotos.map((foto, idx) => {
                      const videoThumb = isCheckoutMediaVideo(foto);
                      return (
                      <button
                        key={idx}
                        type="button"
                        className={`checkout-view-foto-thumb ${idx === selectedFotoIdx ? 'active' : ''}${videoThumb ? ' checkout-view-foto-thumb--video' : ''}`}
                        onClick={() => setSelectedFotoIdx(idx)}
                        title={foto.operacao || (videoThumb ? `Vídeo ${idx + 1}` : `Foto ${idx + 1}`)}
                      >
                        {videoThumb ? (
                          <span className="checkout-view-foto-thumb-video" aria-hidden>
                            <Play size={18} strokeWidth={2.2} />
                          </span>
                        ) : (
                          <img
                            src={resolveCheckoutFotoSrc(foto)}
                            alt={foto.operacao || `Foto ${idx + 1}`}
                          />
                        )}
                      </button>
                      );
                    })}
                  </div>
                  <div className="checkout-view-foto-main">
                    <figure className="checkout-view-foto-item">
                      <div className="checkout-view-foto-wrapper">
                        {isCheckoutMediaVideo(fotos[selectedFotoIdx]) ? (
                          <video
                            key={resolveCheckoutFotoSrc(fotos[selectedFotoIdx])}
                            className="checkout-view-foto-img checkout-view-foto-video"
                            src={resolveCheckoutFotoSrc(fotos[selectedFotoIdx])}
                            controls
                            playsInline
                            preload="metadata"
                            controlsList="nodownload"
                          />
                        ) : (
                          <>
                            <img
                              src={resolveCheckoutFotoSrc(fotos[selectedFotoIdx])}
                              alt={`Foto ${selectedFotoIdx + 1} da OS ${selectedOs.os.numeroOS}`}
                              className="checkout-view-foto-img"
                            />
                            <button
                              type="button"
                              className="checkout-view-foto-zoom-btn"
                              title="Ampliar foto"
                              onClick={() => setZoomFoto(fotos[selectedFotoIdx])}
                            >
                              <Expand size={14} strokeWidth={2} aria-hidden />
                            </button>
                          </>
                        )}
                      </div>
                      {fotos[selectedFotoIdx].operacao && (
                        <figcaption className="checkout-view-foto-caption">
                          {fotos[selectedFotoIdx].operacao}
                        </figcaption>
                      )}
                    </figure>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {showAssinaturaModal && selectedOs && assinaturaSvg && (
        <div className="modal-overlay" style={{ zIndex: 10006 }} onClick={() => setShowAssinaturaModal(false)}>
          <div className="checkout-view-fotos-modal checkout-view-assinatura-modal" onClick={(e) => e.stopPropagation()}>
            <div className="checkout-view-fotos-header">
              <div className="checkout-view-fotos-header-info">
                <h3 className="checkout-view-fotos-title">Assinatura da OS {selectedOs.os.numeroOS}</h3>
                <div className="checkout-view-fotos-subtitle">
                  <span>{selectedOs.os.modeloEquipamento || '—'}</span>
                  <span>•</span>
                  <span>Série: {selectedOs.os.numeroSerie || '—'}</span>
                </div>
              </div>
              <button
                type="button"
                className="modal-close-button"
                onClick={() => setShowAssinaturaModal(false)}
              >
                ✕
              </button>
            </div>
            <div className="checkout-view-fotos-body checkout-view-assinatura-body">
              <div className="checkout-view-assinatura-frame">
                <img
                  className="checkout-view-assinatura-img"
                  src={
                    assinaturaSvg.trim().startsWith('data:image/svg')
                      ? assinaturaSvg.trim()
                      : `data:image/svg+xml;charset=utf-8,${encodeURIComponent(assinaturaSvg)}`
                  }
                  alt={`Assinatura da OS ${selectedOs.os.numeroOS}`}
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {zoomFoto && (
        <div className="modal-overlay" style={{ zIndex: 10007 }} onClick={() => setZoomFoto(null)}>
          <div className="checkout-view-foto-zoom-modal" onClick={(e) => e.stopPropagation()}>
            <button
              type="button"
              className="modal-close-button"
              onClick={() => setZoomFoto(null)}
            >
              ✕
            </button>
            <div className="checkout-view-foto-zoom-body">
              <img
                src={resolveCheckoutFotoSrc(zoomFoto)}
                alt={`Foto ampliada da OS ${selectedOs?.os?.numeroOS || ''}`}
                className="checkout-view-foto-zoom-img"
              />
            </div>
          </div>
        </div>
      )}
      {refTooltip && ReactDOM.createPortal(
        <div
          className={`checkout-view-tooltip-portal ${refTooltip.place === 'above' ? 'checkout-view-tooltip-portal-above' : ''}`}
          style={{
            position: 'fixed',
            left: refTooltip.x,
            top: refTooltip.y,
            transform: refTooltip.place === 'above' ? 'translate(-50%, -100%)' : 'translateX(-50%)',
            marginTop: refTooltip.place === 'above' ? -8 : 0,
            zIndex: 99999,
            pointerEvents: 'none',
          }}
        >
          <span className="checkout-view-tooltip-portal-arrow" />
          <span className="checkout-view-tooltip-portal-text">{refTooltip.text}</span>
        </div>,
        document.body
      )}
      <EquipmentConsultSearchModal
        key={adhocEquipSearch.os?.numeroOS || adhocEquipSearch.serial || 'adhoc-equip-search'}
        open={adhocEquipSearch.open}
        initialSerial={adhocEquipSearch.serial}
        plainHeader
        autoSelectOnFind
        allowContractSearch
        closeOnOverlayClick={false}
        adhocOperationCode={adhocEquipSearch.os?.numeroOS || ''}
        title={
          adhocEquipSearch.os?.numeroOS
            ? `Atendimento avulso ${adhocEquipSearch.os.numeroOS}`
            : 'Atendimento avulso'
        }
        onClose={() => setAdhocEquipSearch({
          open: false,
          serial: '',
          os: null,
          areaId: '',
          techName: '',
          condLabel: '',
        })}
        onSelectEquipment={(suggestion) => {
          const ctx = adhocEquipSearch;
          setAdhocEquipSearch({
            open: false,
            serial: '',
            os: null,
            areaId: '',
            techName: '',
            condLabel: '',
          });
          if (!ctx?.os || !suggestion?.serie) return;
          setAdhocOsCreateFlow({
            operationCode: ctx.os.numeroOS,
            serie: suggestion.serie,
            contrato: suggestion.codigo || '',
            equipment: suggestion,
            os: ctx.os,
            areaId: ctx.areaId,
            techName: ctx.techName,
            condLabel: ctx.condLabel,
          });
        }}
      />
      <CheckoutAdhocOsCreateModal
        open={!!adhocOsCreateFlow}
        flow={adhocOsCreateFlow}
        onClose={() => setAdhocOsCreateFlow(null)}
        onSuccess={() => {
          refreshCheckoutResumo();
        }}
        onGoToFechamento={async (osNumber, flowCtx) => {
          const num = String(osNumber || '').trim();
          setAdhocOsCreateFlow(null);
          if (!num) return;
          setDetailSearchQuery(num);
          setDetailSearchOpen(true);
          const data = await refreshCheckoutResumo();
          const map = data?.osByAreaTechCondition || null;
          let hit = findOsInCheckoutResumo(map, num);
          if (!hit && flowCtx?.areaId && flowCtx?.techName) {
            const areaId = flowCtx.areaId;
            const techName = flowCtx.techName;
            setSelectedTechKeys((prev) => {
              const next = new Set(prev);
              next.add(techKey(areaId, techName));
              return next;
            });
          }
          if (hit) {
            setSelectedTechKeys((prev) => {
              const next = new Set(prev);
              next.add(techKey(hit.areaId, hit.techName));
              return next;
            });
            setExpandedConditionKey(conditionKey(hit.areaId, hit.techName, hit.conditionLabel));
            setSelectedOs({
              areaId: hit.areaId,
              techName: hit.techName,
              conditionLabel: hit.conditionLabel,
              os: hit.os,
            });
            if (detailSearchInputRef.current) {
              try {
                detailSearchInputRef.current.focus();
              } catch (_) {
                /* ignore */
              }
            }
            return;
          }
          // retry curto: SP/resumo podem atrasar a lista
          await new Promise((r) => setTimeout(r, 600));
          const data2 = await refreshCheckoutResumo();
          hit = findOsInCheckoutResumo(data2?.osByAreaTechCondition, num);
          if (!hit) return;
          setSelectedTechKeys((prev) => {
            const next = new Set(prev);
            next.add(techKey(hit.areaId, hit.techName));
            return next;
          });
          setExpandedConditionKey(conditionKey(hit.areaId, hit.techName, hit.conditionLabel));
          setSelectedOs({
            areaId: hit.areaId,
            techName: hit.techName,
            conditionLabel: hit.conditionLabel,
            os: hit.os,
          });
        }}
      />
    </>
  );
  if (asPage) {
    return (
      <div className="fechamento-page-wrap" style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
        {innerContent}
      </div>
    );
  }
  return (
    <div
      className="checkout-view-backdrop"
      onClick={(e) => e.target === e.currentTarget && onClose()}
      role="dialog"
      aria-modal="true"
      aria-label="Visualizar Check-Out"
    >
      {innerContent}
    </div>
  );
};

export default CheckoutViewModal;
